import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    ArrowLeft,
    ArrowLeftRight,
    User as UserIcon,
    Phone,
    Mail,
    MapPin,
    Plane,
    Plus,
    CreditCard,
    Car,
    Clock,
    Copy,
    AlertTriangle,
    XCircle,
    Edit2,
    Send,
    MailCheck,
    History,
    FileText,
    ChevronRight,
    RotateCcw,
} from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, ErrorPage, StatusPill } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Textarea, Input, Select } from '../../components/ui/Form.jsx';
import AddressInput from '../../components/AddressInput.jsx';
import { AllocateBookingModal } from './AllocateBookingModal.jsx';
import DuplicateBookingModal from '../../components/bookings/DuplicateBookingModal';
import ReturnBookingModal from '../../components/bookings/ReturnBookingModal';
import OperatingCostsCard from './OperatingCostsCard.jsx';
import TakePaymentCard from './TakePaymentCard.jsx';
import { createContext, useContext } from 'react';
import EmailPreviewModal from '../../components/email/EmailPreviewModal.jsx';
import { JobOfferSection } from './SendOfferModal.jsx';
import { TrackingLinkRow } from './TrackingLinkCard.jsx';

const ChangesNotifyContext = createContext(null);

// =============================================================================
// BookingDetailPage (Round 7d.2.2 + 7d.2.3 allocation wired in)
// =============================================================================
// Single-page scroll showing every detail of one booking. Drives the dispatcher
// workflow: view, transition status, allocate (slice 7d.2.3), edit/cancel
// (slice 7d.2.4). Status transitions enforce the matrix backend-side; the UI
// only surfaces the buttons for valid next states.
// =============================================================================

const STATUS_CONFIG = {
    pending_payment: { label: 'Pending payment', pillStatus: 'warning' },
    confirmed:  { label: 'Confirmed',  pillStatus: 'info'    },
    allocated:  { label: 'Allocated',  pillStatus: 'neutral' },  // internal-only, not yet visible to chauffeur
    dispatched: { label: 'Dispatched', pillStatus: 'info'    },  // sent to chauffeur, visible in portal
    en_route:   { label: 'En route',   pillStatus: 'warning' },
    arrived:    { label: 'Arrived',    pillStatus: 'warning' },
    on_journey: { label: 'POB',        pillStatus: 'warning' },
    completed:  { label: 'Completed',  pillStatus: 'success' },
    cancelled:  { label: 'Cancelled',  pillStatus: 'neutral' },
    no_show:    { label: 'No show',    pillStatus: 'danger'  },
};

const STATUS_BUTTON_CLASSES = {
en_route:   'bg-status-enRoute text-white hover:bg-status-enRoute/90 border-transparent',
arrived:    'bg-status-arrived text-white hover:bg-status-arrived/90 border-transparent',
on_journey: 'bg-status-onJourney text-white hover:bg-status-onJourney/90 border-transparent',
completed:  'bg-ink text-white hover:bg-ink/90 border-transparent',
};

// =============================================================================
// StatusStepper — visual progress through the booking lifecycle
// =============================================================================
// Desktop-only horizontal stepper showing every step of the happy path with
// done/current/future visual treatment. Driven entirely off booking.status —
// no business logic, pure display layer.
//
// Step model:
//   pending_payment → confirmed → allocated → dispatched → en_route → arrived → on_journey → completed
//
// Terminal divergences:
//   - cancelled: replaces the final "Completed" cell with a red "Cancelled" pill;
//     steps the booking didn't reach render as faint (never-reached).
//   - no_show: same idea, "No show" replaces the final cell. no_show only fires
//     from arrived, so arrived will be done, en_route done, and on_journey + the
//     would-be completed get replaced by the no_show cell.
//   - force-complete (status='completed' without traversing through every
//     intermediate): intermediate cells render dashed/skipped rather than done.
//
// Mobile (<lg): the entire stepper is hidden and the old current-status text
// block is shown instead.
// =============================================================================

const STEPPER_PATH = [
    'pending_payment',
    'confirmed',
    'allocated',
    'dispatched',
    'en_route',
    'arrived',
    'on_journey',
    'completed',
];

// Short labels for the stepper cells. "POB" is the in-house term for on_journey
// — matches what the dispatcher reads on the bookings list and elsewhere.
const STEPPER_LABELS = {
    pending_payment: 'Pending payment',
    confirmed:       'Confirmed',
    allocated:       'Allocated',
    dispatched:      'Dispatched',
    en_route:        'En route',
    arrived:         'Arrived',
    on_journey:      'POB',
    completed:       'Completed',
    cancelled:       'Cancelled',
    no_show:         'No show',
};

function StatusStepper({ booking, history }) {
    const status = booking.status;
    const isCancelled = status === 'cancelled';
    const isNoShow = status === 'no_show';
    const isTerminalDivergence = isCancelled || isNoShow;

    // Build the set of statuses the booking has actually passed through,
    // by walking the status_history. This handles force-complete cleanly —
    // intermediate steps that were skipped show as skipped rather than done.
    const visitedStatuses = (() => {
        const set = new Set();
        // Every booking conceptually passes through pending_payment, even when
        // the Stripe webhook (or non-Stripe payment method) jumps it straight
        // to confirmed. Show as done so the stepper reads naturally.
        set.add('pending_payment');
        for (const row of history || []) {
            if (row.from_status) set.add(row.from_status);
            if (row.to_status) set.add(row.to_status);
        }
        set.add(status);
        return set;
    })();

    const currentIdx = STEPPER_PATH.indexOf(status);

    // For cancelled/no_show, build a custom path. Path is:
    //   - all steps the booking actually visited, in order, marked done
    //   - then the terminal divergence cell at the right end
    const cells = isTerminalDivergence
        ? buildDivergedCells(visitedStatuses, status)
        : buildHappyPathCells(visitedStatuses, currentIdx, status);

    return (
        <div className="hidden lg:flex items-stretch gap-0 select-none">
            {cells.map((cell, i) => (
                <StepperCell key={`${cell.status}-${i}`} cell={cell} isLast={i === cells.length - 1} />
            ))}
        </div>
    );
}

function buildHappyPathCells(visitedStatuses, currentIdx, currentStatus) {
    return STEPPER_PATH.map((s, i) => {
        const isCurrent = s === currentStatus;
        const wasVisited = visitedStatuses.has(s);
        const isPast = i < currentIdx;
        const isFuture = i > currentIdx;

        let state;
        if (isCurrent) {
            state = 'current';
        } else if (isPast && wasVisited) {
            state = 'done';
        } else if (isPast && !wasVisited) {
            state = 'skipped';
        } else {
            state = 'future';
        }
        return { status: s, state };
    });
}

function buildDivergedCells(visitedStatuses, terminalStatus) {
    // Render only the path steps the booking actually visited (as done),
    // then append the terminal divergence cell at the end.
    const visited = STEPPER_PATH
        .filter((s) => visitedStatuses.has(s) && s !== terminalStatus)
        .map((s) => ({ status: s, state: 'done' }));
    visited.push({ status: terminalStatus, state: 'terminal_divergence' });
    return visited;
}

function StepperCell({ cell, isLast }) {
    const label = STEPPER_LABELS[cell.status] || cell.status;

    // Style maps. Connector line between cells inherits the state of the cell
    // it leads INTO (a future cell's connector is faint, etc.) — visually
    // matches what users expect from progress steppers in mature apps.
    const cellStyles = {
        done:       'bg-canvas text-ink-muted border-paper-border',
        current:    'bg-ink text-paper border-ink shadow-sm',
        future:     'bg-paper text-ink-faint border-paper-border-soft',
        skipped:    'bg-paper text-ink-faint border-paper-border-soft border-dashed',
        terminal_divergence: cell.status === 'cancelled' || cell.status === 'no_show'
            ? 'bg-red-100 text-red-800 border-red-300'
            : 'bg-canvas text-ink-muted border-paper-border',
    };

    const connectorStyles = {
        done:       'bg-ink/20',
        current:    'bg-ink/20',
        future:     'bg-paper-border-soft',
        skipped:    'bg-paper-border-soft',
        terminal_divergence: cell.status === 'cancelled' || cell.status === 'no_show'
            ? 'bg-red-200'
            : 'bg-ink/20',
    };

    return (
        <>
            <div
                className={
                    'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border whitespace-nowrap transition-colors ' +
                    cellStyles[cell.state]
                }
            >
                {cell.state === 'done' && (
                    <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z" clipRule="evenodd" />
                    </svg>
                )}
                {cell.state === 'skipped' && (
                    <span className="text-ink-faint">—</span>
                )}
                {cell.state === 'terminal_divergence' && (cell.status === 'cancelled' || cell.status === 'no_show') && (
                    <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                )}
                {label}
            </div>
            {!isLast && (
                <div className={'self-center w-3 h-0.5 ' + connectorStyles[cell.state]} aria-hidden="true" />
            )}
        </>
    );
}

// Forward-only inline transitions only. Cancel + no-show go through the
// dedicated CancelBookingCard / CancelBookingModal flow (slice 7d.cancel-modal),
// not the inline status buttons. Keeping them out of this matrix means the
// existing requestTransition() path stays simple — no special-casing needed
// for refund-bearing transitions.
//
// pending_payment has no forward transition exposed here — the Stripe webhook
// handles confirmed auto-flip when payment confirms.
const STATUS_TRANSITION_MATRIX = {
    pending_payment: [],
    confirmed:  [],
    allocated:  ['en_route'],   // En route auto-promotes through dispatched
    dispatched: ['en_route'],
    en_route:   ['arrived'],
    arrived:    ['on_journey'],
    on_journey: ['completed'],
    completed:  [],
    cancelled:  [],
    no_show:    [],
};

const PRIMARY_NEXT = {
    allocated:  'en_route',
    dispatched: 'en_route',
    en_route:   'arrived',
    arrived:    'on_journey',
    on_journey: 'completed',
};

// Mirrors NewBookingPage. Inline edit's vehicle-class dropdown uses these
// labels; backend stores both class key and label so the booking row's
// vehicle_class_label stays in sync.
const VEHICLE_CLASS_LABELS = {
    business: 'Business Class',
    first: 'First Class',
    suv: 'First Class SUV',
    xl: 'Business Class XL',
};

// -----------------------------------------------------------------------------
// Airport / station detection — ported from the engine (lanz_booking_engine
// detectAirport / detectStation). String-match against the address. Used to
// show flight number / tail / FBO fields only when an airport is involved,
// and station-specific UI for stations. Cheap, no API calls.
// -----------------------------------------------------------------------------
function detectAirport(addr) {
    if (!addr) return null;
    const a = String(addr).toLowerCase();
    if (a.indexOf('heathrow') !== -1 || a.indexOf('lhr') !== -1) return 'heathrow';
    if (a.indexOf('gatwick') !== -1  || a.indexOf('lgw') !== -1) return 'gatwick';
    if (a.indexOf('luton')   !== -1  || a.indexOf('ltn') !== -1) return 'luton';
    if (a.indexOf('stansted') !== -1 || a.indexOf('stn') !== -1) return 'stansted';
    if (a.indexOf('city airport') !== -1 || a.indexOf('lcy') !== -1) return 'city';
    return null;
}

function detectStation(addr) {
    if (!addr) return null;
    const a = String(addr).toLowerCase();
    if (a.indexOf('st pancras') !== -1 || a.indexOf('st. pancras') !== -1 || a.indexOf('saint pancras') !== -1) return 'st_pancras';
    return null;
}

// Helper: does this booking touch an airport (pickup OR dropoff)?
function bookingHasAirport(booking) {
    return Boolean(detectAirport(booking?.pickup_address) || detectAirport(booking?.dropoff_address));
}

// Helper: does this booking touch a station (pickup OR dropoff)?
function bookingHasStation(booking) {
    return Boolean(detectStation(booking?.pickup_address) || detectStation(booking?.dropoff_address));
}

const TRANSITION_LABELS = {
    confirmed:  'Un-allocate',
    allocated:  'Allocate',
    en_route:   'En route',
    arrived:    'Arrived',
    on_journey: 'POB',
    completed:  'Complete',
    cancelled:  'Cancel',
    no_show:    'No show',
};

// timeToMins — needed by computeHourlyBilling. Same impl as NewBookingPage.
function timeToMins(hhmm) {
    if (!hhmm || typeof hhmm !== 'string') return null;
    const parts = hhmm.split(':');
    if (parts.length !== 2) return null;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return h * 60 + m;
}

// Stage 2 — refines billing using actual driving time per leg from Distance
// Matrix. Same logic as the engine + NewBookingPage. May auto-push the
// dispatcher's final_time picker forward when expected arrival exceeds it.
// Returns { billedHours, newFinalTime: 'HH:MM' or null }.
function computeHourlyBilling({ pickupTime, finalTime, stops, legSeconds }) {
    const pickupMins = timeToMins(pickupTime);
    if (pickupMins === null) {
        return { billedHours: 2, newFinalTime: null };
    }

    let expectedFinalMins = null;

    let lastTimedStopIdx = -1;
    let lastTimedStopMins = null;
    for (let i = 0; i < (stops || []).length; i++) {
        const t = stops[i]?.time;
        const m = timeToMins(t);
        if (m !== null) {
            lastTimedStopIdx = i;
            lastTimedStopMins = m;
        }
    }

    if (lastTimedStopMins !== null) {
        let remainingMins = 0;
        for (let li = lastTimedStopIdx + 1; li <= (stops?.length || 0); li++) {
            remainingMins += Math.ceil(((legSeconds || [])[li] || 0) / 60);
        }
        expectedFinalMins = (lastTimedStopMins + remainingMins) % 1440;
    } else {
        const totalDrivingMins = (legSeconds || []).reduce(
            (sum, s) => sum + Math.ceil((s || 0) / 60), 0
        );
        expectedFinalMins = (pickupMins + totalDrivingMins) % 1440;
    }

    const relFromPickup = (mins) => {
        let rel = mins - pickupMins;
        if (rel < 0) rel += 1440;
        return rel;
    };

    const pickerFinalMins = timeToMins(finalTime);
    const pickerRel = pickerFinalMins !== null ? relFromPickup(pickerFinalMins) : 0;
    const expectedRel = relFromPickup(expectedFinalMins);

    let newFinalTime = null;

    if (pickerFinalMins === null || expectedRel > pickerRel) {
        const slots = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
        let newFH = Math.floor(expectedFinalMins / 60);
        let newFM = expectedFinalMins % 60;
        let rounded = slots.find((s) => s >= newFM);
        if (rounded === undefined) {
            rounded = 0;
            newFH = (newFH + 1) % 24;
        }
        newFinalTime = `${String(newFH).padStart(2, '0')}:${String(rounded).padStart(2, '0')}`;
        expectedFinalMins = newFH * 60 + rounded;
    }

    const finalPickerMins = newFinalTime !== null
        ? timeToMins(newFinalTime)
        : (pickerFinalMins !== null ? pickerFinalMins : pickupMins);

    const pickerSpan = (() => {
        let s = finalPickerMins - pickupMins;
        if (s < 0) s += 1440;
        return s;
    })();

    const expectedSpan = (() => {
        let s = expectedFinalMins - pickupMins;
        if (s < 0) s += 1440;
        return s;
    })();

    const effectiveSpan = Math.max(pickerSpan, expectedSpan);
    const billedHours = Math.max(2, Math.ceil(effectiveSpan / 60));

    return { billedHours, newFinalTime };
}

// Distance Matrix with the same session cache pattern as NewBookingPage.
// Module-level Map survives re-renders; cache hits skip Google entirely.
const _legCache = new Map();

function _legCacheKey(from, to) {
    const norm = (s) => String(s || '').toLowerCase().trim().replace(/\s+/g, ' ');
    return `${norm(from)}|||${norm(to)}`;
}

async function fetchDistanceMatrix(pickup, dropoff, stops = []) {
    if (typeof window === 'undefined' || !window.google?.maps) {
        throw new Error('Google Maps not loaded yet.');
    }

    const cleanStops = (stops || []).filter((s) => s && s.trim().length > 0);
    const legs = [];
    let prev = pickup;
    for (const stop of cleanStops) {
        legs.push([prev, stop]);
        prev = stop;
    }
    legs.push([prev, dropoff]);

    let totalMeters = 0;
    let totalSeconds = 0;
    const legSeconds = [];

    const service = new window.google.maps.DistanceMatrixService();

    for (const [origin, destination] of legs) {
        const cacheKey = _legCacheKey(origin, destination);
        const cached = _legCache.get(cacheKey);
        let result;
        if (cached) {
            result = {
                duration: { value: cached.seconds },
                distance: { value: cached.meters },
            };
        } else {
            result = await new Promise((resolve, reject) => {
                service.getDistanceMatrix({
                    origins: [origin],
                    destinations: [destination],
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    unitSystem: window.google.maps.UnitSystem.IMPERIAL,
                }, (response, status) => {
                    if (status !== 'OK') {
                        reject(new Error(`Distance Matrix returned ${status}.`));
                        return;
                    }
                    const element = response?.rows?.[0]?.elements?.[0];
                    if (!element || element.status !== 'OK') {
                        reject(new Error(
                            `Could not compute "${origin}" → "${destination}" (${element?.status || 'unknown'}).`
                        ));
                        return;
                    }
                    resolve(element);
                });
            });

            _legCache.set(cacheKey, {
                meters: result.distance?.value || 0,
                seconds: result.duration?.value || 0,
            });
        }

        const seconds = result.duration?.value || 0;
        totalMeters += result.distance?.value || 0;
        totalSeconds += seconds;
        legSeconds.push(seconds);
    }

    return {
        miles: Math.ceil(totalMeters / 1609.34),
        legSeconds,
    };
}

// Compute billed hours from pickup_time + final_time (HH:MM strings).
// Mirrors the booking engine's calculateHourlyDuration logic + NewBookingPage:
//   total = (final - pickup), wrap +1440 if negative
//   billedHours = max(2, ceil(total/60))   ← 2hr minimum charge floor
// Returns null if either time can't be parsed.
function naiveHourlyBilling(pickupTime, finalTime) {
    const toMins = (hhmm) => {
        if (!hhmm || typeof hhmm !== 'string') return null;
        const parts = hhmm.split(':');
        if (parts.length !== 2) return null;
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        if (isNaN(h) || isNaN(m)) return null;
        if (h < 0 || h > 23 || m < 0 || m > 59) return null;
        return h * 60 + m;
    };
    const p = toMins(pickupTime);
    const f = toMins(finalTime);
    if (p === null || f === null) return null;
    let total = f - p;
    if (total < 0) total += 1440;
    return Math.max(2, Math.ceil(total / 60));
}

function formatDateTime(date, time) {
    if (!date) return '—';
    let dateStr;
    if (date instanceof Date) {
        dateStr = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
    } else if (typeof date === 'string') {
        dateStr = date.slice(0, 10);
    } else return '—';
    try {
        const d = new Date(`${dateStr}T${time || '00:00'}`);
        if (isNaN(d.getTime())) return `${dateStr} · ${time || '—'}`;
        return d.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' }) + ' at ' + (time || '—');
    } catch {
        return `${dateStr} · ${time || '—'}`;
    }
}

// Date-only formatter — used in the itinerary header line to show e.g.
// "Tuesday, 28 Apr 2026" without a time. Same Date-vs-string handling as
// formatDateTime above (pg returns DATE columns as Date objects).
function formatBookingDate(date) {
    if (!date) return '';
    let dateStr;
    if (date instanceof Date) {
        dateStr = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
    } else if (typeof date === 'string') {
        dateStr = date.slice(0, 10);
    } else return '';
    try {
        const d = new Date(`${dateStr}T00:00`);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return dateStr;
    }
}

function formatTimestamp(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function formatRelativeTime(ts) {
    if (!ts) return '';
    const now = new Date();
    const then = new Date(ts);
    if (isNaN(then.getTime())) return '';

    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
    const dayDiff = Math.round((today - thenDay) / 86400000);

    const timeStr = then.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

   if (dayDiff === 0) return timeStr;
    if (dayDiff === 1) return `Yesterday at ${timeStr}`;
    return then.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }) + ` at ${timeStr}`;
}

function formatPrice(amount) {
    if (amount === null || amount === undefined || amount === '') return '—';
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(n)) return '—';
    return `£${n.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Build the human-readable payment-method line for the BookingDetailsCard.
// Branches on `payment_method` to explain HOW payment is being handled — the
// amount lives in its own Quoted/Charged row above, so this row never repeats
// the figure, just the method + state context.
//
// Returns a JSX node or null if there's nothing to show.
// ---------------------------------------------------------------------------
function renderPaymentLine(booking) {
    const method = booking.payment_method;
    const note   = booking.payment_external_note;
    const charged = booking.charged_amount;

    if (method === 'external') {
        return (
            <span>
                <span className="font-medium">Other</span>
                {note && <span className="text-ink-subtle"> — {note}</span>}
            </span>
        );
    }

    if (method === 'account') {
        return (
            <span>
                <span className="font-medium">Account billing</span>
                <span className="text-ink-subtle">
                    {' — '}
                    {charged != null ? 'invoice paid' : 'invoiced separately'}
                </span>
            </span>
        );
    }

    if (method === 'saved_card') {
        // Show last4 to disambiguate when customer might have multiple cards.
        // Backend supplies default_payment_method_last4 on the booking detail
        // response (joined via the pinned payment_method_id FK from mig 018).
        const last4 = booking.default_payment_method_last4;
        const label = last4 ? `Card on file (\u2022\u2022\u2022\u2022 ${last4})` : 'Card on file';
        const captureStatus = booking.stripe_capture_status;
        const captureError = booking.stripe_capture_error;

        // Status hint (if any) priority:
        //   1. capture error (red — needs attention)
        //   2. voided auth (auth was released, no money taken)
        //   3. uncaptured (auth held, awaiting capture at completion)
        //   4. pending charge (initial state before webhook)
        let hint = null;
        let hintClass = 'text-ink-subtle';
        if (captureError) {
            hint = ` — capture failed: ${captureError}`;
            hintClass = 'text-red-700';
        } else if (captureStatus === 'voided') {
            hint = ' — auth voided (no charge)';
        } else if (captureStatus === 'uncaptured') {
            hint = ' — auth held, captured on completion';
        } else if (charged == null) {
            hint = ' — pending charge';
        }

        return (
            <span>
                <span className="font-medium">{label}</span>
                {hint && <span className={hintClass}>{hint}</span>}
            </span>
        );
    }

    if (method === 'payment_link') {
        return (
            <span>
                <span className="font-medium">Stripe payment link</span>
                <span className="text-ink-subtle">
                    {' — '}
                    {charged != null ? 'paid' : 'awaiting payment'}
                </span>
            </span>
        );
    }

    // Unknown / null — caller already gates on payment_method being truthy
    // before rendering this row, so we shouldn't normally reach here.
    return null;
}

// ---------------------------------------------------------------------------
// Normalise the booking response into the shape AllocateBookingModal expects.
// The detail endpoint returns flat fields (chauffeur_first_name, vehicle_make
// etc.) — but the modal's pre-fill logic reads nested objects for clarity.
// ---------------------------------------------------------------------------
function buildBookingForModal(booking) {
    if (!booking) return null;

    const allocated_chauffeur = booking.allocated_chauffeur_id
        ? {
            id: booking.allocated_chauffeur_id,
            first_name: booking.chauffeur_first_name,
            last_name: booking.chauffeur_last_name,
            phone: booking.chauffeur_phone,
            call_sign: booking.chauffeur_call_sign,
            fleet_partner_id: booking.chauffeur_fleet_partner_id || null,
            fleet_partner_company_name: booking.chauffeur_fleet_partner_company_name || null,
        }
        : null;

    const allocated_vehicle = booking.allocated_vehicle_id
        ? {
            id: booking.allocated_vehicle_id,
            make: booking.vehicle_make,
            model: booking.vehicle_model,
            registration: booking.vehicle_registration,
            nickname: booking.vehicle_nickname,
            vehicle_class: booking.vehicle_class,
            fleet_partner_id: booking.vehicle_fleet_partner_id || null,
            fleet_partner_company_name: booking.vehicle_fleet_partner_company_name || null,
        }
        : null;

    return {
        ...booking,
        // Modal reads booking.ride_datetime_local for the conflict check window.
        // Detail endpoint stores pickup as separate date/time; combine for the modal.
        ride_datetime_local: booking.ride_datetime_local
            || (booking.pickup_date && booking.pickup_time
                ? `${String(booking.pickup_date).slice(0, 10)}T${booking.pickup_time}`
                : null),
        allocated_chauffeur,
        allocated_vehicle,
    };
}

export default function BookingDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = useApi();
    const queryClient = useQueryClient();
    const { isOwner } = useCurrentUser();
    
    // Auto-refresh — chauffeur status updates from partner.lanzcts.com land
    // in dispatch without manual reload. Polls every 30s on non-terminal
    // bookings while the tab is visible; refetches on tab refocus; stops
    // entirely once terminal.
    const TERMINAL_STATUSES = ['completed', 'cancelled', 'no_show'];

    const { data: booking, isLoading, isError, error } = useQuery({
        queryKey: ['booking', id],
        queryFn:  () => api.get(`/bookings/${id}`),
        refetchInterval: (data) => {
            if (!data) return 30_000;
            if (TERMINAL_STATUSES.includes(data.status)) return false;
            return 30_000;
        },
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

const [transitionTarget, setTransitionTarget] = useState(null);
    const [transitionNote, setTransitionNote] = useState('');
    const [showUndoConfirm, setShowUndoConfirm] = useState(false);
    const [showForceCompleteConfirm, setShowForceCompleteConfirm] = useState(false);
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [changesNotifyContext, setChangesNotifyContext] = useState(null);
 // 'cancel' | 'no_show' | 'issue_refund' | null
    // Drives the relocated CancelBookingModal (formerly triggered by CancelBookingCard).
    const [cancelModalMode, setCancelModalMode] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Unified edit mode (replaces per-subsection edit). Click "Edit booking"
    // in the Actions card → all subsections of BookingDetailsCard become
    // editable simultaneously → Save fires ONE PATCH → ONE re-notify modal.
    const [editingBooking, setEditingBooking] = useState(false);

    const transitionMutation = useMutation({
        mutationFn: ({ status, note }) =>
            api.post(`/bookings/${id}/status`, { status, note }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['booking', id] });
            queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] });
            toast.success('Status updated');
            setTransitionTarget(null);
            setTransitionNote('');
        },
        onError: (err) => {
            toast.error(err.message || 'Could not update status');
        },
    });

    const undoMutation = useMutation({
        mutationFn: () => api.post(`/bookings/${id}/undo`),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['booking', id] });
            queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] });
            toast.success(`Reset to ${STATUS_CONFIG[data.to_status]?.label || data.to_status}`);
            setShowUndoConfirm(false);
        },
        onError: (err) => {
            toast.error(err.message || 'Could not reset status');
            setShowUndoConfirm(false);
        },
    });

    const forceCompleteMutation = useMutation({
        mutationFn: () => api.post(`/bookings/${id}/force-complete`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['booking', id] });
            queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] });
            toast.success('Marked complete');
            setShowForceCompleteConfirm(false);
        },
        onError: (err) => {
            toast.error(err.message || 'Could not mark complete');
            setShowForceCompleteConfirm(false);
        },
    });

    const { data: siblingsData } = useQuery({
        queryKey: ['booking', id, 'siblings'],
        queryFn: () => api.get(`/bookings/${id}/siblings`),
        staleTime: 30_000,
    });

    // Compute modal-shaped booking once per render
    const bookingForModal = useMemo(() => buildBookingForModal(booking), [booking]);

    if (isLoading) return <LoadingPage label="Loading booking…" />;
    if (isError) return <ErrorPage error={error} />;
    if (!booking) return null;

    const statusCfg     = STATUS_CONFIG[booking.status] || { label: booking.status, pillStatus: 'neutral' };
    const allowedNext   = STATUS_TRANSITION_MATRIX[booking.status] || [];
    const isTerminal    = allowedNext.length === 0;

    // Walk history newest-first to find the first un-undone forward transition
    const history = booking.status_history || [];
    let previousState = null;
    let depth = 0;
    for (const row of history) {
        const isUndo = (row.note || '').startsWith('[UNDO]');
        if (isUndo) {
            depth += 1;
        } else {
            depth -= 1;
            if (depth === -1) {
                previousState = row.from_status;
                break;
            }
        }
    }
    // "Entered current status" = the most recent row that actually
    // transitioned INTO the current status. Edit rows write
    // from_status === to_status (e.g. editing a completed booking logs
    // "completed → completed"); those aren't status entries, so skip them —
    // otherwise the relative time jumps to the last edit instead of the
    // genuine transition.
    const statusEntryRow = history.find(
        (h) => h.to_status === booking.status && h.from_status !== booking.status
    );
    const enteredAt = statusEntryRow?.changed_at
        || statusEntryRow?.created_at
        || history[0]?.changed_at
        || history[0]?.created_at
        || booking.created_at;

    const UNDO_BLOCKED = ['confirmed', 'cancelled'];
    const canUndo = !UNDO_BLOCKED.includes(booking.status) && previousState;

    // Allocation availability: anything except terminal states.
    // Per 7d.2.3 spec: re-allocation is allowed even on en_route+ (emergencies,
    // breakdowns) — the backend resets status to allocated when chauffeur changes.
    const canAllocate = !['cancelled', 'completed', 'no_show'].includes(booking.status);

    const requestTransition = (targetStatus) => {
        setTransitionTarget(targetStatus);
        setTransitionNote('');
    };

    const confirmTransition = () => {
        if (!transitionTarget) return;
        if (transitionTarget === 'cancelled' && !transitionNote.trim()) {
            toast.error('Cancellation requires a reason');
            return;
        }
        transitionMutation.mutate({
            status: transitionTarget,
            note:   transitionNote.trim() || undefined,
        });
    };

    const isMultiRide = booking.booking_ref_master !== booking.ride_ref;
    const siblings = siblingsData?.siblings || [];
    const siblingCount = siblings.length;
    const thisRideIndex = booking.ride_index
        || (siblings.findIndex((s) => s.id === booking.id) + 1)
        || 1;

    return (
        <ChangesNotifyContext.Provider value={setChangesNotifyContext}>
            <div>
                <PageHeader
                title={
                    <span className="inline-flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-base">{booking.ride_ref}</span>
                        <StatusPill status={statusCfg.pillStatus}>{statusCfg.label}</StatusPill>
                        {isMultiRide && siblingCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-200">
                                Ride {thisRideIndex} of {siblingCount}
                            </span>
                        )}
                    </span>
                }
                subtitle={
                    <span className="text-sm">
                        {/* navigate(-1) goes back in history (preserves the
                            filtered URL the user came from), instead of
                            /bookings which would strip query params and
                            default to today. Falls back to /bookings if
                            there's no history (e.g. user opened the detail
                            page directly via URL). */}
                        <button
                            type="button"
                            onClick={() => {
                                if (window.history.length > 1) {
                                    navigate(-1);
                                } else {
                                    navigate('/bookings');
                                }
                            }}
                            className="text-ink-muted hover:text-ink inline-flex items-center gap-1"
                        >
                            <ArrowLeft className="h-3 w-3" />
                            Bookings
                        </button>
                        <span className="text-ink-muted mx-2">·</span>
                        <Link
                            to={`/customers/${booking.customer_id}`}
                            className="text-ink-muted hover:text-ink"
                        >
                            {booking.customer_name}
                        </Link>
                        {booking.booking_ref_master !== booking.ride_ref && (
                            <>
                                <span className="text-ink-muted mx-2">·</span>
                                <span className="text-ink-muted">Master: {booking.booking_ref_master}</span>
                            </>
                        )}
                    </span>
                }
                actions={null}
            />

            {/* Status panel */}
            <div className="card mb-5">
                <div className="card-body">
                    {/* Desktop: stepper across the top with relative-time below.
                        Mobile: old big-status text block (unchanged from before). */}
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            {/* Desktop stepper — hidden on mobile */}
                            <div className="hidden lg:block">
                                <StatusStepper booking={booking} history={history} />
                                <div className="mt-3 text-xs text-ink-subtle">
                                    {booking.status === 'confirmed'
                                        ? 'Pending allocation'
                                        : booking.status === 'pending_payment'
                                        ? 'Awaiting payment'
                                        : `${statusCfg.label} \u00b7 ${formatRelativeTime(enteredAt)}`}
                                </div>
                            </div>

                            {/* Mobile fallback — unchanged from old design */}
                            <div className="lg:hidden">
                                <div className="text-xs uppercase text-ink-subtle tracking-wide font-medium">
                                    Current status
                                </div>
                                <div className="mt-2 text-2xl font-semibold text-ink">
                                    {statusCfg.label}
                                </div>
                                <div className="mt-1 text-xs text-ink-subtle">
                                    {booking.status === 'confirmed'
                                        ? 'Pending allocation'
                                        : formatRelativeTime(enteredAt)}
                                </div>
                            </div>
                        </div>

                        {canUndo && (
                            <button
                                type="button"
                                onClick={() => setShowUndoConfirm(true)}
                                className="text-xs text-ink-subtle hover:text-ink inline-flex items-center gap-1 transition-colors mt-1 flex-shrink-0"
                                title={`Reset status back to ${STATUS_CONFIG[previousState]?.label || previousState}`}
                            >
                                <RotateCcw className="h-3 w-3" />
                                Undo
                            </button>
                        )}
                    </div>

                    {!isTerminal && allowedNext.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-paper-border-soft">
                            <div className="text-[10px] uppercase tracking-[0.1em] text-ink-subtle font-semibold mb-2">
                                Next status update
                            </div>
                            <div className="flex flex-wrap gap-2">
                            {(() => {
                                const primary = PRIMARY_NEXT[booking.status];
                                const ordered = primary && allowedNext.includes(primary)
                                    ? [primary, ...allowedNext.filter(s => s !== primary)]
                                    : allowedNext;
                               return ordered.map(next => {
const isPrimary = next === primary;
const isCancel  = next === 'cancelled';
const isNoShow  = next === 'no_show';
const isDanger  = isCancel || isNoShow;
const primaryColorClass = isPrimary
    ? (STATUS_BUTTON_CLASSES[next] || '')
    : '';

return (
    <Button
        key={next}
        size="sm"
        variant={isPrimary ? 'primary' : 'secondary'}
        onClick={() => requestTransition(next)}
        className={[
            isDanger ? 'text-status-danger' : '',
            primaryColorClass,
        ].filter(Boolean).join(' ')}
    >
        {isCancel && <XCircle className="h-3.5 w-3.5" />}
        {TRANSITION_LABELS[next] || next}
    </Button>
);
});
                            })()}
                            </div>
                        </div>
                    )}
               {/* Bottom-right strip: status-aware exceptional actions.
                        Cancel / No-show / Issue refund moved out of the separate
                        CancelBookingCard and into here (same modal, same logic).
                        Mark complete (override) stays as the rightmost action.
                        Each link renders only when appropriate for the current
                        status — same gating that drove CancelBookingCard's
                        visibility. */}
                    {(() => {
                        const status = booking.status;
                        const canCancel = ['pending_payment', 'confirmed', 'allocated', 'dispatched', 'en_route'].includes(status);
                        const canNoShow = status === 'arrived';
                        const canIssueRefund = (status === 'cancelled' || status === 'no_show')
                            && !booking.refund_decision_at;
                        const canForceComplete = isOwner && status !== 'completed' && status !== 'cancelled' && status !== 'no_show';

                        const links = [];
                        if (canCancel) {
                            links.push(
                                <button
                                    key="cancel"
                                    type="button"
                                    onClick={() => setCancelModalMode('cancel')}
                                    className="text-xs text-ink-subtle hover:text-red-700 underline decoration-dotted"
                                    title="Cancel this booking"
                                >
                                    Cancel booking
                                </button>
                            );
                        }
                        if (canNoShow) {
                            links.push(
                                <button
                                    key="no_show"
                                    type="button"
                                    onClick={() => setCancelModalMode('no_show')}
                                    className="text-xs text-ink-subtle hover:text-red-700 underline decoration-dotted"
                                    title="Passenger did not show up after arrival"
                                >
                                    Mark as no-show
                                </button>
                            );
                        }
                        if (canIssueRefund) {
                            links.push(
                                <button
                                    key="issue_refund"
                                    type="button"
                                    onClick={() => setCancelModalMode('issue_refund')}
                                    className="text-xs text-ink-subtle hover:text-ink underline decoration-dotted"
                                    title="Record a refund decision for this cancelled booking"
                                >
                                    Issue refund
                                </button>
                            );
                        }
                        if (canForceComplete) {
                            links.push(
                                <button
                                    key="force_complete"
                                    type="button"
                                    onClick={() => setShowForceCompleteConfirm(true)}
                                    className="text-xs text-ink-subtle hover:text-ink underline decoration-dotted"
                                    title="Owner override — bypasses the normal status flow"
                                >
                                    Mark complete (override)
                                </button>
                            );
                        }
                        if (isOwner) {
                            links.push(
                                <button
                                    key="delete"
                                    type="button"
                                    onClick={() => setShowDeleteModal(true)}
                                    className="text-xs text-ink-subtle hover:text-red-700 underline decoration-dotted"
                                    title="Permanently delete this booking (owner only)"
                                >
                                    Delete booking
                                </button>
                            );
                        }

                        if (links.length === 0) return null;

                        // Interleave with middot separators.
                        const withSeparators = [];
                        links.forEach((link, i) => {
                            if (i > 0) {
                                withSeparators.push(
                                    <span key={`sep-${i}`} className="text-ink-subtle">·</span>
                                );
                            }
                            withSeparators.push(link);
                        });

                        return (
                            <div className="mt-3 pt-3 border-t border-paper-border-soft flex justify-end items-center gap-2">
                                {withSeparators}
                            </div>
                        );
                    })()}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    {isMultiRide && siblings.length > 0 && (
                        <ItineraryCard
                            currentBookingId={booking.id}
                            siblings={siblings}
                            masterRef={booking.booking_ref_master}
                            masterChargedTotal={booking.master_charged_total}
                        />
                    )}
                  <BookingDetailsCard booking={booking} editing={editingBooking} setEditing={setEditingBooking} />
                                        <BillingDetailsCard booking={booking} />
                    <OperatingCostsCard booking={booking} />
                    <AdjustmentsCard booking={booking} />
                    <StatusHistoryCard history={booking.status_history || []} />
                </div>

                <div className="lg:col-span-1 space-y-5">
                    <AllocationCard
                        booking={booking}
                        canAllocate={canAllocate}
                        onOpenAllocate={() => setShowAllocateModal(true)}
                    />
                    <DispatcherActionsCard
                        booking={booking}
                        editingBooking={editingBooking}
                        setEditingBooking={setEditingBooking}
                        setShowDuplicateModal={setShowDuplicateModal}
                        setShowReturnModal={setShowReturnModal}
                    />
                    <TakePaymentCard booking={booking} />
                </div>
            </div>

           {/* Transition confirmation modal — forward-only transitions.
                Cancel + no-show have their own modal (CancelBookingModal). */}
          {transitionTarget && (
                <Modal
                    open
                    onClose={() => {
                        setTransitionTarget(null);
                        setTransitionNote('');
                    }}
                    title={`Transition to ${STATUS_CONFIG[transitionTarget]?.label || transitionTarget}?`}
                    description={`Move ${booking.ride_ref} from ${statusCfg.label} to ${STATUS_CONFIG[transitionTarget]?.label}.`}
                    size="md"
                >
                    <Textarea
                        label="Note (optional)"
                        value={transitionNote}
                        onChange={(e) => setTransitionNote(e.target.value)}
                        placeholder="Optional context for the audit log"
                        rows={3}
                    />
                    <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setTransitionTarget(null);
                                setTransitionNote('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={confirmTransition}
                            loading={transitionMutation.isPending}
                        >
                            Confirm
                        </Button>
                    </div>
                </Modal>
            )}

            {/* Undo confirmation modal */}
            {showUndoConfirm && (
                <Modal
                    open
                    onClose={() => setShowUndoConfirm(false)}
                    title="Reset status?"
                    description={`This will revert from ${statusCfg.label} back to ${STATUS_CONFIG[previousState]?.label || previousState}. The change is logged in the status history.`}
                    size="md"
                >
                    <div className="text-sm text-ink-muted">
                        {booking.status === 'allocated'
                            ? 'Un-allocates the chauffeur. The booking goes back to needing allocation.'
                            : 'Use this to fix accidental taps by the chauffeur. For other corrections, contact an owner.'}
                    </div>
                    <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button
                            variant="secondary"
                            onClick={() => setShowUndoConfirm(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => undoMutation.mutate()}
                            loading={undoMutation.isPending}
                        >
                            <RotateCcw className="h-4 w-4" />
                            Reset status
                        </Button>
                    </div>
                </Modal>
            )}

                {/* Force-complete confirmation modal — owner override */}
            {showForceCompleteConfirm && (
                <Modal
                    open
                    onClose={() => setShowForceCompleteConfirm(false)}
                    title="Mark complete (override)?"
                    description={`This bypasses the normal status flow and marks ${booking.ride_ref} as completed regardless of its current state.`}
                    size="md"
                >
                    <div className="text-sm text-ink-muted">
                        Used for bookings fulfilled outside the dispatch system. No chauffeur fare line is generated — billing is customer-side only.
                    </div>
                    <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button
                            variant="secondary"
                            onClick={() => setShowForceCompleteConfirm(false)}
                            disabled={forceCompleteMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => forceCompleteMutation.mutate()}
                            loading={forceCompleteMutation.isPending}
                        >
                            Mark complete
                        </Button>
                    </div>
                </Modal>
            )}

            {/* Allocation modal — slice 7d.2.3 */}
            {showAllocateModal && bookingForModal && (
                <AllocateBookingModal
                    booking={bookingForModal}
                    onClose={() => setShowAllocateModal(false)}
                    onAllocated={(allocateResponse) => {
                        // If chauffeur changed, the backend returns a notification
                        // context with one or more channels to prompt about.
                        // Open the NotifyChangesModal to let dispatcher decide.
                        if (allocateResponse?.notification_context) {
                            setChangesNotifyContext({
                                diff: allocateResponse.notification_context.diff,
                                customerNotification: allocateResponse.notification_context.customer_notification,
                                driverNotification: allocateResponse.notification_context.driver_notification,
                                previousChauffeurNotification: allocateResponse.notification_context.previous_chauffeur_notification,
                            });
                        }
                        // Detail page refetches via the query invalidations
                        // already fired from inside the modal mutation.
                    }}
                />
            )}
                
            {/* Re-notify driver modal — fires after edit-while-notified */}
            {/* Notify changes modal — fires after edit OR chauffeur swap */}
            {changesNotifyContext && (
                <NotifyChangesModal
                    booking={booking}
                    diff={changesNotifyContext.diff}
                    customerNotification={changesNotifyContext.customerNotification}
                    driverNotification={changesNotifyContext.driverNotification}
                    previousChauffeurNotification={changesNotifyContext.previousChauffeurNotification}
                    onClose={() => setChangesNotifyContext(null)}
                />
            )}

{showDuplicateModal && (
    <DuplicateBookingModal
        booking={booking}
        onClose={(result) => {
            setShowDuplicateModal(false);
            if (result?.kind === 'multi') {
                // Optional: trigger a toast. For now we just close — dispatcher
                // sees the count in the modal status before submit, and the
                // navigate-to-new-booking only happens for single duplicates.
            }
        }}
    />
)}

{showReturnModal && (
    <ReturnBookingModal
        booking={booking}
        onClose={() => setShowReturnModal(false)}
    />
)}

{/* Cancel / No-show / Issue-refund modal — relocated from CancelBookingCard.
    Same modal, same flow, same refund logic. Mode flag picks the copy/behaviour. */}
{cancelModalMode && (
    <CancelBookingModal
        booking={booking}
        mode={cancelModalMode}
        onClose={() => setCancelModalMode(null)}
    />
)}

{showDeleteModal && (
    <DeleteBookingModal
        booking={booking}
        siblings={siblings}
        isMultiRide={isMultiRide}
        onClose={() => setShowDeleteModal(false)}
    />
)}
                
            </div>
        </ChangesNotifyContext.Provider>
    );
}

// =============================================================================
// Inline-edit infrastructure (7d.2.4)
// =============================================================================
//
// Reusable hook + header component for the section-level inline edit pattern.
// Every editable card on this page uses these:
//   1. read-only state (label + Edit button top-right)
//   2. click Edit → fields become inputs, Cancel/Save buttons appear
//   3. Save → PATCH /bookings/:id with the section's fields → toast +
//      query invalidation → flips back to read-only
//   4. Cancel → discard local draft, flip back
//
// Fields editable on a section are defined by the calling card. The hook
// just manages the lifecycle. The PATCH endpoint is field-agnostic — it
// accepts any subset of editable fields.
//
// Status-aware locking is handled at the card level (each card decides
// whether to render the Edit button) — the hook itself doesn't know about
// status rules.
// =============================================================================

// Statuses where pre-active-only fields (route, timing, vehicle, price) can
// be edited. After this, only always-editable fields work (notes, passenger,
// booker contact). Mirrors the backend rule in PRE_ACTIVE_STATUSES.
const PRE_ACTIVE_STATUSES = new Set(['pending_payment', 'confirmed', 'allocated']);

function isPreActive(booking) {
    return PRE_ACTIVE_STATUSES.has(booking?.status);
}

function useInlineEdit({ booking, initialFields }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const onChangesNotifyNeeded = useContext(ChangesNotifyContext);

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(initialFields);

    // Reset draft when initialFields change (e.g., after refetch) but only
    // when not actively editing — don't blow away the user's typed input.
    useEffect(() => {
        if (!editing) setDraft(initialFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(initialFields), editing]);

    const saveMutation = useMutation({
        mutationFn: (payload) => api.patch(`/bookings/${booking.id}`, payload),
        onSuccess: (data) => {
            if (data?.fields_changed === 0) {
                toast('No changes to save');
            } else {
                toast.success('Saved');
            }
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            setEditing(false);

            // Open the unified notify modal if EITHER customer or driver was
            // previously emailed/notified. Backend returns both flags in the
            // PATCH response — modal decides per-channel visibility from there.
            const customerCtx = data?.customer_notification?.edited_while_notified
                ? {
                    previously_emailed: true,
                    booker_email: data.customer_notification.booker_email,
                  }
                : null;
            const driverCtx = data?.driver_notification?.edited_while_notified
                ? {
                    previously_notified: true,
                    chauffeur_email: data.driver_notification.chauffeur_email,
                  }
                : null;

            if (
                data?.diff?.length > 0
                && (customerCtx || driverCtx)
                && onChangesNotifyNeeded
            ) {
                onChangesNotifyNeeded({
                    diff: data.diff,
                    customerNotification: customerCtx,
                    driverNotification: driverCtx,
                });
            }
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Failed to save';
            toast.error(`Couldn't save: ${msg}`);
        },
    });

    function startEdit() {
        setDraft(initialFields);
        setEditing(true);
    }

    function cancel() {
        setDraft(initialFields);
        setEditing(false);
    }

    function save(transform = (d) => d) {
        const payload = transform(draft);
        // If the caller already filtered to changed-only fields and the
        // payload is empty, skip the network call.
        if (payload && Object.keys(payload).length === 0) {
            toast('No changes to save');
            setEditing(false);
            return;
        }
        saveMutation.mutate(payload);
    }

   // Convenience helper — most subsections only update one field at a time,
    // so they call setField(key, value) instead of setDraft((d) => ({ ...d, [key]: value })).
    const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

    return {
        editing,
        draft,
        setField,
        setDraft,
        startEdit,
        cancel,
        save,
        saving: saveMutation.isPending,
    };
}

// Card header with optional Edit button, used by every editable card.
// `canEdit` lets the card hide the button based on status rules.
function EditableCardHeader({ icon: Icon, title, subtitle, editing, canEdit, onEdit }) {
    return (
        <div className="card-header flex items-start justify-between">
            <div>
                <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                    {Icon && <Icon className="h-4 w-4 text-ink-subtle" />}
                    {title}
                </h2>
                {subtitle && (
                    <p className="text-xs text-ink-subtle mt-0.5">{subtitle}</p>
                )}
            </div>
            {!editing && canEdit && (
                <button
                    type="button"
                    onClick={onEdit}
                    className="text-xs text-ink-subtle hover:text-ink inline-flex items-center gap-1"
                >
                    <Edit2 className="h-3.5 w-3.5" />
                    Edit
                </button>
            )}
        </div>
    );
}

// Cancel / Save row used at the bottom of every editing card.
function EditActions({ onCancel, onSave, saving, saveLabel = 'Save' }) {
    return (
        <div className="flex items-center justify-end gap-2 pt-3 mt-2 border-t border-paper-border-soft">
            <Button variant="secondary" onClick={onCancel} disabled={saving}>
                Cancel
            </Button>
            <Button variant="primary" onClick={onSave} loading={saving}>
                {saveLabel}
            </Button>
        </div>
    );
}

// =============================================================================
// ItineraryCard — Slice 7d.add-ride
// =============================================================================
//
// Renders all sibling rides of a multi-ride basket, in order. Each row links
// to that sibling's detail page (except the current booking, which is shown
// without a link and visually highlighted).
//
// Read-only display. Editing the master total / sibling routing happens
// through the basket UI on NewBookingPage (next slice). For now this card
// just shows the dispatcher what's in the basket.
//
// Status pills use the same STATUS_CONFIG mapping as the main page header,
// so colour / label matches the dispatcher's mental model from the bookings
// list.
// =============================================================================
function ItineraryCard({ currentBookingId, siblings, masterRef, masterChargedTotal }) {
    return (
        <div className="card">
            <div className="card-body">
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-base font-semibold text-ink">Itinerary</h2>
                        <div className="text-xs text-ink-subtle mt-0.5">
                            Master ref{' '}
                            <span className="font-mono">{masterRef}</span>
                                                        {' \u00b7 '}{siblings.length} ride{siblings.length !== 1 ? 's' : ''}
                        </div>
                    </div>
                    {masterChargedTotal != null && (
                        <div className="text-right">
                            <div className="text-xs text-ink-subtle uppercase tracking-wide">
                                Customer paid
                            </div>
                            <div className="text-lg font-semibold text-ink">
                                £{Number(masterChargedTotal).toFixed(2)}
                            </div>
                        </div>
                    )}
                </div>
 
                <div className="space-y-2">
                    {siblings.map((s) => {
                        const isCurrent = s.id === currentBookingId;
                        const cfg = STATUS_CONFIG[s.status] || { label: s.status, pillStatus: 'neutral' };
                        const route = (s.pickup_address && s.dropoff_address)
                            ? `${shortAddress(s.pickup_address)} \u2192 ${shortAddress(s.dropoff_address)}`
                            : 'No route set';
                        const dateTime = formatItineraryDateTime(s.pickup_date, s.pickup_time);
                        const modeLabel = s.mode === 'hourly'
                            ? `Hourly \u00b7 ${s.hours || 2}h`
                            : 'One-way';
                        const vehicle = VEHICLE_CLASS_LABELS[s.vehicle_class] || s.vehicle_class || '';
                        const chauffeur = (s.chauffeur_first_name || s.chauffeur_last_name)
                            ? `${s.chauffeur_first_name || ''} ${s.chauffeur_last_name || ''}`.trim()
                            : null;
 
                        const rowClass = isCurrent
                            ? 'block px-3 py-3 rounded-lg border-2 border-indigo-300 bg-indigo-50/40'
                            : 'block px-3 py-3 rounded-lg border border-paper-border-soft bg-paper hover:bg-canvas/50 transition-colors';
 
                        const inner = (
                            <>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 rounded-full bg-ink text-paper text-xs font-bold px-1.5">
                                                {s.ride_index || '?'}
                                            </span>
                                            <span className="font-mono text-xs text-ink-subtle">{s.ride_ref}</span>
                                            <StatusPill status={cfg.pillStatus}>{cfg.label}</StatusPill>
                                            {isCurrent && (
                                                <span className="text-[10px] uppercase tracking-wide font-medium text-indigo-700">
                                                    Viewing
                                                </span>
                                            )}
                                        </div>
 
                                        <div className="mt-2 text-sm text-ink truncate">{route}</div>
 
                                        <div className="mt-1 flex items-center gap-3 text-xs text-ink-subtle flex-wrap">
                                            <span className="inline-flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {dateTime}
                                            </span>
                                            <span>{modeLabel}</span>
                                            {vehicle && <span>{vehicle}</span>}
                                            {chauffeur && (
                                                <span className="text-ink">
                                                    Chauffeur: <span className="font-medium">{chauffeur}</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
 
                                    <div className="text-right flex-shrink-0">
                                        <div className="text-sm font-semibold text-ink">
                                            {s.quoted_price != null
                                                ? `£${Number(s.quoted_price).toFixed(2)}`
                                                : '\u2014'}
                                        </div>
                                        {!isCurrent && (
                                            <ChevronRight className="h-4 w-4 text-ink-subtle inline-block mt-1" />
                                        )}
                                    </div>
                                </div>
                            </>
                        );
 
                        return isCurrent
                            ? <div key={s.id} className={rowClass}>{inner}</div>
                            : <Link key={s.id} to={`/bookings/${s.id}`} className={rowClass}>{inner}</Link>;
                    })}
                </div>
            </div>
        </div>
    );
}
 
// Small helpers used only by ItineraryCard. Define alongside the component
// (or at the bottom of the file with the other helpers — pick wherever feels
// natural in the file's existing structure).
function shortAddress(s) {
    if (!s) return '';
    return s.split(',')[0].trim();
}
 
function formatItineraryDateTime(dateStr, timeStr) {
    if (!dateStr) return 'No date';
    try {
        // dateStr can be 'YYYY-MM-DD' or an ISO timestamp; normalise.
        const dStr = String(dateStr).slice(0, 10);
        const d = new Date(`${dStr}T00:00:00`);
        if (isNaN(d.getTime())) return dateStr;
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const day = d.getDate();
        const month = months[d.getMonth()];
        const time = timeStr ? String(timeStr).slice(0, 5) : '';
        return time ? `${day} ${month} ${time}` : `${day} ${month}`;
    } catch {
        return dateStr;
    }
}


// =============================================================================
// Subsection helpers — used inside the unified BookingDetailsCard
// =============================================================================
//
// Each subsection (Times, Booker, Passenger, Route, Notes) has its own
// inline-edit lifecycle. SubsectionHeader renders a small label + Edit
// button row; SubsectionDivider draws the separator between sections.
// Subsections are independent: editing Booker doesn't affect Passenger.
// =============================================================================

// =============================================================================
// BookingDetailsCard — UNIFIED edit mode (replaces per-subsection edit)
// =============================================================================
//
// Owns the draft state and saveMutation for ALL editable booking fields.
// When `editing` is true (controlled by parent), every subsection switches
// to input mode simultaneously. Single Save → ONE PATCH → ONE re-notify
// modal → ONE email to driver.
//
// Subsections (Itinerary, Booker, Passenger, Flight, Notes) are dumb
// dual-mode renderers — they read from `draft` and write via `setField`.
// All edit lifecycle lives here.
//
// Billing is intentionally separate (different lock rules, different
// audit trail) — see BillingDetailsCard below.
// =============================================================================

function buildInitialDraft(booking) {
    return {
        // Itinerary
        // DB column is `mode`, wire field is `booking_mode`. GET returns
        // `booking.mode`, so alias it here. Falls back to `booking.booking_mode`
        // in case backend ever surfaces it under that key, then 'one_way'.
       booking_mode: (() => {
    const raw = booking.mode || booking.booking_mode || 'one_way';
    const normalized = String(raw).replace(/[-_\s]/g, '').toLowerCase();
    return normalized === 'hourly' ? 'hourly' : 'one_way';
})(),
        pickup_date: booking.pickup_date ? String(booking.pickup_date).slice(0, 10) : '',
        pickup_time: booking.pickup_time ? String(booking.pickup_time).slice(0, 5) : '',
        final_time: booking.final_time ? String(booking.final_time).slice(0, 5) : '',
        hours: booking.hours != null ? String(booking.hours) : '',
        pickup_address: booking.pickup_address || '',
        dropoff_address: booking.dropoff_address || '',
        stops: Array.isArray(booking.stops) ? booking.stops.map((s) =>
            typeof s === 'string' ? { address: s, time: '' } : { address: s?.address || '', time: s?.time || '' }
        ) : [],
        vehicle_class: booking.vehicle_class || 'business',

        // Booker — slice 7d.picker — picker uses booker_id; inline name/phone
        // kept for backward compat with computeChangedFields but no longer
        // exposed in the UI (the picker swaps the booker, doesn't edit name).
        booker_id: booking.booker_id || '',
        booker_name: booking.booker_name_current || booking.booker_name || '',
        booker_phone: booking.booker_phone_current || booking.booker_phone || '',

        // Passenger — same shape as booker. passenger_id can be empty
        // ((no passenger) option). pax_name / pax_phone get auto-updated
        // server-side when passenger_id changes; not exposed in the UI.
        passenger_id: booking.passenger_id || '',
        pax_name: booking.pax_name_current || booking.pax_name || '',
                pax_phone: booking.pax_phone_current || booking.pax_phone || '',
        // Editable phone of the SAVED passenger (updates the passenger record)
        passenger_phone: booking.pax_phone_current || booking.pax_phone || '',
        pax_count: booking.pax_count != null ? String(booking.pax_count) : '',
                pax_comments: booking.pax_comments || '',
        chauffeur_notes: booking.chauffeur_notes || '',

        // Flight
        flight_number: booking.flight_number || '',
        tail_number: booking.tail_number || '',
        fbo_code: booking.fbo_code || '',

        // Notes
        internal_notes: booking.internal_notes || '',

       // Reporting — owner-only field, drives compliance report buckets
        licensing_authority: booking.licensing_authority || 'TFL',

        // Slice 7d.polish-1
        customer_reference: booking.customer_reference || '',
        hours_disclosed_to_chauffeur: booking.hours_disclosed_to_chauffeur === true,
    };
}

function computeChangedFields(before, after, liveBilling) {
    const payload = {};
    const isHourly = after.booking_mode === 'hourly';

// Simple string fields — compare and include if changed.
    // Slice 7d.picker — booker_id and passenger_id are picker swaps.
    // Backend handles the FK update + denorm column refresh.
 const simpleFields = [
        'booking_mode',
        'pickup_date', 'pickup_time', 'pickup_address', 'dropoff_address',
        'vehicle_class',
        'booker_id', 'passenger_id',
                'pax_comments', 'chauffeur_notes',
        'flight_number', 'tail_number', 'fbo_code', 'internal_notes',
        'licensing_authority',
        'customer_reference',
    ];
    for (const f of simpleFields) {
        if ((before[f] || '') !== (after[f] || '')) {
            payload[f] = after[f] || null;
        }
    }

        // Saved passenger's phone — only when the passenger itself wasn't swapped
    // in this edit (a swap brings the new passenger's own phone).
    if (after.passenger_id
        && (before.passenger_id || '') === (after.passenger_id || '')
        && (before.passenger_phone || '').trim() !== (after.passenger_phone || '').trim()) {
        payload.passenger_phone = (after.passenger_phone || '').trim() || null;
    }
    // empty-string fallback above would coerce false to null incorrectly.
    if (Boolean(before.hours_disclosed_to_chauffeur) !== Boolean(after.hours_disclosed_to_chauffeur)) {
        payload.hours_disclosed_to_chauffeur = Boolean(after.hours_disclosed_to_chauffeur);
    }

    // Numeric — pax_count, hours
    if ((before.pax_count || '') !== (after.pax_count || '')) {
        const n = parseInt(after.pax_count, 10);
        payload.pax_count = Number.isFinite(n) ? n : null;
    }
    // Hourly final_time — dispatcher input
    if (isHourly && (before.final_time || '') !== (after.final_time || '')) {
        payload.final_time = after.final_time || null;
    }

    // Hours — derived from pickup_time + final_time. Dispatcher never types
    // it directly; we compute it on save when relevant times change. Recompute
    // if mode is hourly AND (pickup_time, final_time, or mode itself changed).
    if (isHourly) {
        const pickupChanged = (before.pickup_time || '') !== (after.pickup_time || '');
        const finalChanged = (before.final_time || '') !== (after.final_time || '');
        const stopsTimesChanged = JSON.stringify(
            (before.stops || []).map((s) => s?.time || '')
        ) !== JSON.stringify(
            (after.stops || []).map((s) => s?.time || '')
        );
        const modeChangedToHourly = (before.booking_mode || 'one_way') !== 'hourly'
            && after.booking_mode === 'hourly';

        if (pickupChanged || finalChanged || stopsTimesChanged || modeChangedToHourly) {
            // Prefer Stage-2 refined value (from Distance Matrix), fall back
            // to Stage-1 naive math. Both honour the 2-hour minimum floor.
            const computed = (liveBilling && Number.isFinite(liveBilling.billedHours))
                ? liveBilling.billedHours
                : naiveHourlyBilling(after.pickup_time, after.final_time);
            const beforeHours = before.hours != null && before.hours !== ''
                ? parseFloat(before.hours)
                : null;
            if (computed !== null && computed !== beforeHours) {
                payload.hours = computed;
            }
        }
    }

    // Switching one-way: explicitly clear hourly-specific fields so the DB
    // doesn't keep stale final_time / hours values from the previous hourly
    // shape. Only fire when mode actually changed (avoid pointless writes).
    const modeChanged = (before.booking_mode || 'one_way') !== (after.booking_mode || 'one_way');
    if (modeChanged && after.booking_mode === 'one_way') {
        if (before.final_time) payload.final_time = null;
        if (before.hours != null && before.hours !== '') payload.hours = null;
        // Also wipe stops if any are set on the before side. Frontend blocks
        // this case but defensive — if dispatcher somehow ended up here, the
        // backend would 409 anyway. Better to send a clean empty array.
        if ((before.stops || []).length > 0) payload.stops = [];
    }

    // Stops — JSON compare. Strip blanks before sending.
    const beforeStops = JSON.stringify(before.stops || []);
    const afterStopsClean = (after.stops || [])
        .filter((s) => s && (s.address || '').trim())
        .map((s) => ({ address: s.address.trim(), time: s.time || '' }));
    if (beforeStops !== JSON.stringify(afterStopsClean)) {
        payload.stops = afterStopsClean;
    }

    // Vehicle class label sync
    if (payload.vehicle_class) {
        payload.vehicle_class_label = VEHICLE_CLASS_LABELS[payload.vehicle_class] || payload.vehicle_class;
    }

    return payload;
}

function BookingDetailsCard({ booking, editing, setEditing }) {
    const api = useApi();
    const queryClient = useQueryClient();

    // Customer's saved addresses for the AddressInput picker. Lazy-loaded
    // on first edit (avoids fetching for view-only sessions). Cached by
    // React Query keyed on customer_id so multiple edit/cancel cycles
    // don't re-hit the endpoint.
    const { data: customerData } = useQuery({
        queryKey: ['customer', booking.customer_id],
        queryFn: () => api.get(`/customers/${booking.customer_id}`),
        enabled: !!booking.customer_id && editing,
        staleTime: 5 * 60 * 1000,
    });
    const savedAddresses = useMemo(() => {
        const list = customerData?.customer?.addresses || customerData?.addresses || [];
        const sorted = [...list].sort((a, b) => {
            const al = a.last_used_at || a.created_at || '';
            const bl = b.last_used_at || b.created_at || '';
            return bl.localeCompare(al);
        });
return sorted.slice(0, 5);
    }, [customerData]);
    const savedNotes = useMemo(() => {
        return customerData?.customer?.saved_notes || customerData?.saved_notes || [];
    }, [customerData]);
    const onChangesNotifyNeeded = useContext(ChangesNotifyContext);
    const { isOwner } = useCurrentUser();

    const initialDraft = useMemo(() => buildInitialDraft(booking), [booking]);
    const [draft, setDraft] = useState(initialDraft);

    // Live hourly billing — Stage 1 (instant) + Stage 2 (Distance Matrix).
    // Same model as NewBookingPage. Seeded from current booking values so
    // the duration pill shows correctly the moment edit mode opens.
    const [liveBilling, setLiveBilling] = useState(() => ({
        billedHours: booking.hours || 2,
        miles: null,
    }));

    // Reset draft when booking changes underneath us (e.g. polling refetch),
    // but only when not actively editing — otherwise we'd nuke typed input.
    useEffect(() => {
        if (!editing) setDraft(initialDraft);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(initialDraft), editing]);

    // Re-seed draft on every entry into edit mode so we start from current truth.
    useEffect(() => {
        if (editing) {
            setDraft(buildInitialDraft(booking));
            setLiveBilling({ billedHours: booking.hours || 2, miles: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing]);

    // Stage 1: instant naive recompute on time changes.
    useEffect(() => {
        if (!editing) return;
        if (draft.booking_mode !== 'hourly') return;
        const naive = naiveHourlyBilling(draft.pickup_time, draft.final_time);
        if (naive !== null) {
            setLiveBilling((b) => ({ ...b, billedHours: naive }));
        }
    }, [editing, draft.booking_mode, draft.pickup_time, draft.final_time]);

    // Stage 2: debounced Distance Matrix + refined billing. May auto-push
    // the final_time picker forward when expected arrival exceeds it.
    useEffect(() => {
        if (!editing) return;
        if (draft.booking_mode !== 'hourly') return;
        if (!draft.pickup_address || !draft.dropoff_address) return;

        const stopAddresses = (draft.stops || []).map((s) => s.address);
        const allHaveAddresses = stopAddresses.every((a) => a && a.trim().length > 0);
        if ((draft.stops || []).length > 0 && !allHaveAddresses) return;

        let cancelled = false;
        const handle = setTimeout(async () => {
            try {
                const { miles, legSeconds } = await fetchDistanceMatrix(
                    draft.pickup_address,
                    draft.dropoff_address,
                    stopAddresses
                );
                if (cancelled) return;

                const { billedHours, newFinalTime } = computeHourlyBilling({
                    pickupTime: draft.pickup_time,
                    finalTime: draft.final_time,
                    stops: draft.stops || [],
                    legSeconds,
                });

                setLiveBilling({ billedHours, miles });

                if (newFinalTime && newFinalTime !== draft.final_time) {
                    setDraft((d) => ({ ...d, final_time: newFinalTime }));
                }
            } catch {
                // Distance Matrix failed — keep stage-1 value, miles stays null
            }
        }, 400);

        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [
        editing,
        draft.booking_mode,
        draft.pickup_address,
        draft.dropoff_address,
        draft.pickup_time,
        draft.final_time,
        JSON.stringify(draft.stops),
    ]);

    const saveMutation = useMutation({
        mutationFn: (payload) => api.patch(`/bookings/${booking.id}`, payload),
        onSuccess: (data) => {
            if (data?.fields_changed === 0) {
                toast('No changes to save');
            } else {
                toast.success('Saved');
            }
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            setEditing(false);

            const customerCtx = data?.customer_notification?.edited_while_notified
                ? {
                    previously_emailed: true,
                    booker_email: data.customer_notification.booker_email,
                  }
                : null;
            const driverCtx = data?.driver_notification?.edited_while_notified
                ? {
                    previously_notified: true,
                    chauffeur_email: data.driver_notification.chauffeur_email,
                  }
                : null;

            if (
                data?.diff?.length > 0
                && (customerCtx || driverCtx)
                && onChangesNotifyNeeded
            ) {
                onChangesNotifyNeeded({
                    diff: data.diff,
                    customerNotification: customerCtx,
                    driverNotification: driverCtx,
                });
            }
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Failed to save';
            toast.error(`Couldn't save: ${msg}`);
        },
    });

    const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

    function handleSave() {
        const payload = computeChangedFields(initialDraft, draft, liveBilling);
        if (Object.keys(payload).length === 0) {
            toast('No changes to save');
            setEditing(false);
            return;
        }
        saveMutation.mutate(payload);
    }

    function handleCancel() {
        setDraft(initialDraft);
        setEditing(false);
    }

    const saving = saveMutation.isPending;
    const showFlightSection = bookingHasAirport(booking)
        || booking.flight_number
        || booking.tail_number
        || booking.fbo_code
        || editing;

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Booking details</h2>
            </div>
            <div className="card-body">
               <ItinerarySubsection
                    booking={booking}
                    editing={editing}
                    draft={draft}
                    setField={setField}
                    setDraft={setDraft}
                    saving={saving}
                    liveBilling={liveBilling}
                    savedAddresses={savedAddresses}
                    statusHistory={booking.status_history || []}
                />

                <SubsectionDivider />
                <BookerSubsection
                    booking={booking}
                    editing={editing}
                    draft={draft}
                    setField={setField}
                    saving={saving}
                />

                <SubsectionDivider />
                <PassengerSubsection
                    booking={booking}
                    editing={editing}
                    draft={draft}
                    setField={setField}
                    setDraft={setDraft}
                    saving={saving}
                    savedNotes={savedNotes}
                />

                {showFlightSection && (
                    <>
                        <SubsectionDivider />
                        <FlightDetailsSubsection
                            booking={booking}
                            editing={editing}
                            draft={draft}
                            setField={setField}
                            saving={saving}
                        />
                    </>
                )}

                <SubsectionDivider />
                <NotesSubsection
                    booking={booking}
                    editing={editing}
                    draft={draft}
                    setField={setField}
                    saving={saving}
                />

                {/* Reporting — owner-only. Tiny footer field that drives which
                    licensing-authority compliance report this booking belongs
                    to. N/A excludes from all authority reports (test bookings,
                    internal rides, complimentary jobs). */}
                {isOwner && (
                    <div className="mt-7 pt-4 border-t border-paper-border-soft">
                        <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-ink-subtle">
                            <span className="uppercase tracking-[0.1em] font-medium">
                                Reporting authority
                            </span>
                            {editing ? (
                                <select
                                    value={draft.licensing_authority || 'TFL'}
                                    onChange={(e) => setField('licensing_authority', e.target.value)}
                                    disabled={saving}
                                    className="text-xs border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                                >
                                    <option value="TFL">TFL</option>
                                    <option value="Castle Point">Castle Point</option>
                                    <option value="Wolverhampton">Wolverhampton</option>
                                    <option value="Other">Other</option>
                                    <option value="N/A">N/A — exclude from reports</option>
                                </select>
                            ) : (
                                <span className="text-ink">
                                    {booking.licensing_authority || 'TFL'}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {editing && (
                    <div className="flex items-center justify-end gap-2 pt-4 mt-5 border-t border-paper-border-soft">
                        <Button variant="secondary" onClick={handleCancel} disabled={saving}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSave} loading={saving}>
                            Save changes
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

function SubsectionDivider() {
    return <div className="border-t border-paper-border my-7" />;
}
function SubsectionLabel({ label }) {
    return (
        <div className="text-xs uppercase tracking-[0.1em] text-ink font-bold mb-4 pb-2 border-b border-paper-border">
            {label}
        </div>
    );
}

// ----- ItinerarySubsection -----------------------------------------------
function ItinerarySubsection({ booking, editing, draft, setField, setDraft, saving, liveBilling, savedAddresses = [], statusHistory = [] }) {
    const isHourly = (editing ? draft.booking_mode : booking.mode) === 'hourly';

    // Chauffeur completion time — pulled from the status history (no dedicated
    // column exists; completion is only a 'completed' row written by the
    // driver portal). Used to offer a one-click "use this" on the Final time
    // field for hourly jobs that ran long. History arrives newest-first, so
    // the first completed row is the relevant one.
    // Match the genuine completion transition, not an edit made afterwards.
    // Edits write from_status === to_status (both 'completed' on a completed
    // booking), so filtering those out leaves the real chauffeur/owner
    // completion row. History is newest-first; take the most recent true
    // transition into 'completed'.
    const completionRow = (statusHistory || []).find(
        (h) => h.to_status === 'completed' && h.from_status !== 'completed'
    );
    const completedAtTime = (() => {
        if (!completionRow) return null;
        const ts = completionRow.changed_at || completionRow.created_at;
        if (!ts) return null;
        const d = new Date(ts);
        if (isNaN(d.getTime())) return null;
        // Local HH:MM — matches the <input type="time"> format the field uses.
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    })();

   if (!editing) {
        const modeLabel = (booking.mode || 'one_way') === 'hourly' ? 'Hourly hire' : 'One-way';
        return (
            <div>
                <SubsectionLabel label="Itinerary" />
                <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-ink font-medium">
                            {formatBookingDate(booking.pickup_date)}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold bg-canvas text-ink-muted border border-paper-border-soft">
                            {modeLabel}
                        </span>
                    </div>
                    <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                        <div>
                            <span className="text-ink-subtle">{booking.pickup_time?.slice(0, 5) || '—'}</span>
                            <span className="text-ink ml-2">{booking.pickup_address || '—'}</span>
                        </div>
                    </div>
                    {Array.isArray(booking.stops) && booking.stops.map((s, i) => {
                        const addr = typeof s === 'string' ? s : s?.address;
                        const time = typeof s === 'object' ? s?.time : '';
                        if (!addr) return null;
                        return (
                            <div key={i} className="flex items-start gap-2">
                                <MapPin className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0 opacity-60" />
                                <div>
                                    {time && <span className="text-ink-subtle">{time.slice(0, 5)}</span>}
                                    <span className={`text-ink ${time ? 'ml-2' : ''}`}>{addr}</span>
                                </div>
                            </div>
                        );
                    })}
{isHourly ? (
                        <>
                            <div className="flex items-start gap-2">
                                <MapPin className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                                <div>
                                    <span className="text-ink-subtle">{booking.final_time?.slice(0, 5) || '—'}</span>
                                    <span className="text-ink ml-2">{booking.dropoff_address || '—'}</span>
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <Clock className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                                <div>
                                    <span className="text-ink">End of hire ({booking.hours || '—'} hrs)</span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                            <div>
                                <span className="text-ink">{booking.dropoff_address || '—'}</span>
                            </div>
                        </div>
                    )}
                <div className="text-xs text-ink-subtle pt-1">
                        Vehicle: {VEHICLE_CLASS_LABELS[booking.vehicle_class] || booking.vehicle_class || '—'}
                    </div>
                    {booking.customer_reference && (
                        <div className="text-xs text-ink-subtle">
                            Customer reference: <span className="text-ink font-medium">{booking.customer_reference}</span>
                        </div>
                    )}
                    {/* Hours disclosure indicator — only meaningful for hourly. Shown
                        only when explicitly hidden (default state of "visible" is the
                        normal case and doesn't need callout). */}
                    {isHourly && booking.hours_disclosed_to_chauffeur === false && (
                        <div className="text-xs text-amber-700">
                            Hours hidden from chauffeur
                        </div>
                    )}
                </div>
            </div>
        );
    }

   // Edit mode
    const stopsCount = (draft.stops || []).filter(
        (s) => s && (s.address || '').trim()
    ).length;
    const blockSwitchToOneWay = isHourly && stopsCount > 0;

    function handleModeChange(newMode) {
        if (newMode === draft.booking_mode) return;
        if (newMode === 'one_way' && stopsCount > 0) {
            // Block — frontend matches backend rule. Toast tells dispatcher
            // what to do.
            toast.error('Remove all stops first to switch to one-way');
            return;
        }
        setDraft((d) => ({
            ...d,
            booking_mode: newMode,
            // Clear hourly-specific fields when switching to one-way
            ...(newMode === 'one_way' ? { final_time: '', hours: '' } : {}),
        }));
    }

    return (
        <div>
            <SubsectionLabel label="Itinerary" />
            <div className="space-y-3">
                {/* Mode toggle — One-way vs Hourly */}
                <div className="flex gap-1 p-1 bg-canvas rounded-lg w-fit">
                    {[
                        { value: 'one_way', label: 'One-way' },
                        { value: 'hourly', label: 'Hourly hire' },
                    ].map((m) => {
                        const selected = draft.booking_mode === m.value;
                        const wouldBlock = m.value === 'one_way' && blockSwitchToOneWay;
                        return (
                            <button
                                key={m.value}
                                type="button"
                                onClick={() => handleModeChange(m.value)}
                                disabled={saving}
                                title={wouldBlock ? 'Remove stops first to switch to one-way' : undefined}
                                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                    selected
                                        ? 'bg-paper text-ink shadow-sm'
                                        : wouldBlock
                                            ? 'text-ink-subtle opacity-50 cursor-not-allowed'
                                            : 'text-ink-subtle hover:text-ink'
                                }`}
                            >
                                {m.label}
                            </button>
                        );
                    })}
                </div>
                {blockSwitchToOneWay && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                        Remove all stops to switch this booking to one-way.
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-ink-subtle mb-1">Pickup date</label>
                        <Input
                            type="date"
                            value={draft.pickup_date}
                            onChange={(e) => setField('pickup_date', e.target.value)}
                            disabled={saving}
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-subtle mb-1">Pickup time</label>
                        <Input
                            type="time"
                            value={draft.pickup_time}
                            onChange={(e) => setField('pickup_time', e.target.value)}
                            disabled={saving}
                        />
                    </div>
                </div>
               <div>
                    <label className="block text-xs text-ink-subtle mb-1">Pickup address</label>
                    <AddressInput
                        value={draft.pickup_address}
                        onChange={(addr) => setField('pickup_address', addr?.address || '')}
                        disabled={saving}
                        savedAddresses={savedAddresses}
                    />
                </div>
                {isHourly && (draft.stops || []).map((stop, i) => (
                    <div key={i} className="rounded-lg border border-paper-border-soft bg-paper p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs uppercase tracking-wide text-ink-subtle font-medium">
                                Stop {i + 1}
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    const next = draft.stops.filter((_, idx) => idx !== i);
                                    setDraft((d) => ({ ...d, stops: next }));
                                }}
                                className="text-ink-subtle hover:text-red-700 px-1 py-1 -my-1"
                                disabled={saving}
                                title="Remove stop"
                            >
                                <XCircle className="h-4 w-4" />
                            </button>
                        </div>
                       <AddressInput
                                value={stop.address || ''}
                                onChange={(addr) => {
                                    const next = [...draft.stops];
                                    next[i] = { ...next[i], address: addr?.address || '' };
                                    setDraft((d) => ({ ...d, stops: next }));
                                }}
                                disabled={saving}
                                savedAddresses={savedAddresses}
                            />
                       <div className="flex items-center gap-2">
                            <label className="text-xs text-ink-subtle flex-shrink-0 w-10">Time</label>
                            <div className="relative">
                                <Input
                                    type="time"
                                    value={stop.time || ''}
                                    onChange={(e) => {
                                        const next = [...draft.stops];
                                        next[i] = { ...next[i], time: e.target.value };
                                        setDraft((d) => ({ ...d, stops: next }));
                                    }}
                                    disabled={saving}
                                    className="w-32 pr-8"
                                />
                                {stop.time && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const next = [...draft.stops];
                                            next[i] = { ...next[i], time: '' };
                                            setDraft((d) => ({ ...d, stops: next }));
                                        }}
                                        onTouchEnd={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const next = [...draft.stops];
                                            next[i] = { ...next[i], time: '' };
                                            setDraft((d) => ({ ...d, stops: next }));
                                        }}
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink p-1"
                                        disabled={saving}
                                        title="Clear time"
                                        aria-label="Clear time"
                                    >
                                        <XCircle className="h-3.5 w-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
                {isHourly && (
                    <button
                        type="button"
                        onClick={() => setDraft((d) => ({ ...d, stops: [...(d.stops || []), { address: '', time: '' }] }))}
                        className="text-xs text-ink-info hover:underline inline-flex items-center gap-1"
                        disabled={saving}
                    >
                        <Plus className="h-3 w-3" />
                        Add stop
                    </button>
                )}
                {/* Final destination — required in BOTH modes */}
                <div>
                    <label className="block text-xs text-ink-subtle mb-1">
                        {isHourly ? 'Final destination' : 'Dropoff address'}
                    </label>
                    <AddressInput
                        value={draft.dropoff_address}
                        onChange={(addr) => setField('dropoff_address', addr?.address || '')}
                        disabled={saving}
                        savedAddresses={savedAddresses}
                    />
                </div>

                {/* Hourly: final time + live duration pill (Stage 1 + Stage 2) */}
                {isHourly && (
                    <>
                        <div>
                            <label className="block text-xs text-ink-subtle mb-1">Final time</label>
                            <Input
                                type="time"
                                value={draft.final_time}
                                onChange={(e) => setField('final_time', e.target.value)}
                                disabled={saving}
                            />
                            {completedAtTime && completedAtTime !== draft.final_time && (
                                <button
                                    type="button"
                                    onClick={() => setField('final_time', completedAtTime)}
                                    disabled={saving}
                                    className="mt-1.5 text-xs text-ink-info hover:underline inline-flex items-center gap-1"
                                    title="The chauffeur marked this job complete at this time"
                                >
                                    <Clock className="h-3 w-3" />
                                    Chauffeur completed at {completedAtTime} — use as final time
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-ink pt-1">
                            <span className="text-ink-subtle">Total hire duration:</span>
                            <span className="inline-flex items-center px-2.5 py-1 rounded bg-ink text-paper text-xs font-bold">
                                {liveBilling?.billedHours || 2} hr{(liveBilling?.billedHours || 2) !== 1 ? 's' : ''}
                                {liveBilling?.miles !== null && liveBilling?.miles !== undefined && (
                                    <span className="ml-1 opacity-75">· {liveBilling.miles} mi</span>
                                )}
                            </span>
                        </div>
                    </>
                )}
                
                <div>
                    <label className="block text-xs text-ink-subtle mb-1">Vehicle class</label>
                    <Select
                        value={draft.vehicle_class}
                        onChange={(e) => setField('vehicle_class', e.target.value)}
                        disabled={saving}
                    >
                        {Object.entries(VEHICLE_CLASS_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </Select>
                </div>

                {/* Customer reference — optional PO / cost-centre / internal
                    ref. Per-ride. Surfaces on customer-facing emails so the
                    booker's accounts team can match the email to their
                    internal record. */}
                <div>
                    <label className="block text-xs text-ink-subtle mb-1">
                        Customer reference (optional)
                    </label>
                    <Input
                        type="text"
                        value={draft.customer_reference}
                        onChange={(e) => setField('customer_reference', e.target.value)}
                        disabled={saving}
                        placeholder="e.g. PO-2026-0042"
                        maxLength={100}
                    />
                </div>

                {/* Hours visibility to chauffeur — hourly bookings only.
                    Default ON; toggle OFF for open-ended corporate where the
                    dispatcher has agreed an hourly rate but doesn't want to
                    commit to a specific count. When OFF the partner portal
                    and chauffeur email render "Hourly" only. Customer-facing
                    emails always show actual hours. */}
                {isHourly && (
                    <div className="pt-1">
                        <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={draft.hours_disclosed_to_chauffeur}
                                onChange={(e) => setField('hours_disclosed_to_chauffeur', e.target.checked)}
                                disabled={saving}
                                className="mt-0.5"
                            />
                            <div>
                                <div className="text-sm text-ink">
                                    Show hours to chauffeur
                                </div>
                                <div className="text-xs text-ink-subtle mt-0.5">
                                    When off, the chauffeur sees "Hourly" without the hour count.
                                    Useful for open-ended corporate bookings.
                                </div>
                            </div>
                        </label>
                    </div>
                )}
            </div>
        </div>
    );
}

// ----- BookerSubsection --------------------------------------------------
// Edit mode = picker (dropdown of saved bookers + Add new inline form).
// Selecting a different booker updates booker_id; "Add new" creates a
// booker via POST /customers/:id/bookers, then selects them. Inline name/
// phone edits removed in 7d.picker — bookings table has no booker_name/
// _phone denorm columns, so trip-specific overrides have nowhere to live.
// Permanent booker edits go to CustomerDetailPage.
function BookerSubsection({ booking, editing, draft, setField, saving }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const [showAddNew, setShowAddNew] = useState(false);
    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [adding, setAdding] = useState(false);

    const bookersQ = useQuery({
        queryKey: ['customer', booking.customer_id, 'bookers'],
        queryFn: () => api.get(`/customers/${booking.customer_id}/bookers`),
        enabled: editing && !!booking.customer_id,
    });

    const bookers = bookersQ.data?.bookers || [];
    const currentBookerId = draft.booker_id || booking.booker_id;

    async function handleAddNew() {
        if (!newName.trim() || !newEmail.trim()) {
            toast.error('Name and email required');
            return;
        }
        setAdding(true);
        try {
            const created = await api.post(`/customers/${booking.customer_id}/bookers`, {
                name: newName.trim(),
                email: newEmail.trim(),
                phone: newPhone.trim() || null,
            });
            await queryClient.invalidateQueries({
                queryKey: ['customer', booking.customer_id, 'bookers'],
            });
            setField('booker_id', created.id);
            setShowAddNew(false);
            setNewName(''); setNewEmail(''); setNewPhone('');
            toast.success('Booker added');
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Could not add booker';
            toast.error(msg);
        } finally {
            setAdding(false);
        }
    }

    if (!editing) {
        return (
            <div>
                <SubsectionLabel label="Booker" />
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <Row icon={UserIcon} label="Name" value={booking.booker_name_current || booking.booker_name || '—'} />
                    <Row icon={Phone} label="Phone" value={booking.booker_phone_current || booking.booker_phone || '—'} />
                    {booking.booker_email && (
                        <Row icon={Mail} label="Email" value={booking.booker_email} />
                    )}
                </dl>
            </div>
        );
    }

    return (
        <div>
            <SubsectionLabel label="Booker" />
            {showAddNew ? (
                <div className="space-y-3">
                    <div className="text-xs text-ink-subtle">
                        Adding a new booker for this customer. They'll be saved and selectable for future bookings.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs text-ink-subtle mb-1">Name *</label>
                            <Input
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                disabled={adding || saving}
                                placeholder="Full name"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-ink-subtle mb-1">Phone</label>
                            <Input
                                value={newPhone}
                                onChange={(e) => setNewPhone(e.target.value)}
                                disabled={adding || saving}
                                placeholder="07000 000000"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-xs text-ink-subtle mb-1">Email *</label>
                            <Input
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                disabled={adding || saving}
                                placeholder="name@example.com"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => { setShowAddNew(false); setNewName(''); setNewEmail(''); setNewPhone(''); }}
                            disabled={adding || saving}
                        >
                            Cancel
                        </Button>
                        <Button size="sm" onClick={handleAddNew} loading={adding} disabled={saving}>
                            Add booker
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    <label className="block text-xs text-ink-subtle">Select booker</label>
                    {bookersQ.isLoading ? (
                        <div className="text-sm text-ink-subtle italic">Loading…</div>
                    ) : (
                        <Select
                            value={currentBookerId || ''}
                            onChange={(e) => {
                                if (e.target.value === '__add_new__') {
                                    setShowAddNew(true);
                                } else {
                                    setField('booker_id', e.target.value);
                                }
                            }}
                            disabled={saving}
                        >
                            {bookers.map((b) => (
                                <option key={b.id} value={b.id}>
                                    {b.name} — {b.email}
                                </option>
                            ))}
                            <option value="__add_new__">+ Add new booker…</option>
                        </Select>
                    )}
                </div>
            )}
        </div>
    );
}

// ----- PassengerSubsection -----------------------------------------------
// Edit mode = picker (dropdown of saved passengers + Add new inline form +
// "(no passenger)" option). Selecting a different passenger updates
// passenger_id and the booking's denorm pax_name / pax_phone columns.
//
// Inline name / phone are gone — those are trip-specific overrides we
// agreed not to expose (they used to corrupt the canonical passengers
// row). Permanent passenger details edit on CustomerDetailPage.
//
// pax_count and ride comments stay as inline fields — they're genuinely
// trip-specific (1 pax this trip, 3 next trip; comments differ per ride).
function PassengerSubsection({ booking, editing, draft, setField, setDraft, saving, savedNotes = [] }) {
    const api = useApi();
    const queryClient = useQueryClient();

    function appendSavedNote(text) {
        setDraft((d) => {
            const current = (d.pax_comments || '').trim();
            const next = current ? `${current}; ${text}` : text;
            return { ...d, pax_comments: next };
        });
    }

    const [showAddNew, setShowAddNew] = useState(false);
    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [adding, setAdding] = useState(false);

    const passengersQ = useQuery({
        queryKey: ['customer', booking.customer_id, 'passengers'],
        queryFn: () => api.get(`/customers/${booking.customer_id}/passengers`),
        enabled: editing && !!booking.customer_id,
    });

    const passengers = passengersQ.data?.passengers || [];
    // draft.passenger_id might not exist initially — fall back to booking.
    // Empty string in the dropdown represents "(no passenger)".
    const currentPassengerId = draft.passenger_id !== undefined
        ? draft.passenger_id
        : (booking.passenger_id || '');

    async function handleAddNew() {
        if (!newName.trim()) {
            toast.error('Name required');
            return;
        }
        setAdding(true);
        try {
            const created = await api.post(`/customers/${booking.customer_id}/passengers`, {
                name: newName.trim(),
                phone: newPhone.trim() || null,
                email: newEmail.trim() || null,
            });
            await queryClient.invalidateQueries({
                queryKey: ['customer', booking.customer_id, 'passengers'],
            });
            setField('passenger_id', created.id);
            setShowAddNew(false);
            setNewName(''); setNewEmail(''); setNewPhone('');
            toast.success('Passenger added');
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Could not add passenger';
            toast.error(msg);
        } finally {
            setAdding(false);
        }
    }

    if (!editing) {
        return (
            <div>
                <SubsectionLabel label="Passenger" />
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <Row icon={UserIcon} label="Name" value={booking.pax_name_current || booking.pax_name || '—'} />
                    <Row icon={Phone} label="Phone" value={booking.pax_phone_current || booking.pax_phone || '—'} />
                    <Row label="Passengers" value={booking.pax_count != null ? String(booking.pax_count) : '—'} />
                    {booking.pax_comments && (
                        <Row label="Booking notes" value={booking.pax_comments} fullWidth />
                    )}
                </dl>
            </div>
        );
    }

    return (
        <div>
            <SubsectionLabel label="Passenger" />
            {showAddNew ? (
                <div className="space-y-3">
                    <div className="text-xs text-ink-subtle">
                        Adding a new passenger for this customer. They'll be saved and selectable for future bookings.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs text-ink-subtle mb-1">Name *</label>
                            <Input
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                disabled={adding || saving}
                                placeholder="Full name"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-ink-subtle mb-1">Phone</label>
                            <Input
                                value={newPhone}
                                onChange={(e) => setNewPhone(e.target.value)}
                                disabled={adding || saving}
                                placeholder="07000 000000"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-xs text-ink-subtle mb-1">Email</label>
                            <Input
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                disabled={adding || saving}
                                placeholder="Optional"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => { setShowAddNew(false); setNewName(''); setNewEmail(''); setNewPhone(''); }}
                            disabled={adding || saving}
                        >
                            Cancel
                        </Button>
                        <Button size="sm" onClick={handleAddNew} loading={adding} disabled={saving}>
                            Add passenger
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <div>
                        <label className="block text-xs text-ink-subtle mb-1">Select passenger</label>
                        {passengersQ.isLoading ? (
                            <div className="text-sm text-ink-subtle italic">Loading…</div>
                        ) : (
                            <Select
                                value={currentPassengerId || ''}
                                onChange={(e) => {
                                    if (e.target.value === '__add_new__') {
                                        setShowAddNew(true);
                                    } else {
                                        setField('passenger_id', e.target.value || null);
                                    }
                                }}
                                disabled={saving}
                            >
                                <option value="">(no saved passenger)</option>
                                {passengers.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}{p.phone ? ` — ${p.phone}` : ''}
                                    </option>
                                ))}
                                <option value="__add_new__">+ Add new passenger…</option>
                            </Select>
                        )}
                    </div>
                    {currentPassengerId && currentPassengerId === (booking.passenger_id || '') && (
                        <div className="sm:max-w-xs">
                            <label className="block text-xs text-ink-subtle mb-1">Passenger phone</label>
                            <Input
                                value={draft.passenger_phone || ''}
                                onChange={(e) => setField('passenger_phone', e.target.value)}
                                disabled={saving}
                                placeholder="07000 000000"
                            />
                            <p className="text-[11px] text-ink-subtle mt-1">Saved to this passenger's record.</p>
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs text-ink-subtle mb-1">Passenger count</label>
                            <Input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={draft.pax_count}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d{1,2}$/.test(v)) {
                                        const n = parseInt(v, 10);
                                        if (v === '' || (n >= 0 && n <= 20)) {
                                            setField('pax_count', v);
                                        }
                                    }
                                }}
                                onBlur={() => {
                                    const n = parseInt(draft.pax_count, 10);
                                    if (!Number.isFinite(n) || n < 1) {
                                        setField('pax_count', '1');
                                    }
                                }}
                                disabled={saving}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-xs text-ink-subtle mb-1">Booking notes <span className="text-ink-subtle/70">(customer + chauffeur)</span></label>
                            <Textarea
                                value={draft.pax_comments}
                                onChange={(e) => setField('pax_comments', e.target.value)}
                                rows={2}
                                disabled={saving}
                                placeholder="Visible to the chauffeur and confirmed back to the customer. Child seat, luggage, access codes, etc."
                            />
                            {savedNotes.length > 0 && (
                                <div className="mt-2">
                                    <div className="text-[11px] uppercase tracking-wide text-ink-subtle font-medium mb-1.5">
                                        Saved notes — tap to add
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {savedNotes.map((n) => (
                                            <button
                                                key={n.id}
                                                type="button"
                                                onClick={() => appendSavedNote(n.note_text)}
                                                disabled={saving}
                                                title={n.note_text}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border border-paper-border bg-paper text-ink hover:bg-canvas/60 transition-colors max-w-full disabled:opacity-50"
                                            >
                                                <Plus className="w-3 h-3 flex-shrink-0 text-ink-subtle" />
                                                <span className="truncate">{n.label || n.note_text}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ----- FlightDetailsSubsection -------------------------------------------
function FlightDetailsSubsection({ booking, editing, draft, setField, saving }) {
    if (!editing) {
        return (
            <div>
                <SubsectionLabel label="Flight details" />
               <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <Row icon={Plane} label="Flight no." value={booking.flight_number || '—'} />
                    <Row label="Tail no." value={booking.tail_number || '—'} />
                    <Row label="PPR number" value={booking.fbo_code || '—'} />
                </dl>
            </div>
        );
    }

    return (
        <div>
            <SubsectionLabel label="Flight details" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <label className="block text-xs text-ink-subtle mb-1">Flight number</label>
                    <Input
                        value={draft.flight_number}
                        onChange={(e) => setField('flight_number', e.target.value)}
                        disabled={saving}
                        placeholder="e.g. BA178"
                    />
                </div>
                <div>
                    <label className="block text-xs text-ink-subtle mb-1">Tail number</label>
                    <Input
                        value={draft.tail_number}
                        onChange={(e) => setField('tail_number', e.target.value)}
                        disabled={saving}
                        placeholder="e.g. G-XLEC"
                    />
                </div>
                <div>
                    <label className="block text-xs text-ink-subtle mb-1">PPR number</label>
                    <Input
                        value={draft.fbo_code}
                        onChange={(e) => setField('fbo_code', e.target.value)}
                        disabled={saving}
                        placeholder="FBO access code"
                    />
                </div>
            </div>
        </div>
    );
}
// ----- NotesSubsection ---------------------------------------------------
// "Internal notes" is back-office only. NOT sent to customer, NOT sent to
// chauffeur. Anything the chauffeur or customer needs to see goes in
// "Notes for chauffeur" (pax_comments) on the Passenger subsection above.
function NotesSubsection({ booking, editing, draft, setField, saving }) {
    if (!editing) {
        return (
            <div className="space-y-5">
                <div>
                    <SubsectionLabel label="Chauffeur notes" />
                    {booking.chauffeur_notes ? (
                        <p className="text-sm text-ink whitespace-pre-wrap">{booking.chauffeur_notes}</p>
                    ) : (
                        <p className="text-sm text-ink-subtle italic">No chauffeur notes.</p>
                    )}
                    <p className="text-[11px] text-ink-subtle italic mt-2">
                        Chauffeur only — never sent to the customer.
                    </p>
                </div>
                <div>
                    <SubsectionLabel label="Internal notes" />
                    {booking.internal_notes ? (
                        <p className="text-sm text-ink whitespace-pre-wrap">{booking.internal_notes}</p>
                    ) : (
                        <p className="text-sm text-ink-subtle italic">No internal notes.</p>
                    )}
                    <p className="text-[11px] text-ink-subtle italic mt-2">
                        Back office only — never sent to customer or chauffeur.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div>
                <SubsectionLabel label="Chauffeur notes" />
                <Textarea
                    value={draft.chauffeur_notes}
                    onChange={(e) => setField('chauffeur_notes', e.target.value)}
                    rows={3}
                    disabled={saving}
                    placeholder="Chauffeur only. Never shown to the customer. E.g. VIP — no small talk, wait inside arrivals, use side entrance."
                />
            </div>
            <div>
                <SubsectionLabel label="Internal notes" />
                <Textarea
                    value={draft.internal_notes}
                    onChange={(e) => setField('internal_notes', e.target.value)}
                    rows={4}
                    disabled={saving}
                    placeholder="Back office only. Never sent to customer or chauffeur. Use for flags, reminders, sensitive context."
                />
            </div>
        </div>
    );
}

// =============================================================================
// HoursReconciliationFooter — Slice 7e.1
// =============================================================================
// Owner-only admin overlay. Two manual hour fields per booking:
//   - customer_hours: hours sold to the customer
//   - chauffeur_hours: hours paid out to the chauffeur
//
// Both are NUMERIC(5,2) nullable on the bookings table. Pure stats input —
// no impact on pricing, invoicing, statements, or any customer-facing
// surface. Drives the future Stats tab aggregations.
//
// Edit pattern: click value → field becomes editable → blur saves → toast.
// No bulk Edit/Save/Cancel buttons; one PATCH per field change.
// =============================================================================
function HoursReconciliationFooter({ booking }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const [customerHours, setCustomerHours] = useState(
        booking.customer_hours != null ? String(booking.customer_hours) : ''
    );
        const [chauffeurHours, setChauffeurHours] = useState(
        booking.chauffeur_hours != null ? String(booking.chauffeur_hours) : ''
    );
    // Hours actually worked — analytical only, never affects pay. Defaults to
    // hours paid (chauffeur_hours) until explicitly set, so the effective-rate
    // denominator is sensible out of the box; the owner overrides when the
    // real worked time differs from what was paid.
    const [workedHours, setWorkedHours] = useState(
        booking.chauffeur_hours_worked != null
            ? String(booking.chauffeur_hours_worked)
            : (booking.chauffeur_hours != null ? String(booking.chauffeur_hours) : '')
    );

    // Re-sync from props if the booking refetches (polling, sibling save).
    // Don't blow away in-flight typing — only sync when the value coming in
    // differs from what we last saved.
    useEffect(() => {
        setCustomerHours(booking.customer_hours != null ? String(booking.customer_hours) : '');
    }, [booking.customer_hours]);
        useEffect(() => {
        setChauffeurHours(booking.chauffeur_hours != null ? String(booking.chauffeur_hours) : '');
    }, [booking.chauffeur_hours]);
    useEffect(() => {
        // Keep worked-hours in sync: use the stored worked value if present,
        // otherwise mirror hours paid as the default.
        setWorkedHours(
            booking.chauffeur_hours_worked != null
                ? String(booking.chauffeur_hours_worked)
                : (booking.chauffeur_hours != null ? String(booking.chauffeur_hours) : '')
        );
    }, [booking.chauffeur_hours_worked, booking.chauffeur_hours]);

    const saveMutation = useMutation({
        mutationFn: (payload) => api.patch(`/bookings/${booking.id}`, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Failed to save';
            toast.error(`Couldn't save hours: ${msg}`);
        },
    });

    // Validation: empty | numeric with up to 2dp | <= 999.99 (NUMERIC(5,2))
    function isValidInput(v) {
        if (v === '') return true;
        return /^\d{0,3}(\.\d{0,2})?$/.test(v);
    }

    function handleBlur(field, currentValue, originalValue) {
        // Normalise empty-or-whitespace to null
        const trimmed = (currentValue ?? '').trim();
        const parsed = trimmed === '' ? null : parseFloat(trimmed);

        // No-op if unchanged. originalValue might be null or a number from
        // the booking row; normalise both sides for comparison.
        const originalParsed = originalValue == null ? null : Number(originalValue);
        if (parsed === originalParsed) return;
        if (parsed != null && !Number.isFinite(parsed)) {
            // Invalid — revert visual state to what's stored
                        if (field === 'customer_hours') {
                setCustomerHours(originalValue != null ? String(originalValue) : '');
            } else if (field === 'chauffeur_hours_worked') {
                setWorkedHours(originalValue != null ? String(originalValue) : '');
            } else {
                setChauffeurHours(originalValue != null ? String(originalValue) : '');
            }
            toast.error('Hours must be a number');
            return;
        }

        saveMutation.mutate({ [field]: parsed });
    }

    // Margin display — only when both are filled in. Keeps the footer
    // visually quiet until there's actual reconciliation data.
    const customerNum = booking.customer_hours != null ? Number(booking.customer_hours) : null;
    const chauffeurNum = booking.chauffeur_hours != null ? Number(booking.chauffeur_hours) : null;
    const showDelta = customerNum != null && chauffeurNum != null;
    const delta = showDelta ? customerNum - chauffeurNum : null;

    return (
        <div className="pt-4 mt-1 border-t border-paper-border-soft">
            <div className="flex items-center justify-between mb-2.5">
                <h3 className="text-xs uppercase tracking-[0.1em] text-ink font-bold">
                    Hours reconciliation
                </h3>
                <span className="text-[10px] uppercase tracking-wide font-semibold text-ink-subtle">
                    Owner only
                </span>
            </div>
            <p className="text-xs text-ink-subtle mb-3">
                Manual log of hours sold vs hours paid out — drives stats only, no impact on invoicing.
            </p>
                                  <div className="grid grid-cols-3 gap-3">
                <div>
                    <label className="block text-xs text-ink-subtle mb-1">
                        Hours sold to customer
                    </label>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={customerHours}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (isValidInput(v)) setCustomerHours(v);
                        }}
                        onBlur={() => handleBlur('customer_hours', customerHours, booking.customer_hours)}
                        disabled={saveMutation.isPending}
                        placeholder="—"
                        className="w-full text-sm border border-paper-border rounded px-2 py-1.5 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40 disabled:cursor-not-allowed"
                    />
                </div>
                <div>
                    <label className="block text-xs text-ink-subtle mb-1">
                        Hours paid to chauffeur
                    </label>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={chauffeurHours}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (isValidInput(v)) setChauffeurHours(v);
                        }}
                        onBlur={() => handleBlur('chauffeur_hours', chauffeurHours, booking.chauffeur_hours)}
                        disabled={saveMutation.isPending}
                        placeholder="—"
                        className="w-full text-sm border border-paper-border rounded px-2 py-1.5 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40 disabled:cursor-not-allowed"
                    />
                </div>
                <div>
                    <label className="block text-xs text-ink-subtle mb-1">
                        Hours actually worked
                    </label>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={workedHours}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (isValidInput(v)) setWorkedHours(v);
                        }}
                        onBlur={() => handleBlur('chauffeur_hours_worked', workedHours, booking.chauffeur_hours_worked)}
                        disabled={saveMutation.isPending}
                        placeholder="—"
                        className="w-full text-sm border border-paper-border rounded px-2 py-1.5 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            
            {showDelta && (
                <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-ink-subtle">Delta (customer − chauffeur)</span>
                    <span className={`font-semibold ${delta >= 0 ? 'text-ink' : 'text-red-700'}`}>
                        {delta > 0 ? '+' : ''}{delta.toFixed(2)} hrs
                    </span>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// BillingDetailsCard — Slice 7d.6 Piece 3 (billing card frontend restructure)
// =============================================================================
// Two-sided editable ledger replacing the old single-quoted-price card.
//
// Layout:
//   - Payment-method strip (unchanged, reuses renderPaymentLine)
//   - "Quote: £X" anchor row (read-only reference to original quoted_price)
//   - Customer | Chauffeur columns, each:
//       * Status pill (Pending / Invoiced / Statement raised)
//       * Line rows: label + amount + VAT toggle + delete (×)
//       * "+ Add line" button
//       * Net / VAT / Gross totals
//       * Action button (Mark invoiced / Raise statement) OR Locked pill
//       * Customer side: "Prefill from quote" button when empty
//   - Margin row (customer gross − chauffeur gross)
//
// Edit semantics (locked in design phase):
//   - Inline save on blur with optimistic UI.
//   - Locked side → all inputs disabled, Locked pill shown. Editing or
//     deleting fires LockOverrideConfirm modal; on confirm sends mutation
//     with override_lock:true (audit-logged backend-side).
//   - Side status PATCH (lock/unlock) is owner-confirmed via Modal.
//
// Owner-only gating is enforced at the parent level — this card is rendered
// only inside `{isOwner && <BillingDetailsCard ... />}` in BookingDetailPage.
// Backend routes are also owner-gated as defence in depth.
// =============================================================================

function BillingDetailsCard({ booking }) {
    const api = useApi();
    const queryClient = useQueryClient();

    // Manual invoice number — free text, owner-only, saved via the standard
    // booking PATCH. Local state seeded from the booking; a small inline save
    // fires on blur (or Enter) so it doesn't need its own edit-mode toggle.
    const [invoiceNumber, setInvoiceNumber] = useState(booking.invoice_number || '');
    const [savingInvoice, setSavingInvoice] = useState(false);

    useEffect(() => {
        setInvoiceNumber(booking.invoice_number || '');
    }, [booking.invoice_number]);

    const saveInvoiceNumber = async () => {
        const next = invoiceNumber.trim();
        if (next === (booking.invoice_number || '')) return; // no change
        setSavingInvoice(true);
        try {
            await api.patch(`/bookings/${booking.id}`, { invoice_number: next || null });
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            toast.success('Invoice number saved');
        } catch (err) {
            toast.error(err?.message || 'Could not save invoice number');
            setInvoiceNumber(booking.invoice_number || ''); // revert on failure
        } finally {
            setSavingInvoice(false);
        }
    };

    // Fetch both sides in parallel. Independent queries so refetches don't
    // affect each other and React Query caches/dedupes naturally.
    const customerQ = useQuery({
        queryKey: ['booking', booking.id, 'billing-lines', 'customer'],
        queryFn: () => api.get(`/bookings/${booking.id}/billing-lines?side=customer`),
    });
    const chauffeurQ = useQuery({
        queryKey: ['booking', booking.id, 'billing-lines', 'chauffeur'],
        queryFn: () => api.get(`/bookings/${booking.id}/billing-lines?side=chauffeur`),
    });

    const customerLines = customerQ.data?.lines || [];
    const chauffeurLines = chauffeurQ.data?.lines || [];

    // Side-status comes from the booking row itself, not the lines query.
    // Backend stamps customer_billing_status / chauffeur_billing_status on
    // PATCH /:id/billing-status; the booking detail GET surfaces them.
    const customerStatus = booking.customer_billing_status || 'pending';
    const chauffeurStatus = booking.chauffeur_billing_status || 'pending';
    const customerLocked = customerStatus !== 'pending';
    const chauffeurLocked = chauffeurStatus !== 'pending';

    const hasAnyPricingInfo =
        booking.quoted_price != null ||
        booking.charged_amount != null ||
        booking.payment_method ||
        booking.stripe_payment_intent_id ||
        customerLines.length > 0 ||
        chauffeurLines.length > 0;

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-ink-subtle" />
                    Billing
                </h2>
            </div>
                        <div className="card-body space-y-5">
                {!hasAnyPricingInfo && (
                    <p className="text-sm text-ink-subtle italic">No billing information.</p>
                )}

                {/* Manual invoice number — always visible. Free text, not unique
                    (several bookings can share one consolidated invoice number).
                    Saves on blur / Enter via the standard booking PATCH. */}
                <div>
                    <label className="text-xs uppercase tracking-wide text-ink-subtle font-medium block mb-1">
                        Invoice number
                    </label>
                    <input
                        type="text"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        onBlur={saveInvoiceNumber}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                        disabled={savingInvoice}
                        placeholder="Optional — enter when invoiced"
                        className="w-full text-sm px-2 py-1.5 border border-line rounded bg-white disabled:opacity-50"
                    />
                </div>

                {/* Payment-method strip (unchanged from old card) */}
                {booking.payment_method && (
                    <div className="text-sm">
                        <span className="text-xs uppercase tracking-wide text-ink-subtle font-medium">
                            Payment:{' '}
                        </span>
                        {renderPaymentLine(booking)}
                    </div>
                )}

            {/* Original quote anchor — read-only reference.
                    When a customer discount was applied, the retail price + pct
                    appear inline so the audit trail is visible at a glance:
                    "Retail £200.00 · 10% off · Original quote: £174.60". */}
                {booking.quoted_price != null && (
                    <div className="text-xs text-ink-subtle">
                        {booking.retail_price != null && booking.discount_pct_applied != null && (
                            <span>
                                Retail:{' '}
                                <span className="line-through text-ink-subtle">
                                    {formatPrice(booking.retail_price)}
                                </span>
                                {' · '}
                                <span className="text-ink font-medium">
                                    {Number(booking.discount_pct_applied)}% off
                                </span>
                                {' · '}
                            </span>
                        )}
                        Original quote: <span className="text-ink font-medium">{formatPrice(booking.quoted_price)}</span>
                        {booking.charged_amount != null && Number(booking.charged_amount) !== Number(booking.quoted_price) && (
                            <span> · Charged: <span className="text-ink font-medium">{formatPrice(booking.charged_amount)}</span></span>
                        )}
                        {booking.stripe_payment_intent_id && (
                            <span>
                                {' · '}
                                <a
                                    href={`https://dashboard.stripe.com/payments/${booking.stripe_payment_intent_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-ink-info hover:underline font-mono"
                                >
                                    Stripe ↗
                                </a>
                            </span>
                        )}
                    </div>
                )}

                {/* Two-row ledger — stacked vertically (slice 7d.picker UX polish).
                    Each side full width = more room for line rows (label + type
                    pill + amount + VAT + delete). Margin row at the bottom
                    still gives the at-a-glance comparison. */}
                <div className="space-y-5 pt-1">
                    <BillingLedgerSide
                        booking={booking}
                        side="customer"
                        title="Customer"
                        lines={customerLines}
                        loading={customerQ.isLoading}
                        status={customerStatus}
                        locked={customerLocked}
                        lockedAt={booking.customer_invoiced_at}
                        lockActionLabel="Mark as invoiced"
                        lockTargetStatus="invoiced"
                        lockedPillLabel="Invoiced"
                        canPrefill={customerLines.length === 0 && booking.price_breakdown}
                        vatRegistered={true}
                    />

                    <BillingLedgerSide
                        booking={booking}
                        side="chauffeur"
                        title="Chauffeur"
                        lines={chauffeurLines}
                        loading={chauffeurQ.isLoading}
                        status={chauffeurStatus}
                        locked={chauffeurLocked}
                        lockedAt={booking.chauffeur_statement_raised_at}
                        lockActionLabel="Add to statement"
                        lockTargetStatus="statement_raised"
                        lockedPillLabel="Statement raised"
                        canPrefill={
                            chauffeurLines.length === 0
                            && !!booking.allocated_chauffeur_id
                            && booking.chauffeur_commission_pct != null
                            && customerLines.some((l) => (l.line_type || 'fare') === 'fare')
                        }
                        vatRegistered={booking.chauffeur_vat_registered === true}
                    />
                </div>

        {/* Margin row removed — Net margin (including operating costs)
                    is shown by OperatingCostsCard below. One source of truth. */}

                {/* Slice 7e.1 — owner-only hours reconciliation footer */}
                <OwnerOnlyHoursFooter booking={booking} />
            </div>
        </div>
    );
}

// Tiny wrapper that gates the footer at the call site. Defined separately
// from HoursReconciliationFooter so the latter doesn't need to know about
// auth — useful if we ever want to render it elsewhere conditionally.
function OwnerOnlyHoursFooter({ booking }) {
    const { isOwner } = useCurrentUser();
    if (!isOwner) return null;
    return <HoursReconciliationFooter booking={booking} />;
}

// -----------------------------------------------------------------------------
// One column of the ledger. Self-contained — owns its own line edits +
// status mutation. The two sides differ only in labels/copy/colour.
// -----------------------------------------------------------------------------
function BillingLedgerSide({
    booking,
    side,
    title,
    lines,
    loading,
    status,
    locked,
    lockedAt,
    lockActionLabel,
    lockTargetStatus,
    lockedPillLabel,
    canPrefill,
    vatRegistered = false,
}) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [showLockConfirm, setShowLockConfirm] = useState(false);
    const [showOverrideConfirm, setShowOverrideConfirm] = useState(null);
    // null | { kind: 'edit', payload, lineId } | { kind: 'delete', lineId }
    //       | { kind: 'create', payload }

    const queryKey = ['booking', booking.id, 'billing-lines', side];

    function invalidateLines() {
        return queryClient.invalidateQueries({ queryKey });
    }

    // --- Mutations ---------------------------------------------------------

    const createMutation = useMutation({
        mutationFn: (payload) =>
            api.post(`/bookings/${booking.id}/billing-lines`, payload),
        onSuccess: () => {
            invalidateLines();
        },
        onError: (err) => {
            const code = err?.response?.data?.code;
            if (code === 'billing_side_locked') {
                // Shouldn't reach here for create — locked side hides Add Line.
                // Fallback: surface the message.
                toast.error(err?.response?.data?.error || 'Side is locked');
                return;
            }
            const msg = err?.response?.data?.error || err?.message || 'Could not add line';
            toast.error(msg);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ lineId, payload }) =>
            api.patch(`/bookings/${booking.id}/billing-lines/${lineId}`, payload),
        onSuccess: () => {
            invalidateLines();
        },
        onError: (err) => {
            const code = err?.response?.data?.code;
            if (code === 'billing_side_locked') {
                // Caller handles the override flow — we toast a fallback only
                // if someone slipped past it.
                toast.error('Side is locked. Use override to edit.');
                invalidateLines(); // resync local optimism
                return;
            }
            const msg = err?.response?.data?.error || err?.message || 'Could not update';
            toast.error(msg);
            invalidateLines();
        },
    });

    const deleteMutation = useMutation({
        mutationFn: ({ lineId, override_lock }) =>
            api.delete(
                `/bookings/${booking.id}/billing-lines/${lineId}` +
                (override_lock ? '?override_lock=true' : '')
            ),
        onSuccess: () => {
            toast.success('Line removed');
            invalidateLines();
        },
        onError: (err) => {
            const code = err?.response?.data?.code;
            if (code === 'billing_side_locked') {
                toast.error('Side is locked. Use override to delete.');
                return;
            }
            const msg = err?.response?.data?.error || err?.message || 'Could not delete';
            toast.error(msg);
        },
    });
const prefillMutation = useMutation({
        mutationFn: () =>
            side === 'chauffeur'
                ? api.post(`/bookings/${booking.id}/billing-lines/commission-prefill`)
                : api.post(`/bookings/${booking.id}/billing-lines/prefill?side=${side}`),
        onSuccess: (data) => {
            if (data?.note) {
                toast(data.note);
            } else {
                toast.success(`Prefilled ${data?.lines?.length || 0} line${(data?.lines?.length || 0) !== 1 ? 's' : ''}`);
            }
            invalidateLines();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not prefill';
            toast.error(msg);
        },
    });

    const statusMutation = useMutation({
        mutationFn: (targetStatus) =>
            api.patch(`/bookings/${booking.id}/billing-status`, {
                side,
                status: targetStatus,
            }),
        onSuccess: (data) => {
            const verb = data?.status === 'pending' ? 'unlocked' : 'locked';
            toast.success(`${title} side ${verb}`);
            // Status lives on the booking row, so invalidate the booking too.
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            invalidateLines();
            setShowLockConfirm(false);
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not change status';
            toast.error(msg);
        },
    });

    // --- Lock-override coordinator ----------------------------------------
    // When the side is locked, mutations bounce through this confirmation.
    // If user confirms, we re-fire the mutation with override_lock:true.

    function handleEditAttempt({ lineId, payload }) {
        if (locked) {
            setShowOverrideConfirm({ kind: 'edit', lineId, payload });
            return;
        }
        updateMutation.mutate({ lineId, payload });
    }

    function handleDeleteAttempt({ lineId }) {
        if (locked) {
            setShowOverrideConfirm({ kind: 'delete', lineId });
            return;
        }
        // Simple confirm for unlocked deletes — lock-override is its own modal.
        if (!window.confirm('Remove this line?')) return;
        deleteMutation.mutate({ lineId, override_lock: false });
    }

    function confirmOverride() {
        if (!showOverrideConfirm) return;
        const { kind, lineId, payload } = showOverrideConfirm;
        if (kind === 'edit') {
            updateMutation.mutate({ lineId, payload: { ...payload, override_lock: true } });
        } else if (kind === 'delete') {
            deleteMutation.mutate({ lineId, override_lock: true });
        }
        setShowOverrideConfirm(null);
    }

    // --- Totals ------------------------------------------------------------

    const totals = computeLineTotals(lines);

    // --- Render ------------------------------------------------------------

    return (
        <div className={`rounded-lg border ${locked ? 'border-paper-border bg-canvas/40' : 'border-paper-border-soft'} p-4`}>
            {/* Header: title + status pill */}
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-[0.1em] text-ink font-bold">
                    {title}
                </h3>
                {locked ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold bg-ink text-paper">
                        {lockedPillLabel}
                    </span>
                ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold bg-amber-100 text-amber-800">
                        Pending
                    </span>
                )}
            </div>

            {/* Lines */}
            {loading ? (
                <div className="text-xs text-ink-subtle italic py-2">Loading…</div>
            ) : (
                <div className="space-y-2">
                   {lines.map((line) => (
                        <BillingLineRow
                            key={line.id}
                            line={line}
                            locked={locked}
                            saving={updateMutation.isPending || deleteMutation.isPending}
                            onSave={(payload) => handleEditAttempt({ lineId: line.id, payload })}
                            onDelete={() => handleDeleteAttempt({ lineId: line.id })}
                            side={side}
                            vatRegistered={vatRegistered}
                        />
                    ))}

                    {!locked && (
                        <NewLineForm
                            saving={createMutation.isPending}
                            onCreate={(payload) => createMutation.mutate({ ...payload, side })}
                            side={side}
                            vatRegistered={vatRegistered}
                        />
                    )}

                    {lines.length === 0 && locked && (
                        <p className="text-xs text-ink-subtle italic py-2">No lines.</p>
                    )}
                </div>
            )}

            {/* Totals */}
            {lines.length > 0 && (
                <div className="mt-4 pt-3 border-t border-paper-border-soft space-y-1 text-xs">
                    <div className="flex justify-between text-ink-subtle">
                        <span>Net</span>
                        <span>{formatPrice(totals.net)}</span>
                    </div>
                    <div className="flex justify-between text-ink-subtle">
                        <span>VAT</span>
                        <span>{formatPrice(totals.vat)}</span>
                    </div>
                    <div className="flex justify-between text-ink font-semibold text-sm pt-1">
                        <span>Gross</span>
                        <span>{formatPrice(totals.gross)}</span>
                    </div>
                </div>
            )}

            {/* Action footer */}
            <div className="mt-4 flex flex-col gap-2">
                {canPrefill && (
                    <Button
                        variant="secondary"
                        onClick={() => prefillMutation.mutate()}
                        loading={prefillMutation.isPending}
                        className="w-full text-xs"
                    >
                        {side === 'chauffeur' ? 'Prefill from commission' : 'Prefill from quote'}
                    </Button>
                )}

                {!locked && lines.length > 0 && (
                    <Button
                        variant="primary"
                        onClick={() => setShowLockConfirm(true)}
                        className="w-full text-xs"
                    >
                        {lockActionLabel}
                    </Button>
                )}

                {locked && (
                    <>
                        {lockedAt && (
                            <p className="text-[11px] text-ink-subtle text-center">
                                {lockedPillLabel} {formatRelativeTime(lockedAt)}
                            </p>
                        )}
                        <Button
                            variant="secondary"
                            onClick={() => statusMutation.mutate('pending')}
                            loading={statusMutation.isPending}
                            className="w-full text-xs"
                        >
                            Unlock
                        </Button>
                    </>
                )}
            </div>

            {/* Lock confirmation modal */}
            {showLockConfirm && (
                <Modal
                    open
                    onClose={() => setShowLockConfirm(false)}
                    title={`${lockActionLabel}?`}
                    description={`This locks the ${title.toLowerCase()} side. You'll need to confirm an override to edit any line afterwards. The change is audit-logged.`}
                    size="md"
                >
                    <div className="text-sm text-ink-muted mt-1">
                        Total: <span className="font-semibold text-ink">{formatPrice(totals.gross)}</span> across {lines.length} line{lines.length !== 1 ? 's' : ''}.
                    </div>
                    <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button
                            variant="secondary"
                            onClick={() => setShowLockConfirm(false)}
                            disabled={statusMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => statusMutation.mutate(lockTargetStatus)}
                            loading={statusMutation.isPending}
                        >
                            {lockActionLabel}
                        </Button>
                    </div>
                </Modal>
            )}

            {/* Override confirmation modal */}
            {showOverrideConfirm && (
                <Modal
                    open
                    onClose={() => setShowOverrideConfirm(null)}
                    title="Override the lock?"
                    description={`The ${title.toLowerCase()} side is locked. Continuing will modify a finalised line and is audit-logged as an override.`}
                    size="md"
                >
                    <div className="text-sm text-ink-muted mt-1">
                        {showOverrideConfirm.kind === 'delete'
                            ? 'This will permanently remove the line.'
                            : 'This will save your changes to the locked line.'}
                    </div>
                    <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button
                            variant="secondary"
                            onClick={() => setShowOverrideConfirm(null)}
                            disabled={updateMutation.isPending || deleteMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={confirmOverride}
                            loading={updateMutation.isPending || deleteMutation.isPending}
                        >
                            Override and continue
                        </Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// One row in the ledger. Inline-editable on blur (label + amount + VAT).
//
// 7d.7 additions:
//   - Chauffeur side: Fare/Expense toggle pill (click to flip)
//     · Switching to Expense forces VAT to 0% (dropdown locked)
//     · Switching back to Fare resets to chauffeur's default VAT
//   - VAT% defaults respect chauffeur.vat_registered
// -----------------------------------------------------------------------------
function BillingLineRow({ line, locked, saving, onSave, onDelete, side, vatRegistered }) {
    const [label, setLabel] = useState(line.label);
    const [amount, setAmount] = useState(String(line.amount_ex_vat ?? ''));
    const [vatRate, setVatRate] = useState(Number(line.vat_rate) || 0);
    const [lineType, setLineType] = useState(line.line_type || 'fare');

    // Re-sync local state when the line prop changes (e.g. after refetch
    // following a successful save).
    useEffect(() => {
        setLabel(line.label);
        setAmount(String(line.amount_ex_vat ?? ''));
        setVatRate(Number(line.vat_rate) || 0);
        setLineType(line.line_type || 'fare');
    }, [line.id, line.label, line.amount_ex_vat, line.vat_rate, line.line_type]);

    function handleLabelBlur() {
        const next = label.trim();
        if (next === line.label) return;
        if (next.length === 0) {
            setLabel(line.label); // revert
            toast.error('Label cannot be empty');
            return;
        }
        onSave({ label: next });
    }

    function handleAmountBlur() {
        const parsed = parseFloat(amount);
        if (!Number.isFinite(parsed)) {
            setAmount(String(line.amount_ex_vat ?? ''));
            toast.error('Amount must be a number');
            return;
        }
        if (parsed === Number(line.amount_ex_vat)) return;
        onSave({ amount_ex_vat: parsed });
    }

    function handleVatChange(e) {
        const next = parseFloat(e.target.value);
        setVatRate(next);
        if (next === Number(line.vat_rate)) return;
        onSave({ vat_rate: next });
    }

    // 7d.7 — toggle line type. Switching to expense forces VAT to 0;
    // switching to fare restores the chauffeur's default. Both line_type
    // and vat_rate land in the same PATCH.
function toggleLineType() {
        if (locked || saving) return;
        const next = lineType === 'fare' ? 'expense' : 'fare';
        // Toggle just changes categorisation. VAT rate is set independently
        // and dispatcher can edit it directly via the VAT dropdown — we don't
        // overwrite it on toggle.
        setLineType(next);
        onSave({ line_type: next });
    }

    // VAT rate is always editable — line type just sets the default on creation
    // and on toggle. Dispatcher can override per line.
    const vatLocked = false;
    const isExpense = lineType === 'expense';

    return (
        <div className="flex items-center gap-1.5">
            <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onBlur={handleLabelBlur}
                disabled={locked || saving}
                className="flex-1 min-w-0 text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40 disabled:cursor-not-allowed"
                placeholder="Description"
            />
            <button
                type="button"
                onClick={toggleLineType}
                disabled={locked || saving}
                title={
                    isExpense
                        ? 'Expense — pass-through reimbursement (no VAT). Click to flip to Fare.'
                        : `Fare — ${side === 'chauffeur' ? 'chauffeur earnings' : 'customer revenue'}. Click to flip to Expense.`
                }
                className={`flex-shrink-0 text-[10px] uppercase tracking-wide font-semibold rounded px-2 py-1 border transition-colors ${
                    isExpense
                        ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200'
                        : 'bg-canvas border-paper-border text-ink-subtle hover:bg-paper-hover'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
                {isExpense ? 'Expense' : 'Fare'}
            </button>
            <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^-?\d*\.?\d{0,2}$/.test(v)) setAmount(v);
                }}
                onBlur={handleAmountBlur}
                disabled={locked || saving}
                className="w-20 text-right text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40 disabled:cursor-not-allowed"
                placeholder="0.00"
            />
            <select
                value={vatRate}
                onChange={handleVatChange}
                disabled={locked || saving || vatLocked}
                className="text-xs border border-paper-border rounded px-1.5 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40 disabled:cursor-not-allowed"
                title={vatLocked ? 'Expenses are gross pass-through — VAT is always 0%' : undefined}
            >
                <option value={0}>0%</option>
                <option value={0.2}>20%</option>
            </select>
            <button
                type="button"
                onClick={onDelete}
                disabled={saving}
                className="text-ink-subtle hover:text-red-700 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                title={locked ? 'Override required to delete' : 'Remove line'}
            >
                <XCircle className="h-4 w-4" />
            </button>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Inline form for a new line. Same shape as BillingLineRow.
//
// 7d.7 additions:
//   - Chauffeur side: Fare/Expense toggle pill (click to flip)
//     · Default = Fare
//     · Selecting Expense forces VAT to 0% and locks the VAT dropdown
//   - VAT default depends on side + chauffeur VAT status:
//     · Customer side: 20%
//     · Chauffeur fare, VAT-registered: 20%
//     · Chauffeur fare, non-VAT: 0%
//     · Chauffeur expense: 0% (always)
// -----------------------------------------------------------------------------
function NewLineForm({ saving, onCreate, side, vatRegistered }) {
    function defaultFareVat() {
        return vatRegistered ? 0.2 : 0;
    }

    const [label, setLabel] = useState('');
    const [amount, setAmount] = useState('');
    const [vatRate, setVatRate] = useState(defaultFareVat());
    const [lineType, setLineType] = useState('fare');
    const [adding, setAdding] = useState(false);

    function resetForm() {
        setLabel('');
        setAmount('');
        setLineType('fare');
        setVatRate(defaultFareVat());
        setAdding(false);
    }

    function toggleLineType() {
        if (saving) return;
        const next = lineType === 'fare' ? 'expense' : 'fare';
        setLineType(next);
        setVatRate(next === 'expense' ? 0 : defaultFareVat());
    }

  function tryCreate() {
        const trimmedLabel = label.trim();
        const parsedAmount = parseFloat(amount);
        if (trimmedLabel.length === 0) {
            toast.error('Label required');
            return;
        }
        if (!Number.isFinite(parsedAmount)) {
            toast.error('Amount required');
            return;
        }
        const payload = {
            label: trimmedLabel,
            amount_ex_vat: parsedAmount,
            vat_rate: vatRate,
            line_type: lineType,
        };
        onCreate(payload);
        resetForm();
    }

    if (!adding) {
        return (
            <button
                type="button"
                onClick={() => setAdding(true)}
                className="text-xs text-ink-info hover:underline inline-flex items-center gap-1 mt-1"
            >
                <Plus className="h-3 w-3" />
                Add line
            </button>
        );
    }

const vatLocked = false;
    const isExpense = lineType === 'expense';

    return (
        <div className="flex items-center gap-1.5 pt-1">
            <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                disabled={saving}
                placeholder={
                    isExpense
                        ? 'e.g. Car park, Tolls'
                        : 'Description'
                }
                autoFocus
                className="flex-1 min-w-0 text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
            />
            <button
                type="button"
                onClick={toggleLineType}
                disabled={saving}
                title={
                    isExpense
                        ? 'Expense — pass-through reimbursement (no VAT). Click to flip to Fare.'
                        : `Fare — ${side === 'chauffeur' ? 'chauffeur earnings' : 'customer revenue'}. Click to flip to Expense.`
                }
                className={`flex-shrink-0 text-[10px] uppercase tracking-wide font-semibold rounded px-2 py-1 border transition-colors ${
                    isExpense
                        ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200'
                        : 'bg-canvas border-paper-border text-ink-subtle hover:bg-paper-hover'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
                {isExpense ? 'Expense' : 'Fare'}
            </button>
            <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^-?\d*\.?\d{0,2}$/.test(v)) setAmount(v);
                }}
                disabled={saving}
                placeholder="0.00"
                className="w-20 text-right text-sm border border-paper-border rounded px-2 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
            />
            <select
                value={vatRate}
                onChange={(e) => setVatRate(parseFloat(e.target.value))}
                disabled={saving || vatLocked}
                className="text-xs border border-paper-border rounded px-1.5 py-1 bg-paper text-ink focus:outline-none focus:ring-1 focus:ring-ink/30 disabled:bg-canvas/40"
                title={vatLocked ? 'Expenses are gross pass-through — VAT is always 0%' : undefined}
            >
                <option value={0}>0%</option>
                <option value={0.2}>20%</option>
            </select>
            <button
                type="button"
                onClick={tryCreate}
                disabled={saving}
                className="text-xs text-ink-info hover:underline px-1 disabled:opacity-50"
                title="Save line"
            >
                Save
            </button>
            <button
                type="button"
                onClick={resetForm}
                disabled={saving}
                className="text-ink-subtle hover:text-ink flex-shrink-0"
                title="Cancel"
            >
                <XCircle className="h-4 w-4" />
            </button>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Margin row at the bottom of the card. Hidden when either side has no lines
// (margin against zero is meaningless and visually noisy).
// -----------------------------------------------------------------------------
function BillingMarginRow({ customerLines, chauffeurLines }) {
    if (customerLines.length === 0 || chauffeurLines.length === 0) return null;

    const customerGross = computeLineTotals(customerLines).gross;
    const chauffeurGross = computeLineTotals(chauffeurLines).gross;
    const margin = customerGross - chauffeurGross;
    const marginPct = customerGross > 0 ? (margin / customerGross) * 100 : 0;

    const positive = margin >= 0;

    return (
        <div className="pt-3 border-t border-paper-border-soft flex items-center justify-between text-sm">
            <span className="text-ink-subtle">Margin</span>
            <span className={`font-semibold ${positive ? 'text-ink' : 'text-red-700'}`}>
                {formatPrice(margin)}
                {customerGross > 0 && (
                    <span className="text-ink-subtle font-normal ml-2">
                        ({marginPct.toFixed(1)}%)
                    </span>
                )}
            </span>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Pure helper — sum lines into net / vat / gross.
// All amounts come from the API as numbers (route hydrateLine coerces them).
// -----------------------------------------------------------------------------
function computeLineTotals(lines) {
    let net = 0;
    let vat = 0;
    for (const line of lines) {
        const amt = Number(line.amount_ex_vat) || 0;
        const rate = Number(line.vat_rate) || 0;
        net += amt;
        vat += amt * rate;
    }
    return {
        net,
        vat,
        gross: net + vat,
    };
}

// =============================================================================
// AdjustmentsCard (Round 7d.2.5 + 7d.2.5b review-flow rewrite)
// =============================================================================
// Lists `booking.adjustments` (added to GET /:id response in 7d.2.5 backend).
//
// 7d.2.5b model:
//   1. Dispatcher adds rows via "+ Add extra charge" — they persist as
//      stripe_status='pending_review' (no Stripe call yet).
//   2. Once any pending rows exist, two buttons appear: "Notify" (email
//      the customer the itemised list) and "Charge £X" (fire ONE Stripe
//      PaymentIntent for the combined total).
//   3. Charged rows lock. Pending rows can be deleted (× per row).
//
// Soft nudge: if dispatcher hasn't notified the customer about the most
// recent pending rows, the Charge confirmation modal shows a one-click
// "Customer hasn't been notified — charge anyway?" warning. Doesn't block.
//
// Visibility rules:
//   - Card always renders for completed bookings (so dispatcher sees the
//     "+ Add extra charge" affordance even on empty state).
//   - On cancelled / no_show: card hidden entirely UNLESS pre-existing
//     adjustments to display (data-integrity rule — never hide existing
//     rows).
//
// Total line at the bottom shows running total. Refunds (when they appear
// here, via the cancel flow in a later slice) would subtract.
// =============================================================================
function AdjustmentsCard({ booking }) {
    const [adding, setAdding] = useState(false);
    const [showNotify, setShowNotify] = useState(false);
    const [showCharge, setShowCharge] = useState(false);

    const adjustments = booking.adjustments || [];

    const canAdd = booking.status === 'completed';
    const isHidden = booking.status === 'cancelled' || booking.status === 'no_show';

    if (isHidden && adjustments.length === 0) {
        return null;
    }

    // Pending rows (need review, not charged yet) vs everything else.
    const pendingRows = adjustments.filter((a) => a.stripe_status === 'pending_review');
    const settledRows = adjustments.filter((a) => a.stripe_status !== 'pending_review');

    const pendingTotalPence = pendingRows.reduce((s, a) => s + a.amount_pence, 0);

    // Settled total — what's already been charged / billed. Failed rows
    // excluded (Stripe didn't take the money). Refunds would subtract.
    const settledTotalPence = settledRows.reduce((sum, a) => {
        if (a.stripe_status === 'failed') return sum;
        const sign = a.kind === 'refund' ? -1 : 1;
        return sum + sign * a.amount_pence;
    }, 0);

    // Soft-nudge logic. A pending row counts as "notified" if notified_at
    // is stamped at all. The earlier "notified_at >= created_at" comparison
    // was too strict — Postgres returns notified_at < created_at by a few
    // microseconds when both happen in quick succession, which fired the
    // nudge even when notification had genuinely been sent.
    //
    // The simpler rule (any notified_at = notified) is correct for the
    // 99% case: dispatcher adds rows, clicks Notify, Notify stamps every
    // pending row, dispatcher clicks Charge.
    //
    // The 1% edge case (dispatcher notifies, then ADDS another row, then
    // charges without re-notifying) — that new row will have notified_at
    // = NULL (the original Notify happened before it existed), so the
    // nudge correctly fires.
    const allPendingNotified = pendingRows.length > 0 && pendingRows.every((a) => {
        return a.notified_at != null;
    });
    const needsNudge = pendingRows.length > 0 && !allPendingNotified;

    return (
        <div className="card">
            <div className="card-header flex items-start justify-between">
                <div>
                    <h2 className="text-sm font-semibold text-ink">Adjustments</h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Extras added after the ride completed. Charged together to the same card as the original.
                    </p>
                </div>
                {!adding && canAdd && (
                    <button
                        type="button"
                        onClick={() => setAdding(true)}
                        className="text-xs text-ink-subtle hover:text-ink inline-flex items-center gap-1"
                    >
                        <Plus className="h-3.5 w-3.5" />
                        Add extra charge
                    </button>
                )}
            </div>
            <div className="card-body">
                {adjustments.length === 0 && !adding && (
                    <p className="text-sm text-ink-subtle italic">
                        {canAdd
                            ? 'No extras yet.'
                            : 'No extras. Available once the ride is completed.'}
                    </p>
                )}

                {adjustments.length > 0 && (
                    <ul className="divide-y divide-paper-border-soft -mt-1">
                        {adjustments.map((a) => (
                            <AdjustmentRow
                                key={a.id}
                                adjustment={a}
                                bookingId={booking.id}
                            />
                        ))}
                    </ul>
                )}

                {/* Settled rows total — only shown if there are any non-pending rows */}
                {settledRows.length > 0 && (
                    <div className="pt-3 mt-3 border-t border-paper-border-soft flex items-center justify-between text-sm">
                        <span className="text-ink-subtle">Charged total</span>
                        <span className="font-semibold text-ink">
                            {settledTotalPence >= 0 ? '+' : '−'}£{Math.abs(settledTotalPence / 100).toFixed(2)}
                        </span>
                    </div>
                )}

                {/* Pending total + action buttons row */}
                {pendingRows.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-paper-border-soft">
                        <div className="flex items-center justify-between text-sm mb-3">
                            <span className="text-ink-subtle">
                                Pending review
                                <span className="ml-2 text-xs text-amber-700">
                                    ({pendingRows.length} row{pendingRows.length !== 1 ? 's' : ''})
                                </span>
                            </span>
                            <span className="font-semibold text-ink">
                                +£{(pendingTotalPence / 100).toFixed(2)}
                            </span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Button
                                variant="secondary"
                                onClick={() => setShowNotify(true)}
                                className="flex-1"
                            >
                                <MailCheck className="h-4 w-4" />
                                Notify customer
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => setShowCharge(true)}
                                className="flex-1"
                            >
                                Charge £{(pendingTotalPence / 100).toFixed(2)}
                            </Button>
                        </div>
                    </div>
                )}

                {adding && (
                    <div className={adjustments.length > 0 ? 'mt-4 pt-4 border-t border-paper-border-soft' : ''}>
                        <AddExtraChargeForm
                            booking={booking}
                            onClose={() => setAdding(false)}
                        />
                    </div>
                )}
            </div>

            {showNotify && (
                <NotifyAdjustmentsModal
                    booking={booking}
                    pendingRows={pendingRows}
                    pendingTotalPence={pendingTotalPence}
                    onClose={() => setShowNotify(false)}
                />
            )}

            {showCharge && (
                <ChargeAdjustmentsModal
                    booking={booking}
                    pendingRows={pendingRows}
                    pendingTotalPence={pendingTotalPence}
                    needsNudge={needsNudge}
                    onClose={() => setShowCharge(false)}
                />
            )}
        </div>
    );
}


// -----------------------------------------------------------------------------
// AdjustmentRow — one line per adjustment in the list
// -----------------------------------------------------------------------------
// Pending rows: amount is muted, gets a "Pending review" pill + delete (×)
// button. Charged rows: render as before.
function AdjustmentRow({ adjustment: a, bookingId }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const isPendingReview = a.stripe_status === 'pending_review';
    const isFailed = a.stripe_status === 'failed';
    const isPending = a.stripe_status === 'pending';     // in-flight Stripe (rare)

    const gross = a.amount_pence / 100;
    const net = gross / 1.2;
    const vat = gross - net;

    const sign = a.kind === 'refund' ? '−' : '+';

    let descriptionMain;
    if (a.reason_category === 'extra_hours') {
        const hours = Number(a.hours);
        const rate = (a.hourly_rate_pence / 100).toFixed(2);
        descriptionMain = `${hours} hr${hours !== 1 ? 's' : ''} × £${rate} — Extra hours`;
    } else {
        descriptionMain = a.reason || 'Other charge';
    }

    const deleteMutation = useMutation({
        mutationFn: () => api.delete(`/bookings/${bookingId}/adjustments/${a.id}`),
        onSuccess: () => {
            toast.success('Removed');
            queryClient.invalidateQueries({ queryKey: ['booking', bookingId] });
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not remove';
            toast.error(msg);
        },
    });

    function handleDelete() {
        if (deleteMutation.isPending) return;
        if (!window.confirm('Remove this pending charge?')) return;
        deleteMutation.mutate();
    }

    return (
        <li className={`py-3 flex items-start justify-between gap-4 ${isFailed ? 'opacity-60' : ''}`}>
            <div className="min-w-0 flex-1">
                <div className="text-sm text-ink flex items-center gap-2 flex-wrap">
                    <span>{descriptionMain}</span>
                    {isPendingReview && (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold text-amber-800 bg-amber-100 rounded">
                            Pending review
                        </span>
                    )}
                </div>
                {a.reason_category === 'extra_hours' && a.reason && (
                    <div className="text-xs text-ink-subtle mt-0.5 italic">{a.reason}</div>
                )}
                <div className="text-xs text-ink-subtle mt-1 flex items-center gap-2 flex-wrap">
                    <span>{formatTimestamp(a.created_at)}</span>
                    {a.created_by_email && <><span>·</span><span>{a.created_by_email}</span></>}
                    {a.notified_at && (
                        <>
                            <span>·</span>
                            <span className="text-ink-subtle italic">notified {formatRelativeTime(a.notified_at)}</span>
                        </>
                    )}
                    {isFailed && (
                        <>
                            <span>·</span>
                            <span className="text-red-700 font-medium">Charge failed</span>
                            {a.stripe_error && (
                                <span className="text-red-700 italic">— {a.stripe_error}</span>
                            )}
                        </>
                    )}
                    {isPending && (
                        <>
                            <span>·</span>
                            <span className="text-amber-700 font-medium">Pending Stripe confirm</span>
                        </>
                    )}
                    {a.stripe_payment_intent_id && (
                        <>
                            <span>·</span>
                            <a
                                href={`https://dashboard.stripe.com/payments/${a.stripe_payment_intent_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-ink-info hover:underline font-mono"
                            >
                                Stripe ↗
                            </a>
                        </>
                    )}
                </div>
            </div>
            <div className="text-right flex-shrink-0 flex items-start gap-2">
                <div>
                    <div className={`text-sm font-semibold ${isFailed ? 'text-ink-subtle line-through' : (isPendingReview ? 'text-ink-subtle' : 'text-ink')}`}>
                        {sign}£{gross.toFixed(2)}
                    </div>
                    {!isFailed && (
                        <div className="text-xs text-ink-subtle whitespace-nowrap mt-0.5">
                            £{net.toFixed(2)} + £{vat.toFixed(2)} VAT
                        </div>
                    )}
                </div>
                {isPendingReview && (
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                        className="text-ink-subtle hover:text-red-700 mt-0.5"
                        title="Remove this pending charge"
                    >
                        <XCircle className="h-4 w-4" />
                    </button>
                )}
            </div>
        </li>
    );
}


// =============================================================================
// NotifyAdjustmentsModal (7d.2.5b)
// =============================================================================
//
// Recipient picker for the "Notify customer about pending extras" action.
// Mirrors SendDetailsModal: booker checkbox (defaults UNTICKED) + free-form
// "Other recipients" field. POST /:id/adjustments/notify on submit.
// =============================================================================
function NotifyAdjustmentsModal({ booking, pendingRows, pendingTotalPence, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const [includeBooker, setIncludeBooker] = useState(false);
    const [other, setOther] = useState('');
    const [otherError, setOtherError] = useState(null);

    function parseOther(input) {
        return input
            .split(/[\s,]+/)
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
    }

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    function validateOther(input) {
        const tokens = parseOther(input);
        const invalid = tokens.filter((t) => !EMAIL_RE.test(t));
        if (invalid.length > 0) {
            return `Invalid: ${invalid.join(', ')}`;
        }
        return null;
    }

    function handleOtherChange(e) {
        const v = e.target.value;
        setOther(v);
        if (v.trim()) {
            setOtherError(validateOther(v));
        } else {
            setOtherError(null);
        }
    }

    const otherTokens = parseOther(other);
    const recipients = [];
    if (includeBooker && booking.booker_email) recipients.push(booking.booker_email);
    for (const t of otherTokens) {
        if (EMAIL_RE.test(t)) recipients.push(t);
    }
    const dedupedRecipients = Array.from(new Set(recipients.map((r) => r.toLowerCase())));

    const canSend = dedupedRecipients.length > 0 && !otherError;

    const sendMutation = useMutation({
        mutationFn: () => api.post(`/bookings/${booking.id}/adjustments/notify`, { recipients }),
        onSuccess: async (data) => {
            toast.success(
                `Notification sent to ${data?.recipient_count || dedupedRecipients.length} recipient${
                    (data?.recipient_count || dedupedRecipients.length) !== 1 ? 's' : ''
                }`
            );
            // Wait for the refetch to actually finish before closing — otherwise
            // the parent BookingDetailPage renders against pre-notify data and
            // the soft-nudge in ChargeAdjustmentsModal misfires.
            await queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            await queryClient.refetchQueries({ queryKey: ['booking', booking.id] });
            onClose();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Failed to send';
            toast.error(`Couldn't send: ${msg}`);
        },
    });

  return (
        <Modal
            open
            onClose={onClose}
            title="Notify customer about extras"
            description={`Sends an itemised email listing ${pendingRows.length} extra${pendingRows.length !== 1 ? 's' : ''} totalling £${(pendingTotalPence / 100).toFixed(2)}. Recipients are BCC'd — they won't see each other.`}
            size="md"
        >
            <div className="space-y-4 mt-2">
                {booking.booker_email && (
                    <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={includeBooker}
                            onChange={(e) => setIncludeBooker(e.target.checked)}
                            className="mt-0.5"
                        />
                        <div>
                            <div className="text-sm font-medium text-ink">Booker</div>
                            <div className="text-xs text-ink-subtle">
                                {booking.booker_name} · {booking.booker_email}
                            </div>
                        </div>
                    </label>
                )}

                <div>
                    <label className="block text-sm font-medium text-ink mb-1.5">
                        Other recipients <span className="text-ink-subtle font-normal">(optional)</span>
                    </label>
                    <Textarea
                        value={other}
                        onChange={handleOtherChange}
                        placeholder="email@example.com, another@example.com"
                        rows={2}
                        className="text-sm"
                    />
                    <div className="text-xs text-ink-subtle mt-1">Comma- or space-separated.</div>
                    {otherError && (
                        <div className="text-xs text-red-700 mt-1">{otherError}</div>
                    )}
                </div>

                {dedupedRecipients.length > 0 && (
                    <div className="text-xs text-ink-subtle bg-canvas/40 rounded px-3 py-2">
                        Sending to <strong className="text-ink">{dedupedRecipients.length}</strong> recipient{dedupedRecipients.length !== 1 ? 's' : ''}.
                    </div>
                )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={sendMutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => sendMutation.mutate()}
                    disabled={!canSend}
                    loading={sendMutation.isPending}
                >
                    <MailCheck className="h-4 w-4" />
                    Send notification
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// NotifyChangesModal — prompts dispatcher after edits or chauffeur change
// =============================================================================
//
// Triggered after PATCH /:id (edits) or POST /:id/allocate (chauffeur swap).
// Shows the diff and lets the dispatcher tick whether to notify customer
// and/or driver of the changes. Each checkbox is visible only if the
// respective party was previously emailed/notified — same principle as the
// driver-only version but now symmetric.
//
// Customer checked → POST /send-details with update_mode + change_summary.
//   Backend stamps customer_details_emailed_at, fires booking details email
//   with amber "What's changed" banner at top.
//
// Driver checked → POST /notify-driver with update_mode + change_summary.
//   Backend resets ack, fires updated email template, writes status_history.
//
// Skip both → POST /audit-skip-renotify-customer and/or
//   /audit-skip-renotify (driver) for whichever was previously notified.
//   Pure audit; captures the dispatcher's choice not to notify.
//
// All combinations: tick none → both skips fire (audit only). Tick one →
// that one sends, the other skips. Tick both → both send. Either way the
// dispatcher's intent is captured for the timeline.
// =============================================================================

function NotifyChangesModal({
    booking,
    diff,
    customerNotification,
    driverNotification,
    previousChauffeurNotification,
    onClose,
}) {
    const queryClient = useQueryClient();
    const api = useApi();

    const customerAvailable = !!customerNotification?.booker_email;
    const driverAvailable = !!driverNotification?.chauffeur_email;
    const previousChauffeurAvailable = !!previousChauffeurNotification?.chauffeur_email;

    const [notifyCustomer, setNotifyCustomer] = useState(customerAvailable);
    const [notifyDriver, setNotifyDriver] = useState(driverAvailable);
const [notifyPreviousChauffeur, setNotifyPreviousChauffeur] = useState(previousChauffeurAvailable);
    const [alsoSms, setAlsoSms] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const changeSummary = useMemo(() => {
        const seen = new Set();
        const lines = [];

        const isEmpty = (field, value) => {
            if (value == null) return true;
            const formatted = formatDiffValue(field, value);
            return formatted === 'empty' || formatted === 'no stops';
        };

         // Hours is a derived field — auto-recalculated when pickup_time or
        // final_time changes. It's never shown to chauffeurs (by default
        // hidden via hours_disclosed_to_chauffeur) and surfacing it as a
        // change just because pickup time shifted is noise. The actual
        // timing change (pickup_time / final_time) is what dispatchers,
        // customers and chauffeurs care about.
        for (const { field, before, after } of diff) {
            if (field === 'hours') continue;
            const label = FIELD_LABELS[field] || field;
            if (seen.has(label)) continue;
            seen.add(label);

            const beforeIsEmpty = isEmpty(field, before);
            const afterIsEmpty = isEmpty(field, after);
            const beforeStr = formatDiffValue(field, before);
            const afterStr = formatDiffValue(field, after);

            let line;
            if (beforeIsEmpty && !afterIsEmpty) {
                line = `${label}: added ${afterStr}`;
            } else if (!beforeIsEmpty && afterIsEmpty) {
                line = `${label}: removed (was ${beforeStr})`;
            } else {
                line = `${label}: ${beforeStr} → ${afterStr}`;
            }
            lines.push(line);
        }
        return lines;
    }, [diff]);

    const hasChauffeurChange = diff.some((d) => d.field === 'chauffeur');
    const hasRideDetailChange = diff.some((d) => d.field !== 'chauffeur');
    const isChauffeurOnlyChange = hasChauffeurChange && !hasRideDetailChange;

    const chauffeurDiff = diff.find((d) => d.field === 'chauffeur');
    const isFirstAllocation = chauffeurDiff && (
        chauffeurDiff.before == null
        || chauffeurDiff.before === ''
        || chauffeurDiff.before === 'unallocated'
    );

    // Build the emails array for the preview modal.
    const emails = useMemo(() => {
        const list = [];

        if (customerAvailable && notifyCustomer) {
            let previewKind, endpoint, sendBody, label;
            if (isChauffeurOnlyChange && isFirstAllocation) {
                previewKind = 'customer_chauffeur_allocated';
                endpoint = `/bookings/${booking.id}/send-customer-chauffeur-email`;
                sendBody = (recipients) => ({ kind: 'allocated', recipients, send_sms: alsoSms });
                label = 'Customer';
            } else if (isChauffeurOnlyChange) {
                previewKind = 'customer_chauffeur_changed';
                endpoint = `/bookings/${booking.id}/notify-customer-chauffeur-changed`;
               sendBody = (recipients) => ({ recipient: recipients[0], send_sms: alsoSms });
                label = 'Customer';
            } else {
                previewKind = 'booking_details_customer';
                endpoint = `/bookings/${booking.id}/send-details`;
                sendBody = (recipients) => ({
                    recipients,
                    update_mode: true,
                    change_summary: changeSummary,
                    send_sms: alsoSms,
                });
                label = 'Customer';
            }
            list.push({
                id: 'customer',
                label,
                previewParams: {
                    kind: previewKind,
                    booking_id: booking.id,
                    update_mode: !isChauffeurOnlyChange,
                    change_summary: !isChauffeurOnlyChange ? changeSummary : undefined,
                },
                sendEndpoint: endpoint,
                sendBody,
            });
        }

        if (driverAvailable && notifyDriver) {
            const isNewChauffeur = !!driverNotification.new_chauffeur || isFirstAllocation;
            list.push({
                id: 'driver',
                label: isNewChauffeur ? 'New chauffeur' : 'Chauffeur',
                previewParams: {
                    kind: 'chauffeur_allocation',
                    booking_id: booking.id,
                    update_mode: !isNewChauffeur,
                    change_summary: !isNewChauffeur ? changeSummary : undefined,
                },
                sendEndpoint: `/bookings/${booking.id}/notify-driver`,
                sendBody: (recipients) => ({
                    recipients,
                    update_mode: !isNewChauffeur,
                    change_summary: !isNewChauffeur ? changeSummary : undefined,
                    send_sms: alsoSms,
                }),
            });
        }

  if (previousChauffeurAvailable && notifyPreviousChauffeur) {
    list.push({
        id: 'previous_chauffeur',
        label: 'Previous chauffeur',
        previewParams: {
            kind: 'chauffeur_reassigned',
            booking_id: booking.id,
            recipient_hint: previousChauffeurNotification.chauffeur_email,
        },
        sendEndpoint: `/bookings/${booking.id}/notify-previous-chauffeur`,
        sendBody: (recipients) => ({ recipient: recipients[0] }),
    });
}

        return list;
 }, [
        customerAvailable, notifyCustomer, driverAvailable, notifyDriver,
        previousChauffeurAvailable, notifyPreviousChauffeur, alsoSms,
        booking.id, changeSummary, isChauffeurOnlyChange, isFirstAllocation,
        driverNotification,
    ]);

    async function fireSkipAudits() {
        const tasks = [];
        if (customerAvailable && !notifyCustomer) {
            tasks.push(
                api.post(`/bookings/${booking.id}/audit-skip-renotify-customer`, {
                    change_summary: changeSummary,
                }).catch(() => {})
            );
        }
        if (driverAvailable && !notifyDriver) {
            tasks.push(
                api.post(`/bookings/${booking.id}/audit-skip-renotify`, {
                    change_summary: changeSummary,
                }).catch(() => {})
            );
        }
        await Promise.all(tasks);
    }

    async function handleStart() {
        fireSkipAudits();
        if (emails.length === 0) {
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            toast('Choices captured in audit');
            onClose();
            return;
        }
        setShowPreview(true);
    }

    if (showPreview) {
        return (
            <EmailPreviewModal
                emails={emails}
                onClose={() => setShowPreview(false)}
                onSent={() => {
                    queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
                    onClose();
                }}
            />
        );
    }

    if (!customerAvailable && !driverAvailable && !previousChauffeurAvailable) {
        return (
            <Modal open onClose={onClose} title="Notify of changes?" size="md">
                <p className="text-sm text-ink mt-2">
                    No one needs to be notified — neither the customer nor any chauffeur has been emailed yet.
                </p>
                <div className="mt-6 flex items-center justify-end pt-4 border-t border-paper-border-soft">
                    <Button variant="primary" onClick={onClose}>Close</Button>
                </div>
            </Modal>
        );
    }

    const anyChecked = notifyCustomer || notifyDriver || notifyPreviousChauffeur;

    return (
        <Modal
            open
            onClose={onClose}
            title="Notify of changes?"
            description="The booking has been updated. Choose who to notify — each email will preview before being sent."
            size="md"
        >
            <div className="space-y-4 mt-2">
                <div className="bg-canvas/40 rounded px-3 py-2.5">
                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium mb-2">
                        Changes
                    </div>
                    <ul className="text-sm text-ink space-y-1.5">
                        {changeSummary.map((line, i) => (
                            <li key={i} className="flex items-start gap-2">
                                <span className="text-ink-subtle flex-shrink-0">•</span>
                                <span className="break-words">{line}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="space-y-2">
                    {customerAvailable && (
                        <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={notifyCustomer}
                                onChange={(e) => setNotifyCustomer(e.target.checked)}
                                className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-ink">
                                    {isChauffeurOnlyChange
                                        ? (isFirstAllocation
                                            ? 'Send chauffeur details to customer'
                                            : 'Send new chauffeur details to customer')
                                        : 'Notify customer'}
                                </div>
                                <div className="text-xs text-ink-subtle mt-0.5">
                                    Sends to <span className="text-ink">{customerNotification.booker_email}</span>
                                </div>
                            </div>
                        </label>
                    )}

                    {driverAvailable && (
                        <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={notifyDriver}
                                onChange={(e) => setNotifyDriver(e.target.checked)}
                                className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-ink">
                                    {isFirstAllocation
                                        ? 'Notify chauffeur'
                                        : (driverNotification.new_chauffeur ? 'Notify new chauffeur' : 'Notify chauffeur')}
                                </div>
                                <div className="text-xs text-ink-subtle mt-0.5">
                                    Sends to <span className="text-ink">{driverNotification.chauffeur_email}</span>
                                </div>
                            </div>
                        </label>
                    )}

                    {previousChauffeurAvailable && (
                        <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={notifyPreviousChauffeur}
                                onChange={(e) => setNotifyPreviousChauffeur(e.target.checked)}
                                className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-ink">Notify previous chauffeur</div>
                                <div className="text-xs text-ink-subtle mt-0.5">
                                    Sends a reassignment notice to <span className="text-ink">{previousChauffeurNotification.chauffeur_email}</span>
                                </div>
                            </div>
                        </label>
                    )}
                </div>

                <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={alsoSms}
                        onChange={(e) => setAlsoSms(e.target.checked)}
                    />
                    <span className="text-sm text-ink">Also send SMS to the same recipients</span>
                </label>
                <div className="text-xs text-ink-subtle bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <strong className="text-amber-900">Note:</strong>{' '}
                    The choice is captured in the booking timeline either way.
                    Untick any channel to skip its email.
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>
                    Skip all
                </Button>
                <Button variant="primary" onClick={handleStart}>
                    <Send className="h-4 w-4" />
                    {anyChecked ? `Preview${emails.length > 1 ? ` (${emails.length})` : ''}` : 'Confirm skip'}
                </Button>
            </div>
        </Modal>
    );
}

// Field labels for the change summary. Matches what the dispatcher sees in
// the inline-edit UI so the summary reads naturally instead of using raw
// column names. Anything missing falls back to the column name.
const FIELD_LABELS = {
            booking_mode: 'Booking type',
            pickup_date: 'Pickup date',
            pickup_time: 'Pickup time',
            final_time: 'Final time',
            hours: 'Hours',
            pickup_address: 'Pickup address',
            dropoff_address: 'Dropoff address',
            stops: 'Stops',
            vehicle_class: 'Vehicle class',
            vehicle_class_label: 'Vehicle class',
            pax_name: 'Passenger name',
            pax_phone: 'Passenger phone',
            pax_count: 'Passenger count',
                        pax_comments: 'Ride comments',
            chauffeur_notes: 'Chauffeur notes',
            flight_number: 'Flight number',
            tail_number: 'Tail number',
            fbo_code: 'PPR',
            booker_name: 'Booker name',
            booker_phone: 'Booker phone',
            booker_id: 'Booker',
            passenger_id: 'Passenger',
            internal_notes: 'Internal notes',
            quoted_price: 'Price',
            override_reason: 'Override reason',
            licensing_authority: 'Licensing authority',
            customer_reference: 'Customer reference',
            hours_disclosed_to_chauffeur: 'Hours visible to chauffeur',
        };

// Format a single diff value for display in the notify modal. Branches on
// field name so dates render as "15 May 2026", times as "10:00", stops as
// count + first item, vehicle class as the human label, etc. Anything
// unrecognised falls through to the generic stringify with truncation.
function formatDiffValue(field, value) {
    if (value == null || value === '') return 'empty';

    if (field === 'pickup_date') {
        try {
            const dateStr = String(value).slice(0, 10);
            const d = new Date(`${dateStr}T00:00:00`);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short', year: 'numeric',
                });
            }
        } catch { /* fall through */ }
    }

    if (field === 'pickup_time' || field === 'final_time') {
        return String(value).slice(0, 5);
    }

    if (field === 'stops') {
        try {
            const arr = typeof value === 'string' ? JSON.parse(value) : value;
            if (Array.isArray(arr)) {
                if (arr.length === 0) return 'no stops';
                // Build a human-readable summary: "Harrods (10:30), V&A Museum"
                const parts = arr.map((s) => {
                    if (typeof s === 'string') return s;
                    const addr = s?.address || '';
                    const time = s?.time ? String(s.time).slice(0, 5) : '';
                    return time ? `${addr} (${time})` : addr;
                }).filter(Boolean);
                if (parts.length === 0) return 'no stops';
                if (parts.length === 1) return parts[0];
                // Truncate long addresses for readability
                const truncate = (str, n) => str.length > n ? str.slice(0, n) + '…' : str;
                const truncated = parts.map(p => truncate(p, 40));
                return truncated.join(', ');
            }
        } catch { /* fall through */ }
    }

   if (field === 'vehicle_class') {
        return VEHICLE_CLASS_LABELS[value] || String(value);
    }

    if (field === 'booking_mode') {
        return value === 'hourly' ? 'Hourly hire' : 'One-way';
    }

    if (typeof value === 'number') return String(value);

    const s = typeof value === 'string' ? value : JSON.stringify(value);
    return s.length > 60 ? s.slice(0, 60) + '\u2026' : s;
}

// =============================================================================
// ChargeAdjustmentsModal (7d.2.5b)
// =============================================================================
//
// Confirmation modal for charging the pending adjustments. Shows the total +
// card details. If the customer hasn't been notified about the latest state
// of the pending list (`needsNudge`), shows a soft warning — doesn't block,
// just makes the dispatcher consciously confirm.
// =============================================================================
function ChargeAdjustmentsModal({ booking, pendingRows, pendingTotalPence, needsNudge, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const totalStr = `£${(pendingTotalPence / 100).toFixed(2)}`;

    const isStripeMethod = booking.payment_method === 'saved_card'
        || booking.payment_method === 'payment_link';
    const cardLast4 = booking.default_payment_method_last4;

    const chargeMutation = useMutation({
        mutationFn: () => api.post(`/bookings/${booking.id}/adjustments/charge`, {}),
        onSuccess: (data) => {
            const stripeStatus = data?.stripe_status;
            if (stripeStatus === 'succeeded') {
                toast.success(`Charged ${totalStr}`);
            } else if (stripeStatus === 'pending') {
                toast.success(`${totalStr} charge sent — awaiting Stripe confirmation`);
            } else {
                // account/external — stripe_status is 'na'
                toast.success(`${totalStr} recorded for invoice`);
            }
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            onClose();
        },
        onError: (err) => {
            const status = err?.response?.status;
            const msg = err?.response?.data?.message
                || err?.response?.data?.error
                || err?.message
                || 'Could not charge';
            if (status === 402) {
                toast.error(`Card declined: ${msg}`);
                queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
                onClose();
            } else {
                toast.error(msg);
            }
        },
    });

    let methodLine;
    if (isStripeMethod) {
        methodLine = cardLast4
            ? `Card on file (•••• ${cardLast4})`
            : 'Card on file';
    } else if (booking.payment_method === 'account') {
        methodLine = 'Account billing — will be added to next invoice';
    } else if (booking.payment_method === 'external') {
        methodLine = 'External — recorded as line item only';
    } else {
        methodLine = 'Unknown payment method';
    }

    return (
        <Modal
            open
            onClose={onClose}
            title={isStripeMethod ? `Charge ${totalStr}?` : `Record ${totalStr}?`}
            description={
                isStripeMethod
                    ? `${pendingRows.length} extra${pendingRows.length !== 1 ? 's' : ''} totalling ${totalStr} will be charged in one transaction.`
                    : `${pendingRows.length} extra${pendingRows.length !== 1 ? 's' : ''} totalling ${totalStr} will be recorded.`
            }
            size="md"
        >
            <div className="space-y-3 mt-2">
                <div className="bg-canvas/40 rounded px-3 py-2 text-sm">
                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium mb-1">Payment method</div>
                    <div className="text-ink">{methodLine}</div>
                </div>

                {needsNudge && isStripeMethod && (
                    <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm text-amber-900 flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="font-medium">Customer hasn't been notified by email</div>
                            <div className="text-xs mt-0.5">
                                If they were told in person or by phone, that's fine. Otherwise consider clicking Notify first.
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={chargeMutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => chargeMutation.mutate()}
                    loading={chargeMutation.isPending}
                >
                    {isStripeMethod ? `Charge ${totalStr}` : `Record ${totalStr}`}
                </Button>
            </div>
        </Modal>
    );
}


// -----------------------------------------------------------------------------
// AddExtraChargeForm — inline form revealed when "Add extra charge" is clicked
// -----------------------------------------------------------------------------
// Two categories chosen by dropdown:
//
//   extra_hours: shows hours field, hourly rate field (prefilled from the
//                booking's original price_breakdown), and an auto-calculated
//                amount that's also editable. Reason field is optional.
//
//   other:       shows description (reason) field and amount field. Both
//                required. No structured breakdown.
//
// Below the amount, a derived "(£X.XX + £Y.YY VAT)" caption shows the split
// for transparency. Always 20% VAT, no flag.
//
// On submit:
//   - validates (amount > 0, reason required for 'other', hours/rate required
//     for 'extra_hours')
//   - POSTs to /bookings/:id/adjustments
//   - on 201: toast + invalidate + close form
//   - on 402 (card declined): toast with reason + invalidate (the row was
//     created with stripe_status='failed' — list refreshes to show it) + close
//   - on other errors: toast with message, leave form open for retry
//
// All amounts are entered in £, converted to pence for the API.
function AddExtraChargeForm({ booking, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();

    // ---- Resolve prefill rate for extra_hours --------------------------
    // Priority:
    //   1. booking.price_breakdown.base.rate_per_hour (dispatch pricing service)
    //   2. booking.price_breakdown.hourly_rate / rate_per_hour (engine meta shapes)
    //   3. blank (dispatcher types it)
    //
    // The price_breakdown shape varies because engine and dispatch built
    // separate calculators (per the "WP keeps own pricing for now" decision).
    // We probe both shapes defensively. If neither has a rate, blank is fine —
    // dispatcher just types it.
   const prefilledRate = useMemo(() => {
        const bd = booking.price_breakdown;
        if (!bd || typeof bd !== 'object') return '';
        // The price_breakdown stores rates NET (engine convention — VAT is added
        // to the displayed customer price). The Adjustments form labels the rate
        // field "inc VAT" so we convert here. 1.2 = 20% VAT (UK).
        // Dispatch pricingService shape (mig 016+): bd.base.rate_per_hour
        const dispatchRate = bd.base?.rate_per_hour;
        if (Number.isFinite(Number(dispatchRate)) && Number(dispatchRate) > 0) {
            return (Number(dispatchRate) * 1.2).toFixed(2);
        }
        // Engine meta shape: rate field name varies. Try a few common keys.
        for (const k of ['hourly_rate', 'rate_per_hour', 'hourlyRate']) {
            if (Number.isFinite(Number(bd[k])) && Number(bd[k]) > 0) {
                return (Number(bd[k]) * 1.2).toFixed(2);
            }
        }
        return '';
    }, [booking.price_breakdown]);

    const [category, setCategory] = useState('extra_hours');
    const [hours, setHours] = useState('');
    const [rate, setRate] = useState(prefilledRate);
    const [amount, setAmount] = useState('');           // £ string
    const [reason, setReason] = useState('');
    const [amountManuallyEdited, setAmountManuallyEdited] = useState(false);

    // For extra_hours: when hours or rate changes AND amount hasn't been
    // manually overridden, auto-update amount = hours × rate. If dispatcher
    // touches the amount field, we stop auto-calc and respect their value.
    useEffect(() => {
        if (category !== 'extra_hours') return;
        if (amountManuallyEdited) return;
        const h = parseFloat(hours);
        const r = parseFloat(rate);
        if (Number.isFinite(h) && Number.isFinite(r) && h > 0 && r > 0) {
            setAmount((h * r).toFixed(2));
        } else {
            setAmount('');
        }
    }, [category, hours, rate, amountManuallyEdited]);

    // Switching category resets things so we don't leak extra_hours data
    // into an 'other' submission or vice versa.
    function selectCategory(next) {
        setCategory(next);
        setHours('');
        setRate(next === 'extra_hours' ? prefilledRate : '');
        setAmount('');
        setReason('');
        setAmountManuallyEdited(false);
    }

    const amountPounds = parseFloat(amount) || 0;
    const net = amountPounds / 1.2;
    const vat = amountPounds - net;

    // Numeric input filter — accepts only digits + single decimal + 2dp.
    function handleNumeric(setter) {
        return (e) => {
            const v = e.target.value;
            if (v === '') { setter(''); return; }
            if (/^\d*\.?\d{0,2}$/.test(v)) setter(v);
        };
    }

    const submitMutation = useMutation({
        mutationFn: (payload) => api.post(`/bookings/${booking.id}/adjustments`, payload),
        onSuccess: () => {
            toast.success('Extra charge added');
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            onClose();
        },
        onError: (err) => {
            // Backend returns 402 when Stripe declined but the row WAS
            // persisted. Surface the message as an error toast, but still
            // refresh + close — the failed row will be visible in the list.
            const status = err?.response?.status;
            const msg = err?.response?.data?.message
                || err?.response?.data?.error
                || err?.message
                || 'Could not add charge';
            if (status === 402) {
                toast.error(`Card declined: ${msg}`);
                queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
                onClose();
            } else {
                toast.error(msg);
                // Leave form open — likely a validation issue the dispatcher
                // can fix and resubmit.
            }
        },
    });

    function handleSubmit() {
        // Client-side validation. Backend zod re-validates everything.
        if (amountPounds <= 0) {
            toast.error('Amount must be greater than zero');
            return;
        }
        if (category === 'other' && !reason.trim()) {
            toast.error('Description required');
            return;
        }
        if (category === 'extra_hours') {
            if (!parseFloat(hours)) {
                toast.error('Hours required');
                return;
            }
            if (!parseFloat(rate)) {
                toast.error('Hourly rate required');
                return;
            }
        }

        const amountPence = Math.round(amountPounds * 100);
        const body = {
            reason_category: category,
            amount_pence: amountPence,
            reason: reason.trim() || undefined,
        };
        if (category === 'extra_hours') {
            body.hours = parseFloat(hours);
            body.hourly_rate_pence = Math.round(parseFloat(rate) * 100);
        }
        submitMutation.mutate(body);
    }

    const saving = submitMutation.isPending;

    return (
        <div className="space-y-4">
            <div className="text-xs uppercase tracking-wide text-ink-subtle font-semibold">
                Add extra charge
            </div>

            {/* Category dropdown */}
            <div>
                <label className="block text-xs uppercase tracking-wide text-ink-subtle font-medium mb-1.5">
                    Category
                </label>
                <Select
                    value={category}
                    onChange={(e) => selectCategory(e.target.value)}
                    disabled={saving}
                >
                    <option value="extra_hours">Extra hours</option>
                    <option value="other">Other</option>
                </Select>
            </div>

            {/* Extra-hours fields */}
            {category === 'extra_hours' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs uppercase tracking-wide text-ink-subtle font-medium mb-1.5">
                            Hours
                        </label>
                        <Input
                            type="text"
                            inputMode="decimal"
                            value={hours}
                            onChange={handleNumeric(setHours)}
                            placeholder="e.g. 2"
                            disabled={saving}
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wide text-ink-subtle font-medium mb-1.5">
                            Rate per hour (£, inc VAT)
                            {prefilledRate && rate === prefilledRate && (
                                <span className="ml-2 text-[10px] normal-case tracking-normal text-ink-subtle/70">from original quote</span>
                            )}
                        </label>
                        <Input
                            type="text"
                            inputMode="decimal"
                            value={rate}
                            onChange={handleNumeric(setRate)}
                            placeholder="e.g. 100.00"
                            disabled={saving}
                        />
                    </div>
                </div>
            )}

            {/* Reason field — required for 'other', optional for extra_hours */}
            <div>
                <label className="block text-xs uppercase tracking-wide text-ink-subtle font-medium mb-1.5">
                    {category === 'other'
                        ? 'Description'
                        : 'Note (optional)'}
                    {category === 'other' && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={
                        category === 'other'
                            ? 'e.g. Cleaning charge — muddy boots, parking ticket pass-through'
                            : 'e.g. airport delayed 90 min, extension agreed by phone'
                    }
                    disabled={saving}
                    maxLength={500}
                />
            </div>

            {/* Amount field — auto-calculated for extra_hours, manual for other */}
            <div>
                <label className="block text-xs uppercase tracking-wide text-ink-subtle font-medium mb-1.5">
                    Amount (£, inc VAT)
                    {category === 'extra_hours' && amountManuallyEdited && (
                        <button
                            type="button"
                            onClick={() => setAmountManuallyEdited(false)}
                            className="ml-2 text-[10px] normal-case tracking-normal text-ink-info hover:underline"
                        >
                            reset to auto
                        </button>
                    )}
                </label>
                <Input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => {
                        handleNumeric(setAmount)(e);
                        if (category === 'extra_hours') setAmountManuallyEdited(true);
                    }}
                    placeholder="0.00"
                    disabled={saving}
                />
                {amountPounds > 0 && (
                    <div className="text-xs text-ink-subtle mt-1">
                        £{amountPounds.toFixed(2)} = £{net.toFixed(2)} + £{vat.toFixed(2)} VAT (20%)
                    </div>
                )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} loading={saving}>
                    Add extra charge
                </Button>
            </div>
        </div>
    );
}

// =============================================================================
// CancelBookingModal
// =============================================================================
//
// Shared modal for three flows:
//   - mode='cancel'        — active booking → cancelled (with optional refund)
//   - mode='no_show'       — arrived booking → no_show (with optional refund)
//   - mode='issue_refund'  — already-terminal booking → record refund decision
//
// Layout:
//   - Reason field (required)
//   - Time-until-pickup context line (e.g. "Pickup is in 4 hours")
//   - For Stripe-paid + charged_amount > 0: editable refund amount field
//     (default = full charged_amount; £0 = explicit no-refund decision)
//   - Standard policy nudge (24h+ refund, <24h no — manual judgement)
//   - Confirm button (red for cancel/no_show; primary for issue_refund)
//
// Submission:
//   - cancel/no_show → POST /:id/cancel
//   - issue_refund → POST /:id/issue-refund
//
// Both invalidate the booking query on success and close the modal.
// =============================================================================
function CancelBookingModal({ booking, mode, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const [reason, setReason] = useState('');
    const [refundDecisionMade, setRefundDecisionMade] = useState(false);
    const [refundAmountStr, setRefundAmountStr] = useState('');

    // Booking economics
    const chargedAmount = booking.charged_amount != null
        ? Number(booking.charged_amount)
        : null;
    const isStripeMethod = booking.payment_method === 'saved_card'
        || booking.payment_method === 'payment_link';
    const isAccountOrExternal = booking.payment_method === 'account'
        || booking.payment_method === 'external';

    // Whether we show the refund section at all.
    //   - For Stripe-paid: only if the original was captured (charged_amount > 0)
    //     AND the auth wasn't voided (capture_status='captured' or null/legacy).
    //     If it's a held-but-uncaptured auth, no refund is needed — that gets
    //     voided automatically on cancel.
    //   - For account/external: always show (refund recorded as ledger entry)
    //   - For pending_payment Stripe bookings: never (no money taken yet)
    const showRefundSection = useMemo(() => {
        if (booking.status === 'pending_payment') return false;
        if (isStripeMethod) {
            return chargedAmount != null && chargedAmount > 0
                && booking.stripe_capture_status !== 'voided'
                && booking.stripe_capture_status !== 'uncaptured';
        }
        if (isAccountOrExternal) return true;
        return false;
    }, [booking.status, booking.stripe_capture_status, isStripeMethod, isAccountOrExternal, chargedAmount]);

    // Default refund = full charged amount when a refund is checked. For
    // account/external where charged_amount is NULL, we default to the
    // quoted_price as the agreed value.
    const defaultRefundAmount = useMemo(() => {
        if (chargedAmount != null && chargedAmount > 0) return chargedAmount.toFixed(2);
        if (booking.quoted_price != null) return Number(booking.quoted_price).toFixed(2);
        return '0.00';
    }, [chargedAmount, booking.quoted_price]);

    // When dispatcher checks "Issue refund", populate with default amount.
    function handleRefundToggle(checked) {
        setRefundDecisionMade(checked);
        if (checked && refundAmountStr === '') {
            setRefundAmountStr(defaultRefundAmount);
        }
    }

    // Numeric input filter (£ with 2dp)
    function handleAmountChange(e) {
        const v = e.target.value;
        if (v === '') { setRefundAmountStr(''); return; }
        if (/^\d*\.?\d{0,2}$/.test(v)) setRefundAmountStr(v);
    }

    // Time-until-pickup context line. Useful for the dispatcher's judgement
    // (their normal policy is 24h+ before pickup = full refund).
    const pickupContext = useMemo(() => {
        if (!booking.pickup_date || !booking.pickup_time) return null;
        const dateStr = String(booking.pickup_date).slice(0, 10);
        const pickupAt = new Date(`${dateStr}T${booking.pickup_time}`);
        if (isNaN(pickupAt.getTime())) return null;

        const diffMs = pickupAt.getTime() - Date.now();
        const diffHours = diffMs / (1000 * 60 * 60);

        if (diffHours < -24) {
            const days = Math.floor(Math.abs(diffHours) / 24);
            return `Pickup was ${days} day${days === 1 ? '' : 's'} ago.`;
        }
        if (diffHours < 0) {
            const hoursAgo = Math.floor(Math.abs(diffHours));
            return `Pickup was ${hoursAgo} hour${hoursAgo === 1 ? '' : 's'} ago.`;
        }
        if (diffHours < 1) {
            const minutes = Math.floor(diffHours * 60);
            return `Pickup is in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
        }
        if (diffHours < 24) {
            const hours = Math.floor(diffHours);
            return `Pickup is in ${hours} hour${hours === 1 ? '' : 's'}.`;
        }
        const days = Math.floor(diffHours / 24);
        return `Pickup is in ${days} day${days === 1 ? '' : 's'}.`;
    }, [booking.pickup_date, booking.pickup_time]);

    const refundPounds = parseFloat(refundAmountStr) || 0;
    const refundPence = Math.round(refundPounds * 100);
    const refundOverflow = chargedAmount != null && refundPounds > chargedAmount;

    const cancelMutation = useMutation({
        mutationFn: () => {
            // For 'issue_refund' mode, the booking is already cancelled —
            // we only need to issue the refund.
            if (mode === 'issue_refund') {
                return api.post(`/bookings/${booking.id}/issue-refund`, {
                    refund_amount_pence: refundDecisionMade ? refundPence : 0,
                    reason: reason.trim(),
                });
            }
            // For cancel/no_show modes, we transition status AND optionally
            // include refund decision in one call.
            const body = {
                target_status: mode === 'no_show' ? 'no_show' : 'cancelled',
                reason: reason.trim(),
            };
            if (refundDecisionMade) {
                body.refund_amount_pence = refundPence;
            }
            return api.post(`/bookings/${booking.id}/cancel`, body);
        },
        onSuccess: async (data) => {
            const refundOutcome = data?.refund_outcome;

            // Decide on the success message based on what happened.
            if (mode === 'issue_refund') {
                if (refundDecisionMade && refundPence > 0) {
                    if (refundOutcome?.error) {
                        toast.error(`Recorded refund — Stripe failed: ${refundOutcome.error}`);
                    } else {
                        toast.success(`Refunded £${refundPounds.toFixed(2)}`);
                    }
                } else {
                    toast.success('No-refund decision recorded');
                }
            } else {
                const statusLabel = mode === 'no_show' ? 'No-show' : 'Cancelled';
                if (refundDecisionMade && refundPence > 0) {
                    if (refundOutcome?.error) {
                        toast.error(`${statusLabel} — refund failed: ${refundOutcome.error}`);
                    } else {
                        toast.success(`${statusLabel} — refunded £${refundPounds.toFixed(2)}`);
                    }
                } else {
                    toast.success(statusLabel);
                }
            }

            await queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            await queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] });
            onClose();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not complete';
            toast.error(msg);
        },
    });

    const canSubmit = reason.trim().length > 0
        && !refundOverflow
        && !cancelMutation.isPending;

    // Modal copy
    const titleByMode = {
        cancel:        'Cancel booking',
        no_show:       'Mark as no-show',
        issue_refund:  'Issue refund',
    };
    const descriptionByMode = {
        cancel:        `This will cancel ${booking.ride_ref} and is not undoable.`,
        no_show:       `This will mark ${booking.ride_ref} as no-show and is not undoable.`,
        issue_refund:  `Record a refund decision for ${booking.ride_ref}. This is one-shot — once recorded, no further refund can be issued from dispatch.`,
    };
    const confirmLabelByMode = {
        cancel:        refundDecisionMade && refundPence > 0
                            ? `Cancel & refund £${refundPounds.toFixed(2)}`
                            : 'Cancel booking',
        no_show:       refundDecisionMade && refundPence > 0
                            ? `Mark no-show & refund £${refundPounds.toFixed(2)}`
                            : 'Mark as no-show',
        issue_refund:  refundDecisionMade && refundPence > 0
                            ? `Refund £${refundPounds.toFixed(2)}`
                            : 'Record no-refund decision',
    };
    const confirmVariantByMode = {
        cancel: 'danger',
        no_show: 'danger',
        issue_refund: 'primary',
    };

    return (
        <Modal
            open
            onClose={cancelMutation.isPending ? undefined : onClose}
            title={titleByMode[mode]}
            description={descriptionByMode[mode]}
            size="md"
        >
            <div className="space-y-4 mt-2">
                {/* Reason */}
                <div>
                    <label className="block text-sm font-medium text-ink mb-1.5">
                        Reason <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={
                            mode === 'no_show'
                                ? 'e.g. Passenger didn\'t show, chauffeur waited 30 min, contacted booker'
                                : mode === 'issue_refund'
                                ? 'e.g. Goodwill refund agreed by phone, weather disruption refund'
                                : 'e.g. Customer cancelled by phone, weather grounded flight'
                        }
                        rows={3}
                        disabled={cancelMutation.isPending}
                        autoFocus
                    />
                </div>

                {/* Pickup context — useful for refund judgement */}
                {pickupContext && (
                    <div className="text-xs text-ink-subtle bg-canvas/40 rounded px-3 py-2 flex items-start gap-2">
                        <Clock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <span>{pickupContext}</span>
                    </div>
                )}

                {/* Refund section */}
                {showRefundSection && (
                    <div className="border-t border-paper-border-soft pt-4">
                        <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={refundDecisionMade}
                                onChange={(e) => handleRefundToggle(e.target.checked)}
                                className="mt-0.5"
                                disabled={cancelMutation.isPending}
                            />
                            <div>
                                <div className="text-sm font-medium text-ink">
                                    {mode === 'issue_refund'
                                        ? 'Issue refund'
                                        : 'Issue refund as part of cancellation'}
                                </div>
                                <div className="text-xs text-ink-subtle mt-0.5">
                                    {isStripeMethod
                                        ? `Refund will be processed against the original Stripe payment${chargedAmount != null ? ` (£${chargedAmount.toFixed(2)} charged)` : ''}.`
                                        : 'Refund will be recorded as a ledger entry for future invoicing.'}
                                </div>
                            </div>
                        </label>

                        {refundDecisionMade && (
                            <div className="mt-3 ml-6 space-y-2">
                                <label className="block text-xs uppercase tracking-wide text-ink-subtle font-medium">
                                    Refund amount (£)
                                </label>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    value={refundAmountStr}
                                    onChange={handleAmountChange}
                                    disabled={cancelMutation.isPending}
                                    className="max-w-[160px]"
                                />
                                {chargedAmount != null && (
                                    <div className="text-xs text-ink-subtle">
                                        Charged: £{chargedAmount.toFixed(2)}
                                        {refundPounds > 0 && refundPounds < chargedAmount && (
                                            <span> · Customer keeps £{(chargedAmount - refundPounds).toFixed(2)}</span>
                                        )}
                                    </div>
                                )}
                                {refundOverflow && (
                                    <div className="text-xs text-red-700">
                                        Refund cannot exceed the charged amount.
                                    </div>
                                )}
                                {refundPounds === 0 && (
                                    <div className="text-xs text-ink-subtle italic">
                                        £0 = explicit no-refund decision (locks the booking from further refund).
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="text-xs text-ink-subtle mt-3 italic">
                            Standard policy: full refund 24h+ before pickup, no refund within 24h. Use your judgement.
                        </div>
                    </div>
                )}

                {/* Held-auth bookings: explain that no refund is needed */}
                {!showRefundSection && booking.stripe_capture_status === 'uncaptured' && (mode === 'cancel' || mode === 'no_show') && (
                    <div className="text-xs text-ink-subtle bg-canvas/40 rounded px-3 py-2">
                        No refund needed — the held authorization will be voided automatically.
                    </div>
                )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={cancelMutation.isPending}
                >
                    Back
                </Button>
                <Button
                    variant={confirmVariantByMode[mode]}
                    onClick={() => cancelMutation.mutate()}
                    disabled={!canSubmit}
                    loading={cancelMutation.isPending}
                >
                    {confirmLabelByMode[mode]}
                </Button>
            </div>
        </Modal>
    );
}
// =============================================================================
// DeleteBookingModal — owner-only hard delete with type-to-confirm
// =============================================================================
function DeleteBookingModal({ booking, siblings, isMultiRide, onClose }) {
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [confirmText, setConfirmText] = useState('');

    const charged = booking.charged_amount != null ? Number(booking.charged_amount) : null;
    const refs = (isMultiRide && siblings.length > 0)
        ? siblings.map((s) => s.ride_ref)
        : [booking.ride_ref];
    const count = refs.length;

    const deleteMutation = useMutation({
        mutationFn: () => api.delete(`/bookings/${booking.id}`),
        onSuccess: (data) => {
            toast.success(
                (data?.deleted_count || 1) > 1
                    ? `Deleted ${data.deleted_count} bookings`
                    : 'Booking deleted'
            );
            queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] });
            if (window.history.length > 1) {
                navigate(-1);
            } else {
                navigate('/bookings');
            }
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not delete';
            toast.error(msg);
        },
    });

    const canDelete = confirmText.trim().toUpperCase() === 'DELETE' && !deleteMutation.isPending;

    return (
        <Modal
            open
            onClose={deleteMutation.isPending ? undefined : onClose}
            title="Delete booking permanently?"
            description={
                (isMultiRide && count > 1)
                    ? `This is part of a ${count}-ride basket. All ${count} rides will be permanently deleted.`
                    : `This permanently deletes ${booking.ride_ref}.`
            }
            size="md"
        >
            <div className="space-y-4 mt-2">
                <div className="bg-red-50 border border-red-200 rounded px-3 py-2.5 text-sm text-red-800 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                        <div className="font-medium">This cannot be undone.</div>
                        <div className="text-xs mt-1">
                            Deletes the booking{count > 1 ? 's' : ''}, status history, adjustments,
                            billing lines, and operating costs.
                        </div>
                    </div>
                </div>

                {count > 1 && (
                    <div className="text-xs text-ink-subtle">
                        Rides to be deleted:{' '}
                        <span className="text-ink font-mono">{refs.join(', ')}</span>
                    </div>
                )}

                {charged != null && charged > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm text-amber-900 flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="font-medium">Captured payment of £{charged.toFixed(2)}</div>
                            <div className="text-xs mt-0.5">
                                Deleting does NOT refund the customer. The Stripe payment stays in Stripe; only the dispatch link is lost.
                            </div>
                        </div>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-ink mb-1.5">
                        Type <span className="font-mono">DELETE</span> to confirm
                    </label>
                    <Input
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="DELETE"
                        disabled={deleteMutation.isPending}
                        autoFocus
                    />
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={deleteMutation.isPending}>
                    Cancel
                </Button>
                <Button
                    variant="danger"
                    onClick={() => deleteMutation.mutate()}
                    disabled={!canDelete}
                    loading={deleteMutation.isPending}
                >
                    Delete {count > 1 ? `${count} bookings` : 'booking'}
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// Section: Allocation
// =============================================================================
function AllocationCard({ booking, canAllocate, onOpenAllocate }) {
    const hasChauffeur = Boolean(booking.allocated_chauffeur_id);
    const hasVehicle   = Boolean(booking.allocated_vehicle_id);
    const [showUnallocate, setShowUnallocate] = useState(false);
    const [notifyPrevious, setNotifyPrevious] = useState(null); // { email } after unallocate
    const canUnallocate = (hasChauffeur || hasVehicle)
        && ['allocated', 'dispatched'].includes(booking.status);

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Allocation</h2>
            </div>
            <div className="card-body space-y-4">
                {!hasChauffeur && !hasVehicle ? (
                    <div className="text-sm text-ink-subtle italic">Not allocated yet.</div>
                ) : (
                    <>
                        {hasChauffeur && (
                            <div className="flex items-start gap-3">
                                <UserIcon className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">Chauffeur</div>
                                    <Link
                                        to={`/chauffeurs/${booking.allocated_chauffeur_id}`}
                                        className="text-sm text-ink hover:underline"
                                    >
                                        {booking.chauffeur_first_name} {booking.chauffeur_last_name}
                                        {booking.chauffeur_call_sign && (
                                            <span className="ml-2 text-xs font-mono text-ink-subtle">{booking.chauffeur_call_sign}</span>
                                        )}
                                    </Link>
                                    {booking.chauffeur_phone && (
                                        <div className="text-xs text-ink-subtle mt-0.5">{booking.chauffeur_phone}</div>
                                    )}
                                </div>
                            </div>
                        )}
                        {hasVehicle && (
                            <div className="flex items-start gap-3">
                                <Car className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">Vehicle</div>
                                    <Link
                                        to={`/vehicles/${booking.allocated_vehicle_id}`}
                                        className="text-sm text-ink hover:underline"
                                    >
                                        {booking.vehicle_nickname || `${booking.vehicle_make} ${booking.vehicle_model}`}
                                    </Link>
                                    {booking.vehicle_registration && (
                                        <div className="text-xs text-ink-subtle mt-0.5">{booking.vehicle_registration}</div>
                                    )}
                                </div>
                            </div>
                        )}
                        {booking.allocated_at && (
                            <div className="pt-3 border-t border-paper-border-soft text-xs text-ink-subtle">
                                Allocated {formatTimestamp(booking.allocated_at)}
                                {booking.allocated_by_email && <> by {booking.allocated_by_email}</>}
                                {booking.allocation_override_reason && (
                                    <div className="mt-1 italic">Override reason: {booking.allocation_override_reason}</div>
                                )}
                            </div>
                        )}

                                                {/* Ack status — only meaningful once notified */}
                        {booking.driver_notified_at && (
                            <DriverAckStatus booking={booking} />
                        )}

                        <TrackingLinkRow booking={booking} />

                    </>
                )}

                <Button
                    variant="secondary"
                    className="w-full"
                    onClick={onOpenAllocate}
                    disabled={!canAllocate}
                    title={canAllocate
                        ? undefined
                        : 'Cannot allocate on terminal bookings (cancelled / completed / no-show)'
                    }
                >
                                        {hasChauffeur ? 'Change allocation' : 'Allocate chauffeur & vehicle'}
                </Button>
              
                <JobOfferSection booking={booking} />

                {canUnallocate && (
                    <button
                        type="button"
                        onClick={() => setShowUnallocate(true)}
                        className="w-full text-xs text-ink-subtle hover:text-red-700 underline decoration-dotted"
                    >
                        Unallocate
                    </button>
                )}

                {showUnallocate && (
                    <UnallocateModal
                        booking={booking}
                        onClose={() => setShowUnallocate(false)}
                        onDone={(prev) => {
                            setShowUnallocate(false);
                            if (prev) setNotifyPrevious(prev);
                        }}
                    />
                )}

                {notifyPrevious && (
                    <EmailPreviewModal
                        emails={[{
                            id: 'previous_chauffeur',
                            label: 'Previous chauffeur',
                            previewParams: {
                                kind: 'chauffeur_reassigned',
                                booking_id: booking.id,
                                recipient_hint: notifyPrevious.email,
                            },
                            sendEndpoint: `/bookings/${booking.id}/notify-previous-chauffeur`,
                            sendBody: (recipients) => ({ recipient: recipients[0] }),
                        }]}
                        onClose={() => setNotifyPrevious(null)}
                        onSent={() => setNotifyPrevious(null)}
                    />
                )}
            </div>
        </div>
    );
}

// =============================================================================
// UnallocateModal — remove chauffeur + vehicle, booking back to Confirmed
// =============================================================================
function UnallocateModal({ booking, onClose, onDone }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [reason, setReason] = useState('');
    const wasNotified = !!booking.driver_notified_at && !!booking.chauffeur_email;
    const [notify, setNotify] = useState(wasNotified);
    const name = [booking.chauffeur_first_name, booking.chauffeur_last_name].filter(Boolean).join(' ') || 'the chauffeur';

    const mutation = useMutation({
        mutationFn: () => api.post(`/bookings/${booking.id}/unallocate`, { reason: reason.trim() }),
        onSuccess: (data) => {
            toast.success('Unallocated \u2014 booking back to Confirmed');
            if (data?.removed_offer_lines > 0) {
                toast(`Removed ${data.removed_offer_lines} job-offer pay line${data.removed_offer_lines === 1 ? '' : 's'}`);
            }
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id, 'offer'] });
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id, 'billing-lines', 'chauffeur'] });
            queryClient.invalidateQueries({ queryKey: ['bookings', 'list'] });
            onDone(notify && wasNotified ? { email: booking.chauffeur_email } : null);
        },
        onError: (err) => toast.error(err?.message || 'Could not unallocate'),
    });

    return (
        <Modal
            open
            onClose={mutation.isPending ? undefined : onClose}
            title="Unallocate booking?"
            description={`Removes ${name} and the vehicle from ${booking.ride_ref}. The booking goes back to Confirmed.`}
            size="md"
        >
            <div className="space-y-4 mt-2">
                <Textarea
                    label="Reason (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    placeholder="e.g. Chauffeur unavailable, reallocating"
                    disabled={mutation.isPending}
                />
                {wasNotified && (
                    <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={notify}
                            onChange={(e) => setNotify(e.target.checked)}
                            className="mt-0.5"
                        />
                        <div>
                            <div className="text-sm font-medium text-ink">Tell {name} they're no longer allocated</div>
                            <div className="text-xs text-ink-subtle mt-0.5">
                                Previews before sending to {booking.chauffeur_email}
                            </div>
                        </div>
                    </label>
                )}
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={() => mutation.mutate()} loading={mutation.isPending}>
                    Unallocate
                </Button>
            </div>
        </Modal>
    );
}

// =============================================================================
// DriverAckStatus — three states: acknowledged, awaiting, stale
// =============================================================================
function DriverAckStatus({ booking }) {
    if (booking.driver_acknowledged_at) {
        // driver_acknowledged_at is bumped on every chauffeur status advance
        // (driverPortal.js status route), so it tracks the latest chauffeur
        // action. Label by current status so the line reads true.
        const ACTION_LABELS = {
            en_route:   'En route',
            arrived:    'Arrived',
            on_journey: 'POB',
            completed:  'Completed',
        };
        const actionLabel = ACTION_LABELS[booking.status] || 'Acknowledged';
        return (
            <div className="pt-2 flex items-center gap-1.5 text-xs text-green-700">
                <span className="flex-shrink-0">✓</span>
                <span>{actionLabel} · {formatRelativeTime(booking.driver_acknowledged_at)}</span>
            </div>
        );
    }

    // Not acked yet. "Stale" = 2 hours since notify OR within 6 hours of pickup.
    const notifiedAt = new Date(booking.driver_notified_at);
    const hoursSinceNotify = (Date.now() - notifiedAt.getTime()) / 3600000;

    let isStale = hoursSinceNotify >= 2;
    if (!isStale && booking.pickup_date && booking.pickup_time) {
        const pickupAt = new Date(`${String(booking.pickup_date).slice(0, 10)}T${booking.pickup_time}`);
        const hoursToPickup = (pickupAt.getTime() - Date.now()) / 3600000;
        if (hoursToPickup > 0 && hoursToPickup <= 6) {
            isStale = true;
        }
    }

    // Status link is per chauffeur (not per job) — "opened" means they opened
    // their portal after this job was sent to them.
    const openedAt = booking.chauffeur_link_last_opened_at;
    const openedSinceSent = !!openedAt && new Date(openedAt) >= notifiedAt;

    return (
        <div className="pt-2 space-y-1">
            <div className={`flex items-center gap-1.5 text-xs ${isStale ? 'text-amber-700 font-medium' : 'text-ink-subtle'}`}>
                <Clock className="h-3 w-3 flex-shrink-0" />
                <span>
                    Chauffeur notified {formatRelativeTime(booking.driver_notified_at)}
                    {isStale ? ' — awaiting acknowledgment' : ''}
                </span>
            </div>
            <div className={`flex items-center gap-1.5 text-xs ${openedSinceSent ? 'text-ink-subtle' : 'text-amber-700'}`}>
                <MailCheck className="h-3 w-3 flex-shrink-0" />
                <span>
                    {openedSinceSent
                        ? `Status link opened ${formatRelativeTime(openedAt)}`
                        : 'Status link not opened yet'}
                </span>
            </div>
        </div>
    );
}

// =============================================================================
// Section: Status history timeline
// =============================================================================
// =============================================================================
// Status history — collapsed to most recent 3 by default
// =============================================================================
//
// Edits + status transitions both write to booking_status_history, so this
// list grows fast on actively-edited bookings. Collapsing keeps the page
// scannable. "Show all" reveals everything; "Show fewer" collapses back.
//
// History arrives newest-first from the backend, so slice(0, 3) gives the
// 3 most recent entries (which is what the dispatcher cares about most).
const STATUS_HISTORY_DEFAULT_VISIBLE = 3;

function StatusHistoryCard({ history }) {
    const [showAll, setShowAll] = useState(false);
    const total = history.length;
    const visible = showAll ? history : history.slice(0, STATUS_HISTORY_DEFAULT_VISIBLE);
    const hiddenCount = total - visible.length;

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                    <History className="h-4 w-4 text-ink-subtle" />
                    Status history
                </h2>
            </div>
            <div className="card-body">
                {total === 0 ? (
                    <p className="text-sm text-ink-subtle italic">No status changes recorded yet.</p>
                ) : (
                    <>
                        <ol className="space-y-3">
                           {visible.map(h => {
                                const fromLabel = h.from_status ? STATUS_CONFIG[h.from_status]?.label || h.from_status : null;
                                const toLabel   = STATUS_CONFIG[h.to_status]?.label || h.to_status;
                                // Edits write the same status on both sides
                                // ("status: confirmed → confirmed"). When that
                                // happens, hide the redundant arrow and just
                                // surface the note (which describes the edit).
                                const isEdit = fromLabel && fromLabel === toLabel;

                                // Smarter label for "Edited" rows based on the
                                // note content. Adjustment / refund / no-refund
                                // notes get distinct labels so the history reads
                                // naturally instead of a wall of "Edited".
                                let editLabel = 'Edited';
                                if (isEdit && h.note) {
                                    const note = h.note.trim();
                                    if (/^Refunded £/i.test(note)) {
                                        editLabel = 'Refunded';
                                    } else if (/^Refund £.*\bFAILED\b/i.test(note)) {
                                        editLabel = 'Refund failed';
                                    } else if (/^Refund £.*pending Stripe/i.test(note)) {
                                        editLabel = 'Refund pending';
                                    } else if (/^Refund £.*recorded for invoice/i.test(note)) {
                                        editLabel = 'Refund recorded';
                                    } else if (/^No refund issued/i.test(note)) {
                                        editLabel = 'No refund';
                                    } else if (/^Adjustments? charged:/i.test(note)) {
                                        editLabel = 'Charged';
                                    } else if (/^Adjustment added/i.test(note)) {
                                        editLabel = 'Extra added';
                                    } else if (/^Adjustment removed/i.test(note)) {
                                        editLabel = 'Extra removed';
                                    } else if (/^Emailed customer details/i.test(note)) {
                                        editLabel = 'Emailed customer';
                                    } else if (/^Notified driver/i.test(note)) {
                                        editLabel = 'Notified chauffeur';
                                    }
                                }

                                return (
                                    <li key={h.id} className="flex items-start gap-3 text-sm">
                                        <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-ink-subtle flex-shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-ink">
                                                {isEdit ? (
                                                    <span className="font-medium">{editLabel}</span>
                                                ) : fromLabel ? (
                                                    <>
                                                        <span className="text-ink-subtle">{fromLabel}</span>
                                                        <ChevronRight className="inline h-3 w-3 mx-1 text-ink-subtle" />
                                                        <span className="font-medium">{toLabel}</span>
                                                    </>
                                                ) : (
                                                    <span className="font-medium">{toLabel}</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-ink-subtle mt-0.5">
                                                {formatTimestamp(h.changed_at)}
                                                {h.changed_by_email && <> · {h.changed_by_email}</>}
                                            </div>
                                            {h.note && (
                                                <div className="mt-1 text-xs text-ink-muted italic">{h.note}</div>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                        {(hiddenCount > 0 || showAll) && total > STATUS_HISTORY_DEFAULT_VISIBLE && (
                            <button
                                type="button"
                                onClick={() => setShowAll(!showAll)}
                                className="mt-4 text-xs text-ink-subtle hover:text-ink"
                            >
                                {showAll
                                    ? 'Show fewer'
                                    : `Show all (${total})`}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}


// =============================================================================
// Section: Dispatcher actions footer (placeholders for 7d.2.4)
// =============================================================================
// Booking as WhatsApp text, for chauffeurs. No price, no chauffeur notes,
// no account name, no links. Empty fields skipped.
function buildWhatsAppText(b) {
    const lines = [];
    const d = b.pickup_date ? new Date(`${String(b.pickup_date).slice(0, 10)}T00:00:00`) : null;
    const dateStr = d && !isNaN(d.getTime())
        ? d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
        : '';
    const time = b.pickup_time ? String(b.pickup_time).slice(0, 5) : '';
    const when = [dateStr, time].filter(Boolean).join(', ');
    lines.push(`*${[b.ride_ref, when].filter(Boolean).join(' · ')}*`);

    const isHourly = b.mode === 'hourly';
    const hoursShown = b.hours_disclosed_to_chauffeur !== false;
    if (isHourly) {
        lines.push(`*Service:* Hourly${hoursShown && b.hours ? ` · ${b.hours} ${Number(b.hours) === 1 ? 'hour' : 'hours'}` : ''}`);
    }

    if (b.pickup_address) lines.push(`*Pickup:* ${b.pickup_address}`);
    const flight = [
        b.flight_number,
        b.tail_number && `Tail ${b.tail_number}`,
        b.fbo_code && `PPR ${b.fbo_code}`,
    ].filter(Boolean).join(' · ');
    if (flight) lines.push(`*Flight:* ${flight}`);

    (Array.isArray(b.stops) ? b.stops : []).forEach((s, i) => {
        const addr = typeof s === 'string' ? s : s?.address;
        const t = typeof s === 'object' && s?.time ? `${String(s.time).slice(0, 5)} ` : '';
        if (addr) lines.push(`*Stop ${i + 1}:* ${t}${addr}`);
    });

    if (b.dropoff_address) {
        const ft = isHourly && hoursShown && b.final_time ? `${String(b.final_time).slice(0, 5)} ` : '';
        lines.push(`*${isHourly ? 'Final destination' : 'Drop off'}:* ${ft}${b.dropoff_address}`);
    }

    const paxName = b.pax_name_current || b.pax_name;
    const paxPhone = b.pax_phone_current || b.pax_phone;
    const pax = [
        paxName,
        paxPhone,
        b.pax_count ? `${b.pax_count} pax` : null,
    ].filter(Boolean).join(', ');
    if (pax) lines.push(`*Passenger:* ${pax}`);

    const car = [b.vehicle_make, b.vehicle_model].filter(Boolean).join(' ');
    const cls = b.vehicle_class ? b.vehicle_class.charAt(0).toUpperCase() + b.vehicle_class.slice(1) : null;
    const vehicle = [car || cls, b.vehicle_registration].filter(Boolean).join(' · ');
    if (vehicle) lines.push(`*Vehicle:* ${vehicle}`);

    if (b.pax_comments) lines.push(`*Notes:* ${b.pax_comments}`);

    return lines.join('\n');
}

async function copyWhatsAppText(b) {
    const text = buildWhatsAppText(b);
    try {
        await navigator.clipboard.writeText(text);
        toast.success('Copied — paste into WhatsApp');
    } catch {
        window.prompt('Copy this text:', text);
    }
}

function DispatcherActionsCard({ booking, editingBooking, setEditingBooking, setShowDuplicateModal, setShowReturnModal }) {
    const api = useApi();
    const { getToken } = useAuth();
    const queryClient = useQueryClient();
    const [confirmResend, setConfirmResend] = useState(false);
    const [showSendEmail, setShowSendEmail] = useState(false);
    const [downloadingJobSheet, setDownloadingJobSheet] = useState(false);

    async function downloadJobSheet(getTokenFn, bk) {
        if (downloadingJobSheet) return;
        setDownloadingJobSheet(true);
        try {
            const token = await getTokenFn();
            const res = await fetch(`/api/bookings/${bk.id}/job-sheet-pdf`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Failed (${res.status})`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const safeRef = String(bk.ride_ref || 'booking').replace(/[^a-zA-Z0-9-]/g, '_');
            a.download = `Lanz-CTS-Job-Sheet-${safeRef}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('Job sheet downloaded');
        } catch (err) {
            toast.error(err.message || 'Could not generate job sheet');
        } finally {
            setDownloadingJobSheet(false);
        }
    }

    // Show the Resend button only when:
    //   - it's a payment_link booking
    //   - AND it's still pending_payment (resending after confirmed/cancelled
    //     would be confusing — customer either already paid, or the booking
    //     is dead and a fresh booking should be made instead)
    const canResendPaymentLink =
        booking?.payment_method === 'payment_link' &&
        booking?.status === 'pending_payment';

    // Show "Send email" when there's at least one possible recipient channel:
    //   - booker has email (customer details kind), OR
    //   - chauffeur is allocated (driver allocation kind)
    // Pending_payment bookings still allow Send email — driver kind may apply
    // post-allocation, and customer kind for those is separately handled by
    // the payment-link resend flow (different content, different intent).
       const blockCustomerEmail = booking?.status === 'pending_payment' && booking?.payment_method === 'payment_link';
    const canSendEmail =
        (!!booking?.booker_email && !blockCustomerEmail)
        || !!booking?.allocated_chauffeur_id;

    const resendMutation = useMutation({
        mutationFn: () => api.post(`/bookings/${booking.id}/resend-payment-link`),
        onSuccess: (data) => {
            const expiresAt = data?.expires_at ? new Date(data.expires_at) : null;
            const expiryStr = expiresAt
                ? expiresAt.toLocaleString('en-GB', {
                    weekday: 'short', day: 'numeric', month: 'short',
                    hour: '2-digit', minute: '2-digit',
                  })
                : 'soon';
            const verb = data?.session_regenerated ? 'New link sent' : 'Link resent';
            toast.success(`${verb} — expires ${expiryStr}`);
            queryClient.invalidateQueries({ queryKey: ['booking', booking.id] });
            setConfirmResend(false);
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Failed to resend';
            toast.error(`Couldn't resend: ${msg}`);
            setConfirmResend(false);
        },
    });

    return (
        <>
            <div className="card">
                <div className="card-header">
                    <h2 className="text-sm font-semibold text-ink">Actions</h2>
                </div>
                <div className="card-body space-y-2">
                    {canResendPaymentLink && (
                        <Button
                            variant="secondary"
                            className="w-full"
                            onClick={() => setConfirmResend(true)}
                            disabled={resendMutation.isPending}
                        >
                            <MailCheck className="h-4 w-4" />
                            {resendMutation.isPending ? 'Sending\u2026' : 'Resend payment link'}
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => setEditingBooking(true)}
                        disabled={editingBooking}
                        title={editingBooking ? 'Already editing' : 'Edit all booking details at once'}
                    >
                        <Edit2 className="h-4 w-4" />
                        {editingBooking ? 'Editing\u2026' : 'Edit booking'}
                    </Button>
                    <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => setShowSendEmail(true)}
                        disabled={!canSendEmail}
                        title={canSendEmail ? null : 'No recipients available — booker has no email and no chauffeur allocated'}
                    >
                        <Send className="h-4 w-4" />
                        Send email / SMS
                    </Button>

                    <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => downloadJobSheet(getToken, booking)}
                        disabled={downloadingJobSheet}
                        title="Download a PDF job sheet"
                    >
                        <FileText className="h-4 w-4" />
                        {downloadingJobSheet ? 'Generating\u2026' : 'Job sheet PDF'}
                    </Button>

                    
                    <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => copyWhatsAppText(booking)}
                        title="Copy booking details as text for WhatsApp"
                    >
                        <Copy className="h-4 w-4" />
                        Copy as text
                    </Button>

                    <button
    onClick={() => setShowDuplicateModal(true)}
    className="w-full flex items-center justify-center gap-2 px-3.5 py-2.5 text-sm font-medium text-ink bg-white border border-slate-300 hover:bg-slate-50 rounded transition"
>
    <Copy className="w-4 h-4" />
    Duplicate
</button>
                    
{(() => {
    const normalizedMode = (booking.mode || '').replace(/[-_\s]/g, '').toLowerCase();
    const isOneWay = normalizedMode === 'oneway';
    const hasDistinctAddresses = booking.pickup_address
        && booking.dropoff_address
        && booking.pickup_address !== booking.dropoff_address;

    if (!isOneWay || !hasDistinctAddresses) return null;

    return (
        <button
            onClick={() => setShowReturnModal(true)}
            className="w-full flex items-center justify-center gap-2 px-3.5 py-2.5 text-sm font-medium text-ink bg-white border border-slate-300 hover:bg-slate-50 rounded transition"
        >
            <ArrowLeftRight className="w-4 h-4" />
            Create return
        </button>
    );
})()}

                    {/* Last-sent indicators — only render when a send has happened */}
                    {(booking.customer_details_emailed_at || booking.driver_notified_at) && (
                        <div className="text-xs text-ink-subtle space-y-0.5 pt-1">
                            {booking.customer_details_emailed_at && (
                                <div>Customer last emailed {formatRelativeTime(booking.customer_details_emailed_at)}</div>
                            )}
                            {booking.driver_notified_at && (
                                <div>Chauffeur last notified {formatRelativeTime(booking.driver_notified_at)}</div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Confirm resend modal */}
            {confirmResend && (
                <Modal
                    open
                    onClose={() => setConfirmResend(false)}
                    title="Resend payment link?"
                    description="If the existing link is still valid, the same URL will be re-emailed. If it has expired, a fresh Checkout Session will be created (the old URL stops working) and a new 24-hour link will be sent."
                    size="md"
                >
                    <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                        <Button
                            variant="secondary"
                            onClick={() => setConfirmResend(false)}
                            disabled={resendMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => resendMutation.mutate()}
                            loading={resendMutation.isPending}
                        >
                            <MailCheck className="h-4 w-4" />
                            Send email
                        </Button>
                    </div>
                </Modal>
            )}

            {/* Send email modal — consolidated email kind picker */}
            {showSendEmail && (
                <SendEmailModal
                    booking={booking}
                    onClose={() => setShowSendEmail(false)}
                />
            )}
        </>
    );
}

// =============================================================================
// SendEmailModal — REFACTORED for preview-first flow (7d.preview)
// =============================================================================
//
// Two phases:
//
//   Phase 1: kind picker (unchanged UX — accordion of audiences + kinds with
//            checkboxes). Click "Send" → enters Phase 2.
//
//   Phase 2: sequential previews. For each selected kind in order, mount
//            <EmailPreviewModal> with that kind's previewParams + endpoint.
//            On send success, advance to the next. On the last, close.
//
// The old `extraRecipients` textarea per kind is GONE — recipient editing
// now lives inside EmailPreviewModal (single source of truth).
//
// kind → backend preview kind mapping. Frontend uses friendly IDs in
// EMAIL_AUDIENCES; the preview endpoint expects its own kind strings.
// =============================================================================

function customerKindDefaultRecipients(booking, includePassenger) {
    const recipients = [];
    if (booking.booker_email) recipients.push(booking.booker_email);
    if (includePassenger
        && booking.passenger_email
        && booking.passenger_notifications_enabled
        && booking.booker_email
        && booking.passenger_email.trim().toLowerCase() !== booking.booker_email.trim().toLowerCase()
    ) {
        recipients.push(booking.passenger_email);
    }
    return recipients;
}

const EMAIL_AUDIENCES = [
    {
        id: 'customer',
        label: 'To customer',
        kinds: [
            {
                id: 'customer_details',
                label: 'Booking details',
                description: 'Sends the booking details email to the booker.',
                endpoint: (booking) => `/bookings/${booking.id}/send-details`,
                defaultRecipients: (booking) => booking.booker_email ? [booking.booker_email] : [],
                lastSentAt: (booking) => booking.customer_details_emailed_at,
                                isAvailable: (booking) =>
                    !!booking.booker_email && !(booking.status === 'pending_payment' && booking.payment_method === 'payment_link'),
                unavailableHint: (booking) => {
                    if (!booking.booker_email) return 'No booker email on file';
                    if (booking.status === 'pending_payment' && booking.payment_method === 'payment_link') return 'Use Resend payment link for pending-payment bookings';
                    return 'Unavailable';
                },
            },
            {
                id: 'customer_chauffeur_allocated',
                label: 'Chauffeur details',
                description: 'Sends chauffeur photo + name + phone + PHV licence + vehicle details to the customer.',
                endpoint: (booking) => `/bookings/${booking.id}/send-customer-chauffeur-email`,
                buildBody: (recipients) => ({ kind: 'allocated', recipients }),
                defaultRecipients: (booking) => customerKindDefaultRecipients(booking, true),
                lastSentAt: () => null,
                isAvailable: (booking) =>
                    !!booking.allocated_chauffeur_id
                    && !['cancelled', 'no_show'].includes(booking.status),
                unavailableHint: (booking) => {
                    if (!booking.allocated_chauffeur_id) return 'No chauffeur allocated yet';
                    if (['cancelled', 'no_show'].includes(booking.status)) return 'Booking is closed';
                    return 'Unavailable';
                },
            },
            {
                id: 'customer_en_route',
                label: 'On the way',
                description: 'Notifies the customer their chauffeur is on the way.',
                endpoint: (booking) => `/bookings/${booking.id}/send-customer-chauffeur-email`,
                buildBody: (recipients) => ({ kind: 'en_route', recipients }),
                defaultRecipients: (booking) => customerKindDefaultRecipients(booking, true),
                lastSentAt: () => null,
                isAvailable: (booking) =>
                    !!booking.allocated_chauffeur_id
                    && ['en_route', 'arrived', 'on_journey', 'completed'].includes(booking.status),
                unavailableHint: (booking) => {
                    if (!booking.allocated_chauffeur_id) return 'No chauffeur allocated yet';
                    return 'Chauffeur not yet en route';
                },
            },
            {
                id: 'customer_arrived',
                label: 'Has arrived',
                description: 'Notifies the customer their chauffeur has arrived.',
                endpoint: (booking) => `/bookings/${booking.id}/send-customer-chauffeur-email`,
                buildBody: (recipients) => ({ kind: 'arrived', recipients }),
                defaultRecipients: (booking) => customerKindDefaultRecipients(booking, true),
                lastSentAt: () => null,
                isAvailable: (booking) =>
                    !!booking.allocated_chauffeur_id
                    && ['arrived', 'on_journey', 'completed'].includes(booking.status),
                unavailableHint: (booking) => {
                    if (!booking.allocated_chauffeur_id) return 'No chauffeur allocated yet';
                    return 'Chauffeur has not arrived yet';
                },
            },
            {
                id: 'customer_on_journey',
                label: 'Ride started',
                description: 'Notifies the customer the ride is underway.',
                endpoint: (booking) => `/bookings/${booking.id}/send-customer-chauffeur-email`,
                buildBody: (recipients) => ({ kind: 'on_journey', recipients }),
                defaultRecipients: (booking) => booking.booker_email ? [booking.booker_email] : [],
                lastSentAt: () => null,
                isAvailable: (booking) =>
                    !!booking.allocated_chauffeur_id
                    && ['on_journey', 'completed'].includes(booking.status),
                unavailableHint: (booking) => {
                    if (!booking.allocated_chauffeur_id) return 'No chauffeur allocated yet';
                    return 'Ride has not started yet';
                },
            },
        ],
    },
    {
        id: 'driver',
        label: 'To chauffeur',
        kinds: [
            {
                id: 'driver_allocation',
                label: 'Job allocation',
                description: 'Sends booking details + portal status link to the allocated chauffeur.',
                endpoint: (booking) => `/bookings/${booking.id}/notify-driver`,
                defaultRecipients: (booking) =>
                    booking.chauffeur_email ? [booking.chauffeur_email] : [],
                lastSentAt: (booking) => booking.driver_notified_at,
                isAvailable: (booking) =>
                    !!booking.allocated_chauffeur_id
                    && !!booking.chauffeur_email
                    && !['cancelled', 'no_show'].includes(booking.status),
                unavailableHint: (booking) => {
                    if (!booking.allocated_chauffeur_id) return 'No chauffeur allocated yet';
                    if (!booking.chauffeur_email) return 'Allocated chauffeur has no email on file';
                    if (['cancelled', 'no_show'].includes(booking.status)) return 'Booking is closed';
                    return 'Unavailable';
                },
            },
        ],
    },
];

const FRONTEND_TO_PREVIEW_KIND = {
    customer_details:              'booking_details_customer',
    customer_chauffeur_allocated:  'customer_chauffeur_allocated',
    customer_en_route:             'customer_en_route',
    customer_arrived:              'customer_arrived',
    customer_on_journey:           'customer_on_journey',
    // customer_completed has no backend equivalent — omitted; row hidden.
    driver_allocation:             'chauffeur_allocation',
};

const SMS_CAPABLE_KINDS = {
    customer_details: (b) => ({
        endpoint: `/bookings/${b.id}/send-details`,
        body: { recipients: b.booker_email ? [b.booker_email] : [], send_sms: true, sms_only: true },
    }),
    customer_chauffeur_allocated: (b) => ({
        endpoint: `/bookings/${b.id}/send-customer-chauffeur-email`,
        body: { kind: 'allocated', recipients: b.booker_email ? [b.booker_email] : [], send_sms: true, sms_only: true },
    }),
    driver_allocation: (b) => ({
        endpoint: `/bookings/${b.id}/notify-driver`,
        body: { send_sms: true, sms_only: true },
    }),
};

// =============================================================================
// SendEmailModal — uses EmailPreviewModal with tabs (one tab per kind)
// =============================================================================
function SendEmailModal({ booking, onClose }) {
    const api = useApi();
    const [expandedAudiences, setExpandedAudiences] = useState({});
    const [checkedKinds, setCheckedKinds] = useState({});
    const [showPreview, setShowPreview] = useState(false);

    // Multi-ride bookings: "all rides in one email" to the customer, with
    // preview. Same query key as the page → already cached, no extra load.
    const [itineraryChecked, setItineraryChecked] = useState(false);
    const [itinerarySms, setItinerarySms] = useState(false);
    const { data: siblingsData } = useQuery({
        queryKey: ['booking', booking.id, 'siblings'],
        queryFn: () => api.get(`/bookings/${booking.id}/siblings`),
    });
    const itineraryIds = (siblingsData?.siblings || []).map((s) => s.id);
    const hasItinerary = itineraryIds.length > 1;

    function toggleAudience(audId) {
        setExpandedAudiences((s) => ({ ...s, [audId]: !s[audId] }));
    }

    function toggleKind(id, checked) {
        setCheckedKinds((s) => ({ ...s, [id]: checked }));
    }

    const audiencesFiltered = useMemo(() => {
        return EMAIL_AUDIENCES.map((aud) => ({
            ...aud,
            kinds: aud.kinds.filter((k) => FRONTEND_TO_PREVIEW_KIND[k.id]),
        })).filter((aud) => aud.kinds.length > 0);
    }, []);

    const allKinds = audiencesFiltered.flatMap((aud) => aud.kinds);
    const selectedKinds = allKinds.filter(
        (k) => checkedKinds[k.id] && k.isAvailable(booking)
    );

    const smsKinds = selectedKinds.filter((k) => SMS_CAPABLE_KINDS[k.id]);

    const smsMutation = useMutation({
        mutationFn: async () => {
            for (const k of smsKinds) {
                const { endpoint, body } = SMS_CAPABLE_KINDS[k.id](booking);
                await api.post(endpoint, body);
            }
        },
        onSuccess: () => {
            toast.success(`SMS sent (${smsKinds.length})`);
            onClose();
        },
        onError: (err) => {
            toast.error(err?.response?.data?.error || err?.message || 'SMS failed');
        },
    });

    // Build the emails array for EmailPreviewModal
    const emails = useMemo(() => {
        const list = selectedKinds.map((kind) => ({
            id: kind.id,
            label: kind.label,
            previewParams: {
                kind: FRONTEND_TO_PREVIEW_KIND[kind.id],
                booking_id: booking.id,
            },
            sendEndpoint: kind.endpoint(booking),
            sendBody: (recipients) => kind.buildBody
                ? kind.buildBody(recipients)
                : { recipients },
        }));
        if (hasItinerary && itineraryChecked) {
            list.push({
                id: 'itinerary_customer',
                label: `All rides (${itineraryIds.length})`,
                previewParams: {
                    kind: 'itinerary_customer',
                    booking_ids: itineraryIds,
                },
                sendEndpoint: '/bookings/bulk/email-customer',
                sendBody: (recipients) => ({
                    booking_ids: itineraryIds,
                    mode: 'consolidated',
                    recipients,
                    send_sms: itinerarySms,
                }),
            });
        }
        return list;
    }, [selectedKinds, booking, hasItinerary, itineraryChecked, itineraryIds.join(','), itinerarySms]);

    if (showPreview) {
        return (
            <EmailPreviewModal
                emails={emails}
                onClose={() => setShowPreview(false)}
                onSent={() => onClose()}
            />
        );
    }

    return (
        <Modal
            open
            onClose={onClose}
            title="Send email"
            description="Pick the email types to send. Each one will preview before being sent."
            size="md"
        >
            <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto -mx-4 px-4 sm:-mx-5 sm:px-5">
                {audiencesFiltered.map((aud) => (
                    <AudienceSection
                        key={aud.id}
                        audience={aud}
                        expanded={!!expandedAudiences[aud.id]}
                        onToggleExpand={() => toggleAudience(aud.id)}
                        booking={booking}
                        checkedKinds={checkedKinds}
                        onToggleKind={toggleKind}
                    />
                ))}

                {hasItinerary && (
                    <div className="border border-paper-border-soft rounded-md px-3 py-3">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={itineraryChecked}
                                onChange={(e) => setItineraryChecked(e.target.checked)}
                                className="mt-0.5"
                            />
                            <span className="text-sm">
                                <span className="font-medium text-ink">Customer · all rides in one email</span>
                                <span className="text-xs text-ink-subtle"> ({itineraryIds.length})</span>
                                <span className="block text-xs text-ink-subtle">
                                    Every ride in this itinerary in one email. Rides awaiting payment are left out.
                                </span>
                            </span>
                        </label>
                        {itineraryChecked && (
                            <label className="flex items-center gap-2 cursor-pointer mt-2 ml-6 text-xs text-ink">
                                <input
                                    type="checkbox"
                                    checked={itinerarySms}
                                    onChange={(e) => setItinerarySms(e.target.checked)}
                                />
                                Also send SMS
                            </label>
                        )}
                    </div>
                )}
            </div>

        <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="secondary"
                    onClick={() => smsMutation.mutate()}
                    disabled={smsKinds.length === 0 || smsMutation.isPending}
                    loading={smsMutation.isPending}
                    title={smsKinds.length === 0 ? 'No SMS-capable types selected' : 'Send SMS only (no email)'}
                >
                    <Phone className="h-4 w-4" />
                    Send SMS {smsKinds.length > 1 ? `(${smsKinds.length})` : ''}
                </Button>
                <Button
                    variant="primary"
                    onClick={() => setShowPreview(true)}
                    disabled={emails.length === 0}
                >
                    Preview {emails.length > 1 ? `(${emails.length})` : ''}
                </Button>
            </div>
        </Modal>
    );
}

// Map a selected kind + booking → previewParams for POST /email-previews.
// Branches on the backend preview kind so each gets the right context.
function buildPreviewParams(kind, booking) {
    const backendKind = FRONTEND_TO_PREVIEW_KIND[kind.id];
    return {
        kind: backendKind,
        booking_id: booking.id,
    };
}

// -----------------------------------------------------------------------------
// AudienceSection — collapsible group. Simplified vs. pre-refactor: no
// per-kind recipient textarea (that lives in EmailPreviewModal now).
// -----------------------------------------------------------------------------
function AudienceSection({
    audience,
    expanded,
    onToggleExpand,
    booking,
    checkedKinds,
    onToggleKind,
}) {
    const availableKinds = audience.kinds.filter((k) => k.isAvailable(booking));
    const audienceAvailable = availableKinds.length > 0;
    const selectedCount = audience.kinds.filter(
        (k) => checkedKinds[k.id] && k.isAvailable(booking)
    ).length;

    const audienceUnavailableHint = !audienceAvailable && audience.kinds[0]
        ? audience.kinds[0].unavailableHint(booking)
        : null;

    return (
        <div className="border border-paper-border-soft rounded-md overflow-hidden">
            <button
                type="button"
                onClick={audienceAvailable ? onToggleExpand : undefined}
                disabled={!audienceAvailable}
                title={audienceAvailable ? undefined : audienceUnavailableHint}
                className={`w-full flex items-center justify-between gap-2 px-3 py-3 text-left transition-colors ${
                    audienceAvailable
                        ? 'hover:bg-canvas/40 cursor-pointer'
                        : 'opacity-50 cursor-not-allowed'
                }`}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <ChevronRight
                        className={`h-4 w-4 text-ink-subtle flex-shrink-0 transition-transform ${
                            expanded ? 'rotate-90' : ''
                        }`}
                    />
                    <span className="text-sm font-medium text-ink">{audience.label}</span>
                    <span className="text-xs text-ink-subtle">
                        ({availableKinds.length})
                    </span>
                    {selectedCount > 0 && (
                        <span className="text-xs text-ink-info font-medium">
                            · {selectedCount} selected
                        </span>
                    )}
                </div>
                {!audienceAvailable && audienceUnavailableHint && (
                    <span className="text-xs text-ink-subtle italic truncate">
                        {audienceUnavailableHint}
                    </span>
                )}
            </button>

            {expanded && audienceAvailable && (
                <div className="border-t border-paper-border-soft p-3 space-y-3 bg-canvas/20">
                    {audience.kinds.map((kind) => (
                        <KindRow
                            key={kind.id}
                            kind={kind}
                            booking={booking}
                            checked={!!checkedKinds[kind.id]}
                            onToggle={(checked) => onToggleKind(kind.id, checked)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// KindRow — checkbox + description. Recipient editing moved to preview modal.
// -----------------------------------------------------------------------------
function KindRow({ kind, booking, checked, onToggle }) {
    const available = kind.isAvailable(booking);
    const defaults = kind.defaultRecipients(booking) || [];

    return (
        <div className={available ? '' : 'opacity-60'}>
            <label className={`flex items-start gap-2.5 ${available ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onToggle(e.target.checked)}
                    disabled={!available}
                    className="mt-0.5"
                />
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink">{kind.label}</div>
                    <div className="text-xs text-ink-subtle mt-0.5">{kind.description}</div>
                    {!available && (
                        <div className="text-xs text-amber-700 mt-1.5 italic">
                            {kind.unavailableHint(booking)}
                        </div>
                    )}
                    {available && defaults.length > 0 && (
                        <div className="text-xs text-ink-subtle mt-1.5">
                            <span className="font-medium text-ink">Default:</span>{' '}
                            {defaults.join(', ')}
                        </div>
                    )}
                    {available && kind.lastSentAt && kind.lastSentAt(booking) && (
                        <div className="text-xs text-ink-info mt-1.5">
                            Last sent {formatRelativeTime(kind.lastSentAt(booking))}
                        </div>
                    )}
                </div>
            </label>
        </div>
    );
}

// =============================================================================
// Helper: definition list row
// =============================================================================
function Row({ icon: Icon, label, value, fullWidth }) {
    return (
        <div className={fullWidth ? 'sm:col-span-2' : ''}>
            <dt className="text-xs uppercase tracking-wide text-ink-subtle font-medium flex items-center gap-1.5">
                {Icon && <Icon className="h-3 w-3" />}
                {label}
            </dt>
            <dd className="mt-0.5 text-ink break-words">{value}</dd>
        </div>
    );
}
